// tree.js — the procedural bonsai: the Tree and Branch classes.
//
// Tree builds the trunk (lathe/tapered geometry, vertex-colored bark, lean +
// noise), owns this.branches, grows main branches and sub-branches, and
// regenerates foliage. Branch attaches itself inside the trunk and builds either
// pine-needle or deciduous foliage. Both read the shared `parameters`; color
// palettes come from Colors and randomness helpers from Math2 (both in core).
// Animations use the global `gsap` (loaded via a <script> tag in index.html).
//
// Self-contained with respect to the scene graph: everything is built into
// `this.mesh` (a THREE.Group); the caller adds that group to app.scene.

import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { parameters, Colors, Math2, app } from './core.js';
import { createRoots, rootVertexColor } from './roots.js';
import { applyBirchBand, applyBirchBase, syncBirchTexture, applyTrunkBark, applyBranchBarkNormal, applyWoodGrain, refreshBarkNormals } from './bark.js';
import {
  injectFoliageShader, leafPalette, isNeedleType, needlePalette,
  paletteIsGradient, pickLeafColors, addLeafGradientRamp, applySpringTipFlush
} from './foliageShader.js';
import { makeLeafGeometry } from './leafGeometry.js';
import { pineConesWanted, makeCone, CONE_LENGTH } from './pineCones.js';

// Branch tube tessellation — must match the TubeGeometry call in
// Branch.createBranchGeometry(). The tube has BRANCH_TUBULAR_SEGMENTS segments
// (so BRANCH_TUBULAR_SEGMENTS + 1 vertex rings) along the branch curve; partial
// pruning snaps the cut to one of those rings.
const BRANCH_TUBULAR_SEGMENTS = 4;
const BRANCH_RADIAL_SEGMENTS = 8;

// One shimpaku juniper "cord": a short tapering twig sheathed in tiny appressed
// scale leaves. Junipers carry no needles — the twigs are wrapped in scales that
// build up into soft rope-like cords, and the cords mass into juniper's billowing
// cloud pads. The scales are arranged decussately (opposite pairs, each node
// rotated 90° from the last) and overlap upward like shingles. Built non-indexed
// with the base at the origin and the cord running up +Y, so it instances exactly
// like a needle. `sizeScale` is parameters.foliageSize / 15.
function makeJuniperCordGeometry(sizeScale = 1) {
  const cordLen = 4.0 * sizeScale;
  const baseRad = 0.17 * sizeScale;                  // twig radius at the base
  const NODES = 6;                                    // scale pairs up the cord
  const scaleLen = 1.5 * sizeScale;                   // overshoots ~2 nodes (shingling)
  const scaleWid = 0.24 * sizeScale;                  // scale half-width
  const radAt = (y) => baseRad * (1 - 0.72 * (y / cordLen)); // taper to a near-point

  const positions = [];
  const uvs = [];
  const pushTri = (a, b, c, uvA, uvB, uvC) => {
    positions.push(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2]);
    uvs.push(uvA[0], uvA[1], uvB[0], uvB[1], uvC[0], uvC[1]);
  };

  // Two opposite scales per node, the pair's axis rotating 90° each node. Each
  // scale is a small kite pressed to the twig, its tip flaring out and pointing up.
  for (let k = 0; k < NODES; k++) {
    const y0 = cordLen * (0.08 + 0.82 * k / NODES);  // node height up the cord
    const r0 = radAt(y0);
    const ang0 = k * (Math.PI / 2) + 0.35;           // decussate 90° twist per node
    for (let s = 0; s < 2; s++) {
      const a = ang0 + s * Math.PI;                   // the two opposite scales
      const out = [Math.cos(a), 0, Math.sin(a)];      // radial (cross-section is XZ)
      const tan = [-Math.sin(a), 0, Math.cos(a)];     // tangential (scale-width axis)
      const yTip = Math.min(y0 + scaleLen, cordLen * 1.02);
      const yMid = y0 + scaleLen * 0.42;
      const rMid = r0 * 0.95;
      const rTip = radAt(Math.min(yTip, cordLen)) + 0.18 * sizeScale; // tip lifts off
      const P0 = [out[0] * r0, y0, out[2] * r0];                              // base
      const PL = [out[0] * rMid - tan[0] * scaleWid, yMid, out[2] * rMid - tan[2] * scaleWid];
      const PR = [out[0] * rMid + tan[0] * scaleWid, yMid, out[2] * rMid + tan[2] * scaleWid];
      const PT = [out[0] * rTip, yTip, out[2] * rTip];                        // flared tip
      pushTri(P0, PL, PT, [0.5, 0], [0, 0.45], [0.5, 1]);
      pushTri(P0, PT, PR, [0.5, 0], [0.5, 1], [1, 0.45]);
    }
  }

  const scaleGeom = new THREE.BufferGeometry();
  scaleGeom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  scaleGeom.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  scaleGeom.computeVertexNormals();

  // A thin tapered twig down the cord centre fills the gaps between scales so the
  // cord reads as a solid rope rather than floating leaves.
  const twig = new THREE.CylinderGeometry(baseRad * 0.28, baseRad * 0.85, cordLen, 5, 1).toNonIndexed();
  twig.translate(0, cordLen / 2, 0);

  const geom = mergeGeometries([twig, scaleGeom]);
  twig.dispose();
  scaleGeom.dispose();
  return geom;
}

// Debris-material pool (prune-hitch fix). A prune drops a falling offcut that
// fades out; previously each cut CLONED the wood/foliage material and disposed
// it when the fall finished, which freed the GPU shader program and forced a
// fresh ~100ms compile+link on the NEXT cut — a recurring frame stall on every
// prune. Instead we keep one reusable, transparent clone per material "kind"
// (keyed so leaf types with different program flags don't collide), compiled
// once and never disposed, so cuts never recompile. The fade animates the shared
// material's opacity and resets it to baseOpacity on complete (overlapping cuts
// of the same kind briefly share the fade — acceptable for brief falling debris).
const debrisMatPool = new Map();
function getDebrisMaterial(kind, sourceMat) {
  // vertexColors flips the USE_COLOR shader define, so it must be part of the key
  // (e.g. cherry leaves use vertex colors, other leaf types don't).
  const key = `${kind}:${sourceMat.vertexColors ? 1 : 0}`;
  let mat = debrisMatPool.get(key);
  if (!mat) {
    mat = sourceMat.clone();
    // clone() doesn't carry onBeforeCompile; reusing the source's keeps the same
    // program cache key (and the shared wind uniforms) so no extra program.
    mat.onBeforeCompile = sourceMat.onBeforeCompile;
    mat.transparent = true;
    mat.userData = { ...mat.userData, pooled: true, baseOpacity: sourceMat.opacity };
    mat.needsUpdate = true;
    debrisMatPool.set(key, mat);
  }
  return mat;
}

// A sub-branch only sprouts a third level of "twigs" once it is at least this
// thick — only the very thinnest sub-branches (which already read as twigs) are
// left bare; mid-size branches still carry a few.
const TWIG_MIN_PARENT_THICKNESS = 0.8;

// --- Winter snow shed / regrow -------------------------------------------------
// Sculpting a branch knocks its snow clumps off; they then slowly re-accumulate.
const SNOW_REGROW_S = 175;     // seconds for a bared branch to refill to full snow
const SNOW_SHED_S = 1.9;      // how long a knocked-off clump takes to shrink away
const SNOW_FALL_G = 11.0;     // shed "gravity" (clump-radii / s²)
const SNOW_REVEAL_BAND = 0.14; // fill window over which one clump scales in

// When set, freshly built snow starts bare (fill 0) and accumulates over time —
// used by the live "grow" buttons so new branches arrive bare and snow settles on
// them. Initial generation and save replay leave it false, so those trees show
// their full winter coat immediately. Toggled around the synchronous growth call.
let snowStartsBare = false;
export function setSnowStartsBare(v) { snowStartsBare = v; }

// Hard cap on user-grown main branches. Main branches each carry their own
// sub-branches/twigs/foliage, so they dominate the geometry/draw cost; past a
// few dozen the scene bogs down. This is a sanity blocker (set high) on the
// "main branches" button only — it never limits initial generation or save
// replay, so existing trees still load exactly as authored.
export const MAX_MAIN_BRANCHES = 45;

// Hard cap on total live branches (trunk branches + main-branch sub-branches +
// twigs) the "grow" button will add up to. Same idea as MAX_MAIN_BRANCHES, but
// for the finer growth: sub-branches and twigs have no per-action ceiling, so
// without this the grow button could be mashed until the scene crawls. Counts
// the live scene graph only (see Tree.branchCount), so it never touches the
// seeded stream and save replay rebuilds trees of any size exactly as authored.
export const MAX_GROW_BRANCHES = 450;

// --- Tree-snow surface texture -------------------------------------------------
// The branch snow clumps share the settings drawer's "Surface Textures" checkbox
// with the bark normals (bark.js) and the rock/pot-cap snow normals
// (environment.js). The pot caps register their material with environment's rock
// system, but tree snow materials are transient — built per branch and disposed
// on melt (meltSnowAway) — so they'd leak/stale that registry. Instead tree.js
// owns the flag: createSnow reads it for newly built clumps, and setTreeSnowTexture
// walks the live tree to flip existing clumps. Loaded lazily + cached in linear
// space (NoColorSpace), matching getRockNormal.
const _snowNormalLoader = new THREE.TextureLoader();
let _snowNormalTex = null;
let _snowTextureEnabled = false;
function getSnowNormal() {
  if (_snowNormalTex) return _snowNormalTex;
  const tex = _snowNormalLoader.load('assets/textures/snow-normal.jpg');
  tex.colorSpace = THREE.NoColorSpace;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  _snowNormalTex = tex;
  return tex;
}

// Settings-drawer toggle (paired with the bark + rock normals): apply or clear the
// snow normal across every live branch-snow material on the current tree.
export function setTreeSnowTexture(enabled) {
  _snowTextureEnabled = !!enabled;
  const root = app.tree && app.tree.mesh;
  if (!root) return;
  root.traverse((o) => {
    if (o.userData && o.userData.snow && o.material) {
      o.material.normalMap = enabled ? getSnowNormal() : null;
      o.material.needsUpdate = true;
    }
  });
}

const _snowQuat = new THREE.Quaternion();
const _snowPos = new THREE.Vector3();
const _snowScl = new THREE.Vector3();
const _snowMat = new THREE.Matrix4();
const _snowUp = new THREE.Vector3();
const _GEOM_Y = new THREE.Vector3(0, 1, 0);
const _seatNormal = new THREE.Vector3();
const _seatTangent = new THREE.Vector3();
const _seatPoint = new THREE.Vector3();
const _seatSpinQ = new THREE.Quaternion();

// Seat one snow clump on the upper surface of `curve` at parameter t: project `up`
// perpendicular to the branch tangent (+ a fixed clock jitter) to find the top-
// facing normal, push the clump out onto the bark, aim the geometry's +Y along it,
// then spin it. `up` is "which way is up" IN CURVE-LOCAL SPACE — (0,1,0) at build
// time, re-derived to the branch's new pose after a shape so snow re-piles on top.
function seatSnowClump(curve, thickness, t, clock, spin, up, outPos, outQuat) {
  curve.getPoint(t, _seatPoint);
  curve.getTangent(t, _seatTangent).normalize();
  _seatNormal.copy(up).addScaledVector(_seatTangent, -up.dot(_seatTangent));
  if (_seatNormal.lengthSq() < 1e-4) _seatNormal.set(1, 0, 0); // up ∥ tangent: any perp
  _seatNormal.normalize();
  _seatNormal.applyAxisAngle(_seatTangent, clock);
  outPos.copy(_seatPoint).addScaledVector(_seatNormal, thickness * 0.6);
  outQuat.setFromUnitVectors(_GEOM_Y, _seatNormal);
  _seatSpinQ.setFromAxisAngle(_GEOM_Y, spin); // random spin about the clump's own up
  outQuat.multiply(_seatSpinQ);
}

// Recompute every clump's rest seat (basePos/baseQuat) for the branch's current
// top side `up` (curve-local). Snow then regrows on the new top after a shape.
function reseatSnow(s, up) {
  const b = s.branch;
  if (!b || !b.curve) return; // e.g. a curveless limb: leave its seats as built
  for (let i = 0; i < s.count; i++) {
    seatSnowClump(b.curve, b.thickness, s.tParam[i], s.clock[i], s.spin[i], up, _snowPos, _snowQuat);
    s.basePos[i * 3] = _snowPos.x; s.basePos[i * 3 + 1] = _snowPos.y; s.basePos[i * 3 + 2] = _snowPos.z;
    s.baseQuat[i * 4] = _snowQuat.x; s.baseQuat[i * 4 + 1] = _snowQuat.y;
    s.baseQuat[i * 4 + 2] = _snowQuat.z; s.baseQuat[i * 4 + 3] = _snowQuat.w;
  }
}

// Knock the snow off every branch under `root` (a Branch mesh/group), then let
// updateSnowMesh() build it back on the branch's NEW top side. Called when a branch
// is sculpted in winter.
export function shedSnowIn(root) {
  if (!root) return;
  root.traverse(o => {
    const s = o.userData && o.userData.snow;
    if (!s || !s.count) return;
    // World-down/up expressed in this mesh's own frame (transformDirection
    // normalizes): localDown drives the slide-off; localUp re-seats the regrow.
    o.updateWorldMatrix(true, false);
    _snowMat.copy(o.matrixWorld).invert();
    s.localDown.set(0, -1, 0).transformDirection(_snowMat);
    _snowUp.set(0, 1, 0).transformDirection(_snowMat);
    for (let i = 0; i < s.count; i++) {
      if (s.f[i] <= 0.001) continue;        // already bare here
      s.falling[i] = 1;
      s.vel[i] = 0.6 + Math.random() * 1.0; // initial slide speed
      // Freeze the current seat as the fall's start, so the clump slides off from
      // where it actually sits — not the about-to-be-recomputed new-top seat.
      for (let k = 0; k < 3; k++) s.fallPos[i * 3 + k] = s.basePos[i * 3 + k];
      for (let k = 0; k < 4; k++) s.fallQuat[i * 4 + k] = s.baseQuat[i * 4 + k];
    }
    reseatSnow(s, _snowUp);                  // regrow accumulates on the new top
    s.fill = 0;                              // regrow restarts from bare
  });
}

// One frame of snow shed/regrow for a single snow InstancedMesh. Recomposes the
// instance matrices only when something actually changed (idle trees cost nothing).
function updateSnowMesh(o, dt) {
  const s = o.userData.snow;
  if (s.melting) return;   // a gsap thaw owns this mesh now (see meltSnowAway)
  let dirty = false;
  // A gsap settle (settleSnowIn) drives `fill` itself when entering winter; otherwise
  // bared snow re-accumulates slowly here. The per-clump reveal below runs either way.
  if (s.fill < 1 && !s.settling) { s.fill = Math.min(1, s.fill + dt / SNOW_REGROW_S); dirty = true; }
  if (s.settling) dirty = true;

  for (let i = 0; i < s.count; i++) {
    if (s.falling[i]) {
      s.vel[i] += SNOW_FALL_G * dt;
      s.drop[i] += s.vel[i] * dt;
      s.f[i] -= dt / SNOW_SHED_S;
      if (s.f[i] <= 0) { s.f[i] = 0; s.falling[i] = 0; s.drop[i] = 0; s.vel[i] = 0; }
      dirty = true;
    } else {
      const want = THREE.MathUtils.smoothstep(
        s.fill, s.thresh[i], s.thresh[i] + SNOW_REVEAL_BAND);
      if (Math.abs(want - s.f[i]) > 1e-4) { s.f[i] = want; dirty = true; }
    }
  }
  if (!dirty) return;

  for (let i = 0; i < s.count; i++) {
    const f = s.f[i];
    const sx = s.baseScale[i * 3], sy = s.baseScale[i * 3 + 1], sz = s.baseScale[i * 3 + 2];
    _snowScl.set(sx * f, sy * f, sz * f);
    if (s.falling[i]) {
      // Slide off from the frozen start seat, keeping its original orientation.
      _snowQuat.set(s.fallQuat[i * 4], s.fallQuat[i * 4 + 1],
        s.fallQuat[i * 4 + 2], s.fallQuat[i * 4 + 3]);
      const slide = s.drop[i] * sx; // slide scaled by clump size → reads at any zoom
      _snowPos.set(
        s.fallPos[i * 3] + s.localDown.x * slide,
        s.fallPos[i * 3 + 1] + s.localDown.y * slide,
        s.fallPos[i * 3 + 2] + s.localDown.z * slide);
    } else {
      // Settled / regrowing: sit at the (possibly re-seated) rest transform.
      _snowQuat.set(s.baseQuat[i * 4], s.baseQuat[i * 4 + 1],
        s.baseQuat[i * 4 + 2], s.baseQuat[i * 4 + 3]);
      _snowPos.set(s.basePos[i * 3], s.basePos[i * 3 + 1], s.basePos[i * 3 + 2]);
    }
    _snowMat.compose(_snowPos, _snowQuat, _snowScl);
    o.setMatrixAt(i, _snowMat);
  }
  o.instanceMatrix.needsUpdate = true;
}

// Thaw a detached snow InstancedMesh away over ~1.8s, then remove + dispose it.
// Used when leaving a snowy leaf type ('none'/'evergreen' → leaves) so the coat
// melts instead of blinking out: each clump flattens (its height collapses faster
// than its footprint), shrinks, and sinks down into the bark along its seat normal.
// Driven by gsap's ticker (not updateSnowMesh, which stops once winter goes
// inactive), so it runs to completion no matter the new season. Cosmetic →
// gsap/Math.random, never the seeded stream.
const SNOW_MELT_S = 2.2;
function meltSnowAway(o) {
  const s = o.userData.snow;
  s.melting = true;                    // also tells updateSnowMesh to leave it alone
  const start = new Float32Array(s.f); // freeze each clump's current visible scale
  const state = { m: 0 };
  gsap.to(state, {
    m: 1, duration: SNOW_MELT_S, ease: 'power1.in',
    onUpdate: () => applySnowMelt(o, s, start, state.m),
    onComplete: () => {
      if (o.parent) o.parent.remove(o);
      o.geometry.dispose();
      o.material.dispose();
    }
  });
}

// One frame of the melt at progress m (0→1): recompose every clump from its rest
// seat, shrunk/flattened/sunk by m.
function applySnowMelt(o, s, start, m) {
  const shrink = 1 - m;          // overall footprint
  const flat = (1 - m) * (1 - m); // height collapses faster → it puddles before it goes
  const sink = m * m;            // accelerating slide into the bark
  for (let i = 0; i < s.count; i++) {
    const f = start[i];
    const sx = s.baseScale[i * 3], sy = s.baseScale[i * 3 + 1], sz = s.baseScale[i * 3 + 2];
    _snowScl.set(sx * f * shrink, sy * f * flat, sz * f * shrink);
    _snowQuat.set(s.baseQuat[i * 4], s.baseQuat[i * 4 + 1],
      s.baseQuat[i * 4 + 2], s.baseQuat[i * 4 + 3]);
    // Local -seatNormal (clump geometry +Y maps to the seat normal) points into the
    // bark, so sinking along it melts the clump down onto the wood.
    _snowUp.set(0, 1, 0).applyQuaternion(_snowQuat);
    _snowPos.set(s.basePos[i * 3], s.basePos[i * 3 + 1], s.basePos[i * 3 + 2])
      .addScaledVector(_snowUp, -sink * sy);
    _snowMat.compose(_snowPos, _snowQuat, _snowScl);
    o.setMatrixAt(i, _snowMat);
  }
  o.instanceMatrix.needsUpdate = true;
}

// --- Foliage grow-in ----------------------------------------------------------
// Scale fresh leaves/needles up from nothing so they look like they sprout instead
// of blinking on. Each instance grows over part of the timeline, staggered by a
// per-instance random threshold (the "jitter") so they pop in raggedly, with a
// little overshoot so each settles with a bounce. Used when switching leaf type
// (updateLeafType). Cosmetic → gsap/Math.random, never the seeded stream.
const FOLIAGE_GROW_S = 1.0;     // total grow-in duration
const FOLIAGE_GROW_BAND = 0.4;  // fraction of the timeline a single instance takes to fill
const _growPos = new THREE.Vector3();
const _growQuat = new THREE.Quaternion();
const _growScl = new THREE.Vector3();
const _growMat = new THREE.Matrix4();

// easeOutBack: overshoots past 1 then settles back — the little "pop" at the end.
function backOut(t) {
  const c1 = 1.70158, c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
}

// One frame of the grow at progress g (0→1): rescale every instance from its frozen
// base transform by its own staggered/overshooting reveal factor.
function applyFoliageGrow(o, count, basePos, baseQuat, baseScale, thresh, g) {
  for (let i = 0; i < count; i++) {
    const raw = THREE.MathUtils.clamp((g - thresh[i]) / FOLIAGE_GROW_BAND, 0, 1);
    const f = g >= 1 ? 1 : backOut(raw); // snap to exact base on the final frame
    _growScl.set(baseScale[i * 3] * f, baseScale[i * 3 + 1] * f, baseScale[i * 3 + 2] * f);
    _growQuat.set(baseQuat[i * 4], baseQuat[i * 4 + 1], baseQuat[i * 4 + 2], baseQuat[i * 4 + 3]);
    _growPos.set(basePos[i * 3], basePos[i * 3 + 1], basePos[i * 3 + 2]);
    _growMat.compose(_growPos, _growQuat, _growScl);
    o.setMatrixAt(i, _growMat);
  }
  o.instanceMatrix.needsUpdate = true;
}

// Animate an instanced-foliage mesh growing in: freeze each instance's final
// transform, then tween a global progress that reveals them staggered + jittered.
function growInFoliage(o) {
  if (!o || !o.count) return;
  const count = o.count;
  const basePos = new Float32Array(count * 3);
  const baseQuat = new Float32Array(count * 4);
  const baseScale = new Float32Array(count * 3);
  const thresh = new Float32Array(count);
  for (let i = 0; i < count; i++) {
    o.getMatrixAt(i, _growMat);
    _growMat.decompose(_growPos, _growQuat, _growScl);
    basePos[i * 3] = _growPos.x; basePos[i * 3 + 1] = _growPos.y; basePos[i * 3 + 2] = _growPos.z;
    baseQuat[i * 4] = _growQuat.x; baseQuat[i * 4 + 1] = _growQuat.y;
    baseQuat[i * 4 + 2] = _growQuat.z; baseQuat[i * 4 + 3] = _growQuat.w;
    baseScale[i * 3] = _growScl.x; baseScale[i * 3 + 1] = _growScl.y; baseScale[i * 3 + 2] = _growScl.z;
    thresh[i] = Math.random() * (1 - FOLIAGE_GROW_BAND); // each instance's stagger offset
  }
  applyFoliageGrow(o, count, basePos, baseQuat, baseScale, thresh, 0); // hide before first paint
  const state = { g: 0 };
  gsap.to(state, {
    g: 1, duration: FOLIAGE_GROW_S, ease: 'none',
    onUpdate: () => applyFoliageGrow(o, count, basePos, baseQuat, baseScale, thresh, state.g),
    onComplete: () => applyFoliageGrow(o, count, basePos, baseQuat, baseScale, thresh, 1)
  });
}

// Settle a freshly built snow InstancedMesh in over ~3s when winter begins, so the
// coat accumulates clump-by-clump (driven by the same fill/thresh reveal that
// updateSnowMesh uses for the post-shape regrow) instead of appearing at full depth.
// Starts the clumps collapsed so nothing flashes before the first updateSnow frame.
const SNOW_SETTLE_S = 3.0;
function settleSnowIn(o) {
  if (!o || !o.userData.snow) return;
  const s = o.userData.snow;
  s.settling = true;
  s.fill = 0;
  for (let i = 0; i < s.count; i++) {
    s.f[i] = 0; s.falling[i] = 0; s.drop[i] = 0; s.vel[i] = 0;
    // Collapse to its rest seat at zero scale now (pre-paint), so no full clump shows.
    _snowQuat.set(s.baseQuat[i * 4], s.baseQuat[i * 4 + 1], s.baseQuat[i * 4 + 2], s.baseQuat[i * 4 + 3]);
    _snowPos.set(s.basePos[i * 3], s.basePos[i * 3 + 1], s.basePos[i * 3 + 2]);
    _snowScl.set(0, 0, 0);
    _snowMat.compose(_snowPos, _snowQuat, _snowScl);
    o.setMatrixAt(i, _snowMat);
  }
  o.instanceMatrix.needsUpdate = true;
  gsap.to(s, {
    fill: 1, duration: SNOW_SETTLE_S, ease: 'power2.out',
    onComplete: () => { s.settling = false; s.fill = 1; }
  });
}

// A re-parameterized slice [t0, t1] of an existing curve, used to rebuild a
// branch tube over just part of its length when a branch is partially pruned.
class SubCurve extends THREE.Curve {
  constructor(base, t0, t1) {
    super();
    this.base = base;
    this.t0 = t0;
    this.t1 = t1;
  }
  getPoint(t, target = new THREE.Vector3()) {
    return this.base.getPoint(this.t0 + (this.t1 - this.t0) * t, target);
  }
}

    class Tree {
      constructor(seed) {
        // Seed the shared RNG BEFORE any geometry is built, so the whole tree
        // (trunk, branches, roots, foliage colors) is reproducible from this seed.
        // A missing seed means a brand-new random tree; we still capture the seed
        // so it can be saved and replayed later.
        this.seed = (seed >>> 0) || ((Math.random() * 0xffffffff) >>> 0);
        Math2.seed(this.seed);

        // Ordered log of post-generation structural edits (grow/prune/shape).
        // Replayed on load to reproduce a tree exactly — see record() and
        // main.js createNewTreeWithData(). Cosmetic actions never go here.
        this.history = [];

        // Monotonic counter for main-branch ids ('m#N'). Monotonic (never reused)
        // so ids stay unique even after a main branch is pruned and another added;
        // it advances identically on replay because the same calls run in order.
        this._mainSeq = 0;

        this.mesh = new THREE.Group();
        this.branches = [];
        this.createTrunk();
      }

      // Append a structural edit to the history log (no-op safety if called
      // before the array exists). Entries are plain JSON so they serialize.
      record(entry) {
        if (!this.history) return;
        // grow (trunk branches) and mainBranches size their new branches from the
        // CURRENT trunk dims. If the trunk has been reshaped since generation, stamp
        // the dims in effect now so replay can restore them before re-growing —
        // otherwise the branches would be rebuilt against the final trunk and come
        // out the wrong size. Stored only when it differs from genTrunc, so the
        // common (never-reshaped) case adds nothing. addSubBranches/twigs size off
        // their parent branch, not the trunk, so they don't need it.
        const trunkBranchOp =
          entry.op === 'mainBranches' ||
          (entry.op === 'grow' && entry.kind !== 'addSubBranches');
        if (trunkBranchOp && this.genTrunc) {
          const h = parameters.truncHeight, t = parameters.truncThickness;
          if (h !== this.genTrunc.h || t !== this.genTrunc.t) entry.trunc = { h, t };
        }
        this.history.push(entry);
      }

      updateLeafType() {
  this.branches.forEach(branch => {
    if (branch.updateLeafType) {
      branch.updateLeafType();
    }
  });
}

// Add/remove pine cones across the whole tree (cone toggle). Mirrors the branch +
// sub-branch walk of updateSubBranchFoliage, but only touches cones (the needles
// stay put). Safe to call before the GLB loads — it just clears cones until then.
refreshPineCones() {
  this.branches.forEach(b => b.refreshPineCones && b.refreshPineCones());
  this.mesh.children.forEach(child => {
    if (!child.userData || !child.userData.isMainBranch) return;
    const walk = (o) => {
      if (o.userData && o.userData.branch && o.userData.branch.refreshPineCones) {
        o.userData.branch.refreshPineCones();
      }
      if (o.children) o.children.forEach(walk);
    };
    walk(child);
  });
}

updateSubBranchFoliage(mainBranchGroup) {
  
  const updateFoliageRecursively = (object) => {
    // Look for Branch objects (they have userData.branch)
    if (object.userData && object.userData.branch) {
      const branch = object.userData.branch;
      if (branch.updateLeafType) {
        branch.updateLeafType();
      }
    }
    
    // Recursively check children
    if (object.children) {
      object.children.forEach(child => updateFoliageRecursively(child));
    }
  };
  
  updateFoliageRecursively(mainBranchGroup);
}

// Method to update foliage colors on sub-branches
updateSubBranchFoliageColors(mainBranchGroup) {
  
  const updateColorsRecursively = (object) => {
    // Look for Branch objects
    if (object.userData && object.userData.branch) {
      const branch = object.userData.branch;
      
      if (isNeedleType() && branch.updateNeedleColors) {
        branch.updateNeedleColors();
      } else if (!isNeedleType() && branch.updateLeafColors) {
        branch.updateLeafColors();
      }
    }

    // Recursively check children
    if (object.children) {
      object.children.forEach(child => updateColorsRecursively(child));
    }
  };
  
  updateColorsRecursively(mainBranchGroup);
}

// Create trunk with low poly aesthetic, proper tapering, and random lean
createTrunk(isRebuild = false) {
  // When rebuilding in place (live trunk edits) drop the previous trunk meshes,
  // disposing their GPU resources so a long slider drag doesn't leak memory.
  if (this._trunkParts) {
    this._trunkParts.forEach(m => {
      this.mesh.remove(m);
      if (m.geometry) m.geometry.dispose();
      if (m.material) m.material.dispose();
    });
  }
  this._trunkParts = [];

  const height = parameters.truncHeight;
  const baseRadius = parameters.truncThickness;

  // The trunk's macro identity (lean + cut-off) is rolled once and reused on
  // every rebuild, so dragging the trunk sliders reshapes the SAME tree rather
  // than spawning a different random one.
  if (!this.trunkShape) {
    this.trunkShape = {
      leanAngle: Math2.rangeRandom(0, Math.PI * 0.25),
      leanDirection: Math2.random() * Math.PI * 2,
      isCutOff: Math2.random() < 0.4,
      // Coherent S-curve identity (option #1). Rolled once so slider drags reshape
      // the SAME gnarl instead of rerolling a new one — same contract as the lean.
      curveAmp: Math2.rangeRandom(0.4, 1.0),     // per-tree intensity, scaled by truncCurve
      curveFreq: Math2.rangeRandom(1.2, 2.6),    // half-waves up the trunk (≈1 → C, ≈2 → S)
      curvePhase: Math2.random() * Math.PI * 2,
      curveDir: Math2.random() * Math.PI * 2,     // horizontal plane the bend sweeps through
    };
    // Trunk dims the base tree's branches are about to be sized against. Frozen at
    // generation (this block runs once, never on a slider-drag rebuild) so that if
    // the trunk is later reshaped, replay can rebuild each branch at the trunk it
    // was actually born from. Only height/thickness affect branch length/thickness
    // (see addMoreBranches/addMainBranches); curve/noise are cosmetic.
    this.genTrunc = { h: parameters.truncHeight, t: parameters.truncThickness };
  }
  const { leanAngle, leanDirection } = this.trunkShape;
  const leanX = Math.sin(leanDirection) * Math.sin(leanAngle);
  const leanZ = Math.cos(leanDirection) * Math.sin(leanAngle);
  
  // Store these for later use
  this.leanX = leanX;
  this.leanZ = leanZ;
  this.curveStrength = parameters.truncNoise;
  this.height = height;
  this.baseRadius = baseRadius;

  // Smooth lateral wander down the trunk, a pure function of height so every vertex
  // in a height-ring shifts together. Because it's coherent (unlike truncNoise's
  // per-vertex jitter), extractTrunkCenterline() picks it up and branches reattach
  // to the curved trunk for free. The ×t anchor keeps the base planted in the pot.
  const { curveAmp, curveFreq, curvePhase, curveDir } = this.trunkShape;
  const curveDirX = Math.cos(curveDir);
  const curveDirZ = Math.sin(curveDir);
  const trunkBend = (y) => {
    const tt = y / height;
    const mag = parameters.truncCurve * curveAmp *
                Math.sin(tt * curveFreq * Math.PI + curvePhase) * tt;
    return { x: curveDirX * mag, z: curveDirZ * mag };
  };
  
  // Create trunk points
const points = [];
const segments = 14;

points.push(new THREE.Vector2(baseRadius, 0));

const isCutOff = this.trunkShape.isCutOff; // rolled once; stable across rebuilds
const endHeight = isCutOff ? height * 0.8 : height; // Cut short if cut-off

for (let i = 1; i <= segments; i++) {
  // Bias the rings toward the top: t = 1-(1-u)^p with p>1 clusters samples where
  // the taper AND the S-curve change fastest, so a sharp upper bend no longer has
  // to span one long lathe facet (which read as a stretched top segment).
  const u = i / segments;
  const t = 1 - Math.pow(1 - u, 2.7);
  const actualT = isCutOff ? t * 0.8 : t; // Compress the taper if cut-off
  const radiusAtHeight = baseRadius * Math.pow(1 - actualT, 1.5);
  const currentY = height * actualT;

  points.push(new THREE.Vector2(Math.max(radiusAtHeight, 0.8), currentY));
}

// Store if this trunk was cut off so we can add a cap
this.trunkIsCutOff = isCutOff;
this.trunkEndHeight = endHeight;
this.trunkEndRadius = isCutOff ? baseRadius * 0.3 : 0.8;

  // Create geometry
  const trunkGeometry = new THREE.LatheGeometry(points, 6);
  
  // Apply transformations and track final positions
  this.trunkCurveData = [];
  const geometryPositions = trunkGeometry.attributes.position;
  
  // Apply the same transformations as before, but track the results
  for (let i = 0; i < geometryPositions.count; i++) {
    const y = geometryPositions.getY(i);
    const t = y / height;
    
    if (t > 0) {
      // Apply lean
      const leanOffsetX = leanX * y * 0.8;
      const leanOffsetZ = leanZ * y * 0.8;
      
      // Add random displacement
      const noiseX = (Math2.random() - 0.5) * parameters.truncNoise * (1 - t);
      const noiseZ = (Math2.random() - 0.5) * parameters.truncNoise * (1 - t);

      const bend = trunkBend(y);
      const finalX = geometryPositions.getX(i) + leanOffsetX + bend.x + noiseX;
      const finalZ = geometryPositions.getZ(i) + leanOffsetZ + bend.z + noiseZ;

      geometryPositions.setX(i, finalX);
      geometryPositions.setZ(i, finalZ);
    }
  }
  geometryPositions.needsUpdate = true;
  trunkGeometry.computeVertexNormals();
  
  // NOW extract the actual centerline positions from the transformed geometry
  this.extractTrunkCenterline(geometryPositions, points);
  
  // Rest of trunk creation (colors, material, etc.)
  const colors = [];
  // Per-vertex weight for the ancient bark normal, blended over the pine bark near
  // the base (see applyTrunkBark/injectTrunkAncient). "Bigger = older": a taller,
  // thicker trunk wears the gnarled bark higher. Girth-weighted, normalized against
  // the truncHeight (40..140) / truncThickness (4..18) slider ranges.
  const ancientWeights = [];
  const hNorm = Math.min(1, Math.max(0, (height - 40) / 100));
  const tNorm = Math.min(1, Math.max(0, (baseRadius - 4) / 14));
  const ageFactor = 0.4 * hNorm + 0.6 * tNorm;        // 0..1, girth weighted
  const ancientReach = 0.08 + ageFactor * 0.5;        // ancient bark climbs 8%..58% up
  const ancientFull = ancientReach * 0.35;            // solid below here, fading above
  const positions = trunkGeometry.attributes.position;
  const baseColor = new THREE.Color(parameters.truncColor);

  for (let i = 0; i < positions.count; i++) {
    const y = positions.getY(i);
    const heightRatio = y / height;

    const color = baseColor.clone();
    color.offsetHSL(0, 0, heightRatio * 0.2 - 0.1);
    color.offsetHSL((Math2.random() - 0.5) * 0.05, 0, (Math2.random() - 0.5) * 0.1);
    applyBirchBase(color, heightRatio); // paper birch: sooty toward the base

    colors.push(color.r, color.g, color.b);

    // smoothstep fade: full ancient up to ancientFull, gone by ancientReach.
    const s = Math.min(1, Math.max(0, (heightRatio - ancientFull) / (ancientReach - ancientFull)));
    ancientWeights.push(1 - s * s * (3 - 2 * s));
  }

  trunkGeometry.setAttribute('color', new THREE.BufferAttribute(new Float32Array(colors), 3));
  trunkGeometry.setAttribute('ancientWeight', new THREE.BufferAttribute(new Float32Array(ancientWeights), 1));

  const trunkMaterial = new THREE.MeshStandardMaterial({
    vertexColors: true,
    roughness: 0.8,
    metalness: 0.1,
    flatShading: true
  });
  syncBirchTexture(trunkMaterial);
  applyTrunkBark(trunkMaterial);

  const trunkMesh = new THREE.Mesh(trunkGeometry, trunkMaterial);
  trunkMesh.castShadow = true;
  trunkMesh.receiveShadow = true;
  trunkMesh.userData.isTrunk = true; // lets interaction tell a trunk tap from a branch tap

// Create interior wood layer
const interiorPoints = points.map(point => {
  // Make interior slightly smaller (85% of original radius)
  return new THREE.Vector2(point.x * 0.85, point.y);
});

const interiorGeometry = new THREE.LatheGeometry(interiorPoints, 6);

// ROTATE FIRST, before applying transformations
const rotationAngle = Math.PI * 0.3; // Adjust this value as needed
const interiorPositions = interiorGeometry.attributes.position;

// Apply rotation around Y-axis to the base geometry
for (let i = 0; i < interiorPositions.count; i++) {
  const x = interiorPositions.getX(i);
  const z = interiorPositions.getZ(i);
  
  // Rotate around Y-axis
  const newX = x * Math.cos(rotationAngle) - z * Math.sin(rotationAngle);
  const newZ = x * Math.sin(rotationAngle) + z * Math.cos(rotationAngle);
  
  interiorPositions.setX(i, newX);
  interiorPositions.setZ(i, newZ);
}

// NOW apply the same lean and noise transformations as the outer trunk
for (let i = 0; i < interiorPositions.count; i++) {
  const y = interiorPositions.getY(i);
  const t = y / height;
  
  if (t > 0) {
    const leanOffsetX = leanX * y * 0.8;
    const leanOffsetZ = leanZ * y * 0.8;
    const noiseX = (Math2.random() - 0.5) * parameters.truncNoise * (1 - t);
    const noiseZ = (Math2.random() - 0.5) * parameters.truncNoise * (1 - t);
    
    const bend = trunkBend(y);
    const finalX = interiorPositions.getX(i) + leanOffsetX + bend.x + noiseX;
    const finalZ = interiorPositions.getZ(i) + leanOffsetZ + bend.z + noiseZ;

    interiorPositions.setX(i, finalX);
    interiorPositions.setZ(i, finalZ);
  }
}
interiorPositions.needsUpdate = true;
interiorGeometry.computeVertexNormals();

// Create lighter wood colors for interior
const interiorColors = [];
const interiorPos = interiorGeometry.attributes.position;
const interiorBaseColor = new THREE.Color(parameters.truncColor);
interiorBaseColor.offsetHSL(0, -0.2, 0.3); // Lighter, less saturated

for (let i = 0; i < interiorPos.count; i++) {
  const y = interiorPos.getY(i);
  const heightRatio = y / height;
  
  const color = interiorBaseColor.clone();
  color.offsetHSL(0, 0, heightRatio * 0.15 - 0.05);
  color.offsetHSL((Math2.random() - 0.5) * 0.03, 0, (Math2.random() - 0.5) * 0.08);
  
  interiorColors.push(color.r, color.g, color.b);
}

interiorGeometry.setAttribute('color', new THREE.BufferAttribute(new Float32Array(interiorColors), 3));

const interiorMaterial = new THREE.MeshStandardMaterial({
  vertexColors: true,
  roughness: 0.7,
  metalness: 0.1,
  flatShading: true,
  side: THREE.BackSide // Important: render from inside
});
applyWoodGrain(interiorMaterial);

const interiorMesh = new THREE.Mesh(interiorGeometry, interiorMaterial);
interiorMesh.castShadow = false; // Don't cast shadows
interiorMesh.receiveShadow = true;
interiorMesh.userData.isSapwood = true; // wood-grain normal target (see setBarkTexture)
interiorMesh.raycast = () => {}; // decorative sap-wood: never intercept prune/shape picks (let the ray reach the bark)

// interior mesh 2
// Create a second interior layer with even lighter wood and different rotation
const interior2Points = points.map(point => {
  // Make this one even smaller (75% of original radius)
  return new THREE.Vector2(point.x * 0.75, point.y);
});

const interior2Geometry = new THREE.LatheGeometry(interior2Points, 6);

// Different rotation angle for better gap coverage
const rotation2Angle = Math.PI * 0.4; // Different angle from first interior
const interior2Positions = interior2Geometry.attributes.position;

// Apply rotation around Y-axis to the base geometry
for (let i = 0; i < interior2Positions.count; i++) {
  const x = interior2Positions.getX(i);
  const z = interior2Positions.getZ(i);
  
  // Rotate around Y-axis with different angle
  const newX = x * Math.cos(rotation2Angle) - z * Math.sin(rotation2Angle);
  const newZ = x * Math.sin(rotation2Angle) + z * Math.cos(rotation2Angle);
  
  interior2Positions.setX(i, newX);
  interior2Positions.setZ(i, newZ);
}

// Apply the same lean and noise transformations
for (let i = 0; i < interior2Positions.count; i++) {
  const y = interior2Positions.getY(i);
  const t = y / height;
  
  if (t > 0) {
    const leanOffsetX = leanX * y * 0.8;
    const leanOffsetZ = leanZ * y * 0.8;
    const noiseX = (Math2.random() - 0.5) * parameters.truncNoise * (1 - t);
    const noiseZ = (Math2.random() - 0.5) * parameters.truncNoise * (1 - t);
    
    const bend = trunkBend(y);
    const finalX = interior2Positions.getX(i) + leanOffsetX + bend.x + noiseX;
    const finalZ = interior2Positions.getZ(i) + leanOffsetZ + bend.z + noiseZ;

    interior2Positions.setX(i, finalX);
    interior2Positions.setZ(i, finalZ);
  }
}
interior2Positions.needsUpdate = true;
interior2Geometry.computeVertexNormals();

// Create even lighter wood colors for second interior
const interior2Colors = [];
const interior2Pos = interior2Geometry.attributes.position;
const interior2BaseColor = new THREE.Color(parameters.truncColor);
interior2BaseColor.offsetHSL(0, -0.1, 0.2); // Even lighter and less saturated

for (let i = 0; i < interior2Pos.count; i++) {
  const y = interior2Pos.getY(i);
  const heightRatio = y / height;
  
  const color = interior2BaseColor.clone();
  color.offsetHSL(0, 0, heightRatio * 0.1 - 0.05);
  color.offsetHSL((Math2.random() - 0.5) * 0.02, 0, (Math2.random() - 0.5) * 0.06);
  
  interior2Colors.push(color.r, color.g, color.b);
}

interior2Geometry.setAttribute('color', new THREE.BufferAttribute(new Float32Array(interior2Colors), 3));

const interior2Material = new THREE.MeshStandardMaterial({
  vertexColors: true,
  roughness: 0.6,
  metalness: 0.1,
  flatShading: true
  // Normal rendering (both sides) - no BackSide
});
applyWoodGrain(interior2Material);

const interior2Mesh = new THREE.Mesh(interior2Geometry, interior2Material);
interior2Mesh.castShadow = false;
interior2Mesh.receiveShadow = true;
interior2Mesh.userData.isSapwood = true; // wood-grain normal target (see setBarkTexture)
interior2Mesh.raycast = () => {}; // decorative sap-wood: never intercept prune/shape picks (let the ray reach the bark)

// Add both interior layers to this.mesh
this.mesh.add(interior2Mesh);
this.mesh.add(interiorMesh);
  this.mesh.add(trunkMesh);
  this.trunkMesh = trunkMesh;
  this._trunkParts = [interior2Mesh, interiorMesh, trunkMesh];

  // Surface roots (nebari): small tapered sections flaring off the trunk base.
  // Built last so they rebuild alongside the trunk and track its thickness/color.
  // Lives in roots.js; operates on this Tree (reads baseRadius/lean/trunkShape,
  // appends to mesh/_trunkParts).
  createRoots(this);

  // First build only: grow the branches. On a live rebuild they are re-attached
  // to the reshaped trunk instead (see updateTrunkGeometry).
  if (!isRebuild) {
    this.createBranches();
    this.createMainBranches();
  }
}

// Rebuild the trunk geometry in place from the current parameters (height /
// thickness / curvature) and re-attach the existing branches, so the trunk
// sliders reshape the tree live without discarding grown/pruned branches.
updateTrunkGeometry() {
  this.createTrunk(true);

  // Regular branches: re-attach to the new trunk surface. This only moves their
  // base — geometry, foliage and any user shaping (rotation) are preserved.
  const endHeight = this.trunkEndHeight || parameters.truncHeight;
  this.branches.forEach(branch => {
    if (branch.isPruned || branch.heightRatio == null || !branch.reattach) return;
    branch.reattach(this.getTrunkPositionAtHeight(endHeight * branch.heightRatio));
  });

  // Main-branch groups: reposition the group base (their sub-branches ride along
  // in the group's local space).
  this.mesh.children.forEach(child => {
    const ud = child.userData;
    if (!ud || !ud.isMainBranch || ud.heightRatio == null || ud.angle == null) return;
    const trunkPos = this.getTrunkPositionAtHeight(this.height * ud.heightRatio);
    const connectionDepth = trunkPos.radius * 0.2;
    child.position.set(
      trunkPos.x + Math.cos(ud.angle) * connectionDepth,
      trunkPos.y,
      trunkPos.z + Math.sin(ud.angle) * connectionDepth
    );
  });

  // Re-bend (Curve) and re-crinkle (Roughness) the main branches and re-place their
  // sub-branches onto the new centerline — the same way createTrunk(true) re-noises
  // the trunk and reattaches its branches on every reshape.
  this.updateMainBranchShape();

  // Thin the main branches if the trunk got thinner, so none ends up fatter than
  // the trunk it sprouts from.
  this.updateMainBranchThickness();
}

// Keep each main branch's girth proportional to the trunk: as the trunk thickness
// slider shrinks the trunk, scale the branch's lathe meshes radially (X/Z only,
// leaving length and the parented sub-branches/foliage alone) by how much thinner
// the trunk now is than when the branch was built. Clamped at 1 — a thicker trunk
// doesn't fatten the branches back up (thinner-than-trunk reads fine; fatter
// looks silly). Idempotent: the scale is absolute, recomputed from the as-built
// radius each call, so dragging the slider back and forth doesn't compound.
updateMainBranchThickness() {
  this.mesh.children.forEach(group => {
    const built = group.userData && group.userData.isMainBranch && group.userData.buildBaseRadius;
    if (!built) return;
    const scale = Math.min(1, this.baseRadius / built);
    group.children.forEach(mesh => {
      const ud = mesh.userData;
      // The end caps live in the same group-local frame as the lathe (geometry on
      // the branch axis, identity mesh transform), so scaling x/z shrinks them in
      // lockstep — otherwise a thinned branch flares back out at the cap.
      if (ud && (ud.isMainBranch || ud.isMainBranchInterior || ud.isBrokenCap || ud.isEndCap)) {
        mesh.scale.x = scale;
        mesh.scale.z = scale;
      }
    });
  });
}

// Re-derive every main branch's geometry from the live parameters, the same way
// createTrunk(true) rebuilds the trunk on a slider drag. Each lathe mesh is rebuilt
// from its stored pre-noise base with the current lean + Curve bend (truncCurve) +
// Roughness (truncNoise) re-applied, so the Curve slider bends the main branches and
// the Roughness slider re-crinkles them. Because the bend can shift the branch a lot,
// the centerline is re-extracted and the sub-branches (and end cap) are re-placed onto
// it — so branches stay attached to the main branch through the curve, just like trunk
// branches reattach when the trunk is reshaped.
updateMainBranchShape() {
  this.mesh.children.forEach(group => {
    const gud = group.userData;
    if (!gud || !gud.isMainBranch) return;

    // Rebuild the lathe meshes (outer + interior layers) with lean + curve + noise.
    let outerMesh = null;
    group.children.forEach(mesh => {
      const ud = mesh.userData;
      if (ud && ud.noiseBase) {
        this._applyMainBranchNoise(mesh.geometry, ud.noiseBase, ud.leanX, ud.leanZ,
          ud.branchLength, gud.curveShape, gud.curveScale);
        if (ud.isMainBranch) outerMesh = mesh;
      }
    });
    if (!outerMesh || gud.buildLength == null) return;

    // Re-extract the bent centerline so attachments follow the new curve.
    const oldCenterline = gud.centerline;
    let newCenterline = this.extractMainBranchCenterline(
      outerMesh.geometry.attributes.position,
      [{ x: gud.buildThickness }],
      gud.buildLength
    );
    // The lathe positions still hold the rings past a prune (only their faces were
    // dropped), so clip the re-extracted centerline back to the surviving length —
    // otherwise sub-branches would reattach to the lopped-off, invisible tip.
    if (gud.cutRing != null && gud.cutRing + 1 < newCenterline.length) {
      newCenterline = newCenterline.slice(0, gud.cutRing + 1);
    }
    gud.centerline = newCenterline;

    // Re-place each sub-branch base onto the bent centerline at its stored height.
    // Same contract as Branch.reattach on the trunk: only the base moves; rotation,
    // geometry, foliage and any user shaping are preserved.
    group.children.forEach(child => {
      const sub = child.userData && child.userData.branch;
      if (!sub || sub.isPruned || sub.parentHeight == null || !sub.reattach) return;
      sub.reattach(this.getMainBranchPositionAtHeight(newCenterline, sub.parentHeight));
    });

    // Slide the end cap along by however far the tip moved, so it stays on the tip
    // without re-jittering the broken-cap geometry (which would "boil" every drag).
    if (oldCenterline && oldCenterline.length && newCenterline.length) {
      const oTip = oldCenterline[oldCenterline.length - 1];
      const nTip = newCenterline[newCenterline.length - 1];
      const dx = nTip.x - oTip.x, dy = nTip.y - oTip.y, dz = nTip.z - oTip.z;
      group.children.forEach(child => {
        const ud = child.userData;
        if (ud && (ud.isEndCap || ud.isBrokenCap)) {
          child.position.x += dx; child.position.y += dy; child.position.z += dz;
        }
      });
    }
  });
}

updateTrunkColor() {
    
    if (!this.trunkMesh) {
      return;
    }

    const newColor = new THREE.Color(parameters.truncColor);
    
    // Update trunk colors
    if (this.trunkMesh.geometry.attributes.color) {
      const colors = this.trunkMesh.geometry.attributes.color.array;
      const positions = this.trunkMesh.geometry.attributes.position;
      const height = this.height;
      
      for (let i = 0; i < positions.count; i++) {
        const y = positions.getY(i);
        const heightRatio = y / height;
        
        const color = newColor.clone();
        color.offsetHSL(0, 0, heightRatio * 0.2 - 0.1);
        color.offsetHSL((Math2.random() - 0.5) * 0.05, 0, (Math2.random() - 0.5) * 0.1);
        applyBirchBase(color, heightRatio); // paper birch: sooty toward the base

        const colorIndex = i * 3;
        colors[colorIndex] = color.r;
        colors[colorIndex + 1] = color.g;
        colors[colorIndex + 2] = color.b;
      }

      this.trunkMesh.geometry.attributes.color.needsUpdate = true;
      syncBirchTexture(this.trunkMesh.material);
    }

    // Update interior trunk layers (sapwood). Roots are also vertex-coloured
    // children of mesh, but they're bark, not interior — skip them here and
    // repaint them with the bark formula below.
    this.mesh.children.forEach(child => {
      if (child.userData.isRoot) return;
      if (child !== this.trunkMesh && child.material && child.material.vertexColors) {
        if (child.geometry.attributes.color) {
          const colors = child.geometry.attributes.color.array;
          const positions = child.geometry.attributes.position;
          
          const interiorColor = newColor.clone();
          interiorColor.offsetHSL(0, -0.2, 0.3);
          
          for (let i = 0; i < positions.count; i++) {
            const y = positions.getY(i);
            const heightRatio = y / this.height;
            
            const color = interiorColor.clone();
            color.offsetHSL(0, 0, heightRatio * 0.15 - 0.05);
            color.offsetHSL((Math2.random() - 0.5) * 0.03, 0, (Math2.random() - 0.5) * 0.08);
            
            const colorIndex = i * 3;
            colors[colorIndex] = color.r;
            colors[colorIndex + 1] = color.g;
            colors[colorIndex + 2] = color.b;
          }
          
          child.geometry.attributes.color.needsUpdate = true;
        }
      }
    });

    // Update surface roots — repaint with the same bark formula createRoots
    // used, keyed off each vertex's stored t (rootFracs).
    this.mesh.children.forEach(child => {
      if (!child.userData.isRoot || !child.geometry.attributes.color) return;
      const colors = child.geometry.attributes.color.array;
      const fracs = child.userData.rootFracs;
      for (let i = 0; i < fracs.length; i++) {
        const [r, g, b] = rootVertexColor(newColor, fracs[i]);
        const ci = i * 3;
        colors[ci] = r;
        colors[ci + 1] = g;
        colors[ci + 2] = b;
      }
      child.geometry.attributes.color.needsUpdate = true;
    });

    // Update main branches - look for groups marked as main branches
    this.mesh.children.forEach((child, index) => {
      if (child.userData && child.userData.isMainBranch) {
        this.updateMainBranchColors(child, newColor);
      }
    });

    // Update regular branches (the ones in this.branches array)
    if (this.branches) {
      this.branches.forEach((branch, index) => {
        if (branch.branchMesh && branch.branchMesh.geometry.attributes.color) {
          this.updateRegularBranchColors(branch, newColor);
        }
      });
    }

    // End caps are solid-colored meshes (no vertex-color attribute), so the
    // per-branch recolor loops above skip them. They also live at many nesting
    // depths (twigs, main-branch sub-branches, twigs of those). One traverse
    // over the whole tree recolors every cap by its isEndCap flag — can't miss
    // one — using the same offset _makeCap() applies from truncColor.
    this.mesh.traverse(obj => {
      if (obj.userData.isBrokenCap && obj.geometry && obj.geometry.attributes.color) {
        // Repaint the bark→heartwood gradient against the new trunk color.
        this._fillBrokenCapColors(obj.geometry.attributes.color.array, obj.userData.capFracs, newColor);
        obj.geometry.attributes.color.needsUpdate = true;
      } else if (obj.userData.isEndCap && obj.material) {
        obj.material.color.copy(newColor).offsetHSL(0, -0.1, 0.3);
      }
    });

    // Swap the bark normals to the new color's grain (paper for white, mossy for
    // mossy, etc.). Materials are reused on a recolor, so re-resolve the normalMap.
    refreshBarkNormals();
  }

  updateSubBranchColors(subBranchGroup, newTrunkColor) {
  
  subBranchGroup.children.forEach((child, childIndex) => {

    // Look for the branch mesh (usually a mesh with tube geometry)
    if (child.type === 'Mesh' && child.material && child.geometry.attributes.color) {
      
      const colors = child.geometry.attributes.color.array;
      const positions = child.geometry.attributes.position;
      
      // For sub-branches, find max Z (they extend along Z axis like regular branches)
      let maxZ = -Infinity;
      for (let i = 0; i < positions.count; i++) {
        const z = positions.getZ(i);
        if (z > maxZ) maxZ = z;
      }
      
      for (let i = 0; i < positions.count; i++) {
        const z = positions.getZ(i);
        const lengthRatio = maxZ > 0 ? z / maxZ : 0;
        
        const color = newTrunkColor.clone();
        color.offsetHSL(0, 0, lengthRatio * 0.15);
        color.offsetHSL((Math2.random() - 0.5) * 0.05, 0, (Math2.random() - 0.5) * 0.1);
        applyBirchBand(color, positions, i, lengthRatio, 'z');

        const colorIndex = i * 3;
        colors[colorIndex] = color.r;
        colors[colorIndex + 1] = color.g;
        colors[colorIndex + 2] = color.b;
      }
      
      child.geometry.attributes.color.needsUpdate = true;
    }
    
    // Handle foliage groups within sub-branches (if they have needles)
    else if (child.type === 'Group') {
      child.children.forEach((grandchild, grandchildIndex) => {
        if (grandchild.type === 'Mesh' && grandchild.material && grandchild.geometry.attributes.color) {
          
          const colors = grandchild.geometry.attributes.color.array;
          const positions = grandchild.geometry.attributes.position;
          
          // Same logic as above for nested meshes
          let maxZ = -Infinity;
          for (let i = 0; i < positions.count; i++) {
            const z = positions.getZ(i);
            if (z > maxZ) maxZ = z;
          }
          
          for (let i = 0; i < positions.count; i++) {
            const z = positions.getZ(i);
            const lengthRatio = maxZ > 0 ? z / maxZ : 0;
            
            const color = newTrunkColor.clone();
            color.offsetHSL(0, 0, lengthRatio * 0.15);
            color.offsetHSL((Math2.random() - 0.5) * 0.05, 0, (Math2.random() - 0.5) * 0.1);
            applyBirchBand(color, positions, i, lengthRatio, 'z');

            const colorIndex = i * 3;
            colors[colorIndex] = color.r;
            colors[colorIndex + 1] = color.g;
            colors[colorIndex + 2] = color.b;
          }
          
          grandchild.geometry.attributes.color.needsUpdate = true;
        }
      });
    }
  });
}

// Updated createMainBranches function with animation
createMainBranches() {
  const numMainBranches = Math.floor(Math2.random() * 3); // 1-3 branches
  
  for (let i = 0; i < numMainBranches; i++) {
    // Position main branches low on the trunk, where the taper is still thick
    const heightRatio = 0.01 + (Math2.random() * 0.11);
    const branchHeight = this.height * heightRatio;
    
    // Get trunk position at this height
    const trunkPos = this.getTrunkPositionAtHeight(branchHeight);
    
    // Create main branch parameters
    const branchLength = this.height * (0.4 + Math2.random() * 0.3); // 40-70% of trunk height
    const branchThickness = this.baseRadius * (0.5 + Math2.random() * 0.18); // 50-68% of trunk thickness
    const angle = (Math.PI * 2 / numMainBranches) * i + Math2.random() * 0.5;
    
    // Create main branch (monotonic id; loop i still drives the fan-out angle)
    const mainBranch = this.createMainBranch(
      branchLength,
      branchThickness,
      angle,
      trunkPos,
      this._mainSeq++
    );
    
    this.mesh.add(mainBranch);

    // Grow in staggered (limb → sub-branches → twigs). Per-branch base delay
    // keeps multiple main branches fanning in one after another. Cosmetic jitter
    // stays on Math.random, off the seeded stream.
    this.animateMainBranchGrowth(mainBranch, i * 0.3 + Math.random() * 0.2);
  }
}

// Stagger a freshly added main branch's grow-in so the limb scales up first,
// then its sub-branches, then the twigs — instead of the whole group popping in
// at once. Sub-branches and twigs are nested children, so a parent left at full
// scale would reveal them immediately; we scale each deeper tier to ~0 up front
// and tween it in on a later delay. All timing stays on Math.random (cosmetic) so
// it never perturbs the seeded stream that save/replay depends on.
animateMainBranchGrowth(branchGroup, baseDelay = 0) {
  branchGroup.scale.set(1, 1, 1); // the group rides full-size; its pieces animate

  // Tier 1: the limb's own wood/cap meshes (direct Mesh children).
  const limbMeshes = branchGroup.children.filter(c => c.type === 'Mesh');
  // Tier 2: sub-branch groups hanging off the limb.
  const subBranches = branchGroup.children.filter(c => c.userData && c.userData.branch);
  // Tier 3: twigs nested inside each sub-branch.
  const twigs = [];
  subBranches.forEach(sb => {
    const b = sb.userData.branch;
    if (b && b.twigs) b.twigs.forEach(tw => twigs.push(tw.mesh));
  });

  const pop = (obj, delay, duration) => {
    obj.scale.set(0.01, 0.01, 0.01);
    gsap.to(obj.scale, { x: 1, y: 1, z: 1, duration, ease: 'back.out(1.7)', delay });
  };

  // Slow, unhurried pacing so the three tiers unfurl across the ~5.4s grow-long.wav
  // cue and suit the game's meditative feel. Each tier eases in only as the one
  // before it settles, then the twigs fan out last.
  const subStart = baseDelay + 1.2;    // sub-branches begin as the limb fills out
  const twigStart = subStart + 1.4;    // twigs follow the sub-branches

  limbMeshes.forEach(m => pop(m, baseDelay, 1.6));
  subBranches.forEach((sb, i) => pop(sb, subStart + i * 0.12, 1.4));
  twigs.forEach((tw, i) => pop(tw, twigStart + i * 0.08, 1.4));
}

updateRegularBranchColors(branch, newTrunkColor) {
  const colors = branch.branchMesh.geometry.attributes.color.array;
  const positions = branch.branchMesh.geometry.attributes.position;
  
  // Find max Z (since regular branches extend along Z axis)
  let maxZ = -Infinity;
  for (let i = 0; i < positions.count; i++) {
    const z = positions.getZ(i);
    if (z > maxZ) maxZ = z;
  }
  
  for (let i = 0; i < positions.count; i++) {
    const z = positions.getZ(i);
    const lengthRatio = maxZ > 0 ? z / maxZ : 0;
    
    const color = newTrunkColor.clone();
    color.offsetHSL(0, 0, lengthRatio * 0.15);
    color.offsetHSL((Math2.random() - 0.5) * 0.05, 0, (Math2.random() - 0.5) * 0.1);
    applyBirchBand(color, positions, i, lengthRatio, 'z');

    const colorIndex = i * 3;
    colors[colorIndex] = color.r;
    colors[colorIndex + 1] = color.g;
    colors[colorIndex + 2] = color.b;
  }
  
  branch.branchMesh.geometry.attributes.color.needsUpdate = true;

  // (End caps are recolored in one traverse pass at the end of updateTrunkColor.)

  // Twigs are third-level branches parented to this one but not registered in
  // tree.branches, so recurse here or their bark keeps the old trunk color.
  if (branch.twigs) {
    branch.twigs.forEach(twig => this.updateRegularBranchColors(twig, newTrunkColor));
  }
}

// Restore a main branch's lathe vertices from their pre-noise snapshot, then
// re-apply lean + procedural roughness (scaled by parameters.truncNoise, fading
// toward the tip). Driven from a saved `base` so it can be re-run live when the
// Roughness slider changes, the way the trunk re-noises on reshape.
_applyMainBranchNoise(geometry, base, leanX, leanZ, length, curveShape, curveScale) {
  const positions = geometry.attributes.position;
  const curveStrength = parameters.truncNoise * 0.7;

  // Coherent lateral bend, identical in form to the trunk's trunkBend(): a pure
  // function of position along the branch (t = y/length), scaled by truncCurve so
  // the Curve slider drives it live. Deterministic (no RNG), so it never perturbs
  // the seeded stream replay depends on. curveScale keeps it proportional to the
  // trunk. The ×t anchor pins the base to the trunk so the branch stays attached.
  const bendDirX = curveShape ? Math.cos(curveShape.curveDir) : 0;
  const bendDirZ = curveShape ? Math.sin(curveShape.curveDir) : 0;
  const scale = curveScale || 1;

  for (let i = 0; i < positions.count; i++) {
    const baseX = base[i * 3];
    const y = base[i * 3 + 1];
    const baseZ = base[i * 3 + 2];
    const t = y / length;

    if (t > 0) {
      const leanOffsetX = leanX * y * 0.6;
      const leanOffsetZ = leanZ * y * 0.6;
      const noiseX = (Math2.random() - 0.5) * curveStrength * (1 - t);
      const noiseZ = (Math2.random() - 0.5) * curveStrength * (1 - t);

      let bendX = 0, bendZ = 0;
      if (curveShape) {
        const mag = parameters.truncCurve * curveShape.curveAmp *
                    Math.sin(t * curveShape.curveFreq * Math.PI + curveShape.curvePhase) * t * scale;
        bendX = bendDirX * mag;
        bendZ = bendDirZ * mag;
      }

      positions.setX(i, baseX + leanOffsetX + noiseX + bendX);
      positions.setZ(i, baseZ + leanOffsetZ + noiseZ + bendZ);
    } else {
      positions.setX(i, baseX);
      positions.setZ(i, baseZ);
    }
  }
  positions.needsUpdate = true;
  geometry.computeVertexNormals();
}

createMainBranch(length, thickness, angle, trunkPos, branchIndex) {
  // Keep the base proportional to the trunk it actually sprouts from. The caller
  // sizes thickness off the trunk's (fat) BASE radius, but branches higher up attach
  // where the trunk has already tapered thin — so cap the base to a fraction of the
  // LOCAL trunk radius. Without this, higher branches read as over-flared stumps at
  // their base. Low branches (local radius ≈ base) fall under the cap and are untouched.
  thickness = Math.min(thickness, trunkPos.radius * 0.75);

  const branchGroup = new THREE.Group();
  
  // IMPORTANT: Mark the GROUP for color updates
  branchGroup.userData.isMainBranch = true;
  branchGroup.userData.branchIndex = branchIndex;
  branchGroup.userData.branchId = 'm#' + branchIndex; // stable id for save/replay
  branchGroup.userData.subCount = 0; // running count of sub-branches, for sub ids
  // Kept so the group can be repositioned if the trunk is reshaped live.
  branchGroup.userData.angle = angle;
  branchGroup.userData.heightRatio = this.height > 0 ? trunkPos.y / this.height : 0;
  // Trunk radius this branch was sized against, so updateMainBranchThickness()
  // can keep the branch proportional (and never fatter than) a thinned trunk.
  branchGroup.userData.buildBaseRadius = this.baseRadius;

  // Coherent curve identity, mirroring the trunk's trunkShape S-curve. Rolled once
  // and stored on the group so dragging the Curve (truncCurve) slider reshapes the
  // SAME bend instead of rerolling a new one — exactly the contract the trunk uses.
  // curveScale keeps the branch's curvature proportional to the trunk's (the trunk
  // bends over its full height; a branch bends the same shape over its own length),
  // captured at build so later height-slider drags don't shift it.
  branchGroup.userData.curveShape = {
    curveAmp: Math2.rangeRandom(0.4, 1.0),
    curveFreq: Math2.rangeRandom(1.2, 2.6),
    curvePhase: Math2.random() * Math.PI * 2,
    curveDir: Math2.random() * Math.PI * 2,
  };
  // 0.5 damping keeps main branches gentler than the trunk (they read as too curly
  // at full strength); the length/height term keeps them proportional to the trunk.
  const MAIN_BRANCH_CURVE_DAMPING = 0.5;
  branchGroup.userData.curveScale =
    (this.height > 0 ? length / this.height : 0.5) * MAIN_BRANCH_CURVE_DAMPING;
  branchGroup.userData.buildLength = length;
  branchGroup.userData.buildThickness = thickness;

  // Position at trunk surface
  const connectionDepth = trunkPos.radius * 0.2;
  const x = trunkPos.x + Math.cos(angle) * connectionDepth;
  const z = trunkPos.z + Math.sin(angle) * connectionDepth;
  
  branchGroup.position.set(x, trunkPos.y, z);
  branchGroup.rotation.y = angle;
  branchGroup.rotation.z = -Math.PI * (0.01 + Math2.random() * 0.35); // Slight upward angle
  
  // Create branch points with cut-off option
  const points = [];
  const segments = 6;
  const segmentLength = length / segments;

  points.push(new THREE.Vector2(thickness, 0));

  const isCutOff = Math2.random() < 0.3; // 30% chance of cut-off
  const endLength = isCutOff ? length * 0.7 : length; // Cut short if cut-off

  for (let i = 1; i <= segments; i++) {
    const t = i / segments;
    const actualT = isCutOff ? t * 0.7 : t; // Compress the taper if cut-off
    const radiusAtLength = thickness * Math.pow(1 - actualT, 1.2);
    const currentLength = (segmentLength * i) * (isCutOff ? 0.7 : 1);
    
    points.push(new THREE.Vector2(Math.max(radiusAtLength, 0.8), currentLength));
  }
    
  // Create geometry 
  const branchGeometry = new THREE.LatheGeometry(points, 6);
  
  // Apply curvature and lean similar to trunk
  const branchLeanX = (Math2.random() - 0.5) * 0.3;
  const branchLeanZ = (Math2.random() - 0.5) * 0.3;

  // Snapshot the pre-noise lathe vertices, then displace. Keeping the base lets
  // the Roughness (truncNoise) slider re-crinkle main branches live, the way the
  // trunk already re-noises on reshape — see updateMainBranchShape().
  const branchBase = Float32Array.from(branchGeometry.attributes.position.array);
  this._applyMainBranchNoise(branchGeometry, branchBase, branchLeanX, branchLeanZ, length,
    branchGroup.userData.curveShape, branchGroup.userData.curveScale);
  const geometryPositions = branchGeometry.attributes.position;
  
  // EXTRACT THE TRANSFORMED CENTERLINE - this is crucial for proper sub-branch attachment
  const mainBranchCenterline = this.extractMainBranchCenterline(geometryPositions, points, length);
  branchGroup.userData.centerline = mainBranchCenterline;
  // Lathe layout, kept so a partial prune can truncate the branch by lopping off
  // the profile rings past the cut (see pruneMainBranchAt / _splitLatheIndex).
  // cutRing is the current tip ring (rings 0..cutRing are present); it shrinks as
  // the branch is pruned, so a later prune/curve-drag clips to the new length.
  branchGroup.userData.latheProfileCount = points.length;
  branchGroup.userData.cutRing = points.length - 1;
  
  // Create colors
  const colors = [];
  // Per-vertex bark-normal fade: full at the base, eased out by mid-branch so the
  // texture doesn't read on the thin tips (see applyBranchBarkNormal/injectBranchFade).
  const barkFades = [];
  const positions = branchGeometry.attributes.position;
  const baseColor = new THREE.Color(parameters.truncColor);

  for (let i = 0; i < positions.count; i++) {
    const y = positions.getY(i);
    const lengthRatio = y / length;

    const color = baseColor.clone();
    color.offsetHSL(0, 0, lengthRatio * 0.2 - 0.1);
    color.offsetHSL((Math2.random() - 0.5) * 0.05, 0, (Math2.random() - 0.5) * 0.1);

    colors.push(color.r, color.g, color.b);

    // smoothstep(0.05 -> 0.75) inverted: 1 near the trunk, 0 past ~75% out.
    const f = Math.min(1, Math.max(0, (lengthRatio - 0.05) / 0.70));
    barkFades.push(1 - f * f * (3 - 2 * f));
  }

  branchGeometry.setAttribute('color', new THREE.BufferAttribute(new Float32Array(colors), 3));
  branchGeometry.setAttribute('barkFade', new THREE.BufferAttribute(new Float32Array(barkFades), 1));

  const branchMaterial = new THREE.MeshStandardMaterial({
    vertexColors: true,
    roughness: 0.8,
    metalness: 0.1,
    flatShading: true,
    side: THREE.DoubleSide
  });
  syncBirchTexture(branchMaterial);
  applyBranchBarkNormal(branchMaterial);

  const branchMesh = new THREE.Mesh(branchGeometry, branchMaterial);
  branchMesh.castShadow = true;
  branchMesh.receiveShadow = true;

  // Roughness re-application data (see _applyMainBranchNoise / updateMainBranchShape).
  branchMesh.userData.noiseBase = branchBase;
  branchMesh.userData.leanX = branchLeanX;
  branchMesh.userData.leanZ = branchLeanZ;
  branchMesh.userData.branchLength = length;

  // Create a second interior layer with even lighter wood and different rotation
  const interior2Points = points.map(point => {
    // Make this one even smaller (75% of original radius)
    return new THREE.Vector2(point.x * 0.75, point.y);
  });

  const interior2Geometry = new THREE.LatheGeometry(interior2Points, 6);

  // Different rotation angle for better gap coverage
  const rotation2Angle = Math.PI * 0.4; // Different angle from first interior
  const interior2Positions = interior2Geometry.attributes.position;

  // Apply rotation around Y-axis to the base geometry
  for (let i = 0; i < interior2Positions.count; i++) {
    const x = interior2Positions.getX(i);
    const z = interior2Positions.getZ(i);
    
    // Rotate around Y-axis with different angle
    const newX = x * Math.cos(rotation2Angle) - z * Math.sin(rotation2Angle);
    const newZ = x * Math.sin(rotation2Angle) + z * Math.cos(rotation2Angle);
    
    interior2Positions.setX(i, newX);
    interior2Positions.setZ(i, newZ);
  }

  // Snapshot the post-rotation vertices, then displace (same live-roughness path
  // as the outer layer, so the interior re-crinkles with the Roughness slider too).
  const interior2Base = Float32Array.from(interior2Positions.array);
  this._applyMainBranchNoise(interior2Geometry, interior2Base, branchLeanX, branchLeanZ, length,
    branchGroup.userData.curveShape, branchGroup.userData.curveScale);

  // Create even lighter wood colors for second interior
  const interior2Colors = [];
  const interior2Pos = interior2Geometry.attributes.position;
  const interior2BaseColor = new THREE.Color(parameters.truncColor);
  interior2BaseColor.offsetHSL(0, -0.1, 0.2); // Even lighter and less saturated

  for (let i = 0; i < interior2Pos.count; i++) {
    const y = interior2Pos.getY(i);
    const lengthRatio = y / length; // Changed from heightRatio to lengthRatio
    
    const color = interior2BaseColor.clone();
    color.offsetHSL(0, 0, lengthRatio * 0.1 - 0.05);
    color.offsetHSL((Math2.random() - 0.5) * 0.02, 0, (Math2.random() - 0.5) * 0.06);
    
    interior2Colors.push(color.r, color.g, color.b);
  }

  interior2Geometry.setAttribute('color', new THREE.BufferAttribute(new Float32Array(interior2Colors), 3));

  const interior2Material = new THREE.MeshStandardMaterial({
    vertexColors: true,
    roughness: 0.6,
    metalness: 0.1,
    flatShading: true
  });
  applyWoodGrain(interior2Material);

  const interior2Mesh = new THREE.Mesh(interior2Geometry, interior2Material);
  interior2Mesh.castShadow = false;
  interior2Mesh.receiveShadow = true;
  interior2Mesh.userData.isSapwood = true; // wood-grain normal target (see setBarkTexture)
  interior2Mesh.raycast = () => {}; // decorative sap-wood: never intercept prune/shape picks (let the ray reach the bark)

  // Roughness re-application data (see _applyMainBranchNoise / updateMainBranchShape).
  interior2Mesh.userData.noiseBase = interior2Base;
  interior2Mesh.userData.leanX = branchLeanX;
  interior2Mesh.userData.leanZ = branchLeanZ;
  interior2Mesh.userData.branchLength = length;

  // IMPORTANT: Mark interior for color updates
  interior2Mesh.userData.isMainBranchInterior = true;

  branchGroup.add(interior2Mesh);
  branchGroup.add(branchMesh);

  // Cap the open end of the lathe. A clean branch gets a flat bark disk; a
  // cut-off branch gets a torn "broken" cap (jagged, recessed, with concentric
  // heartwood rings) so the snapped wood interior shows instead of a hollow tube.
  const endRadius = points[points.length - 1].x;
  const endCap = this.createMainBranchEndCap(mainBranchCenterline, endRadius, isCutOff);
  if (endCap) branchGroup.add(endCap);

  // KEEP YOUR ORIGINAL PRUNING SYSTEM - this is what makes pruning work!
  branchMesh.userData.isMainBranch = true;
  branchMesh.userData.branchGroup = branchGroup;

  // Add sub-branches to this main branch. Pass the *actual* tip height (endLength,
  // shortened on a cut-off branch) — not the nominal length — so subs distribute over
  // the real geometry. Using `length` here let cut-off branches sprout subs past the
  // snapped tip, floating them in mid-air above the stump.
  this.addBranchesToMainBranch(branchGroup, endLength, thickness);
  branchMesh.userData.mainBranch = { group: branchGroup, prune: () => this.pruneMainBranch(branchGroup) };
    
  return branchGroup;
}

// Paint a broken cap's vertex colors: a gradient from bark at the rim (frac 1)
// to lighter, slightly warmer heartwood at the recessed center (frac 0), plus a
// touch of per-vertex grain. Kept separate so updateTrunkColor can repaint live.
_fillBrokenCapColors(colorArray, fracs, trunkColor) {
  const bark = trunkColor.clone();
  const heart = trunkColor.clone().offsetHSL(0.015, -0.18, 0.34);
  const tmp = new THREE.Color();
  for (let i = 0; i < fracs.length; i++) {
    const f = fracs[i]; // 1 = rim/bark, 0 = center/heartwood
    tmp.copy(heart).lerp(bark, f);
    tmp.offsetHSL((Math2.random() - 0.5) * 0.02, 0, (Math2.random() - 0.5) * 0.08);
    colorArray[i * 3] = tmp.r;
    colorArray[i * 3 + 1] = tmp.g;
    colorArray[i * 3 + 2] = tmp.b;
  }
}

// Build the cap that closes a main branch's open lathe end. Non-cut-off branches
// taper to a thin tip and get a simple flat bark disk (isEndCap, recolored by the
// shared traverse pass). Cut-off branches get a torn break: concentric jagged
// rings that recede into the trunk toward a deep center, colored bark→heartwood,
// so it reads as snapped wood with the interior growth levels exposed.
createMainBranchEndCap(centerline, endRadius, isCutOff) {
  if (!centerline || centerline.length < 2) return null;

  const tip = centerline[centerline.length - 1];
  const prev = centerline[centerline.length - 2];
  const center = new THREE.Vector3(tip.x, tip.y, tip.z);
  const dir = new THREE.Vector3(tip.x - prev.x, tip.y - prev.y, tip.z - prev.z);
  if (dir.lengthSq() < 1e-6) dir.set(0, 1, 0);
  dir.normalize();

  if (!isCutOff) {
    // Clean tip: just close the small hole with a flat bark disk.
    // The lathe's end ring sits at a constant local Y (lean/noise only shift its
    // vertices sideways, never tilt the ring), so the disk must face straight up
    // local +Y — orienting it to the lean-tilted centerline tangent skews it a few
    // degrees off the actual opening.
    const capGeometry = new THREE.CircleGeometry(Math.max(endRadius, 0.5) * 0.908, 6);
    const capMaterial = new THREE.MeshStandardMaterial({
      color: new THREE.Color(parameters.truncColor).offsetHSL(0, -0.1, 0.3),
      roughness: 0.7,
      metalness: 0.1,
      flatShading: true
    });
    const cap = new THREE.Mesh(capGeometry, capMaterial);
    cap.userData.isEndCap = true; // recolored by updateTrunkColor's traverse pass
    // Sink the disk a touch into the tube so the bark rim reads as a lip around it,
    // rather than a flush-soldered end.
    cap.position.copy(center).add(dir.clone().multiplyScalar(-Math.max(endRadius, 0.6) * 0.15));
    // Aim along the branch tangent, nudged slightly toward local +Y to take a bit
    // of the lean tilt off the disk — without going all the way to straight-up,
    // which is lookAt's degenerate (parallel-to-up) case.
    const faceDir = dir.clone().lerp(new THREE.Vector3(0, 1, 0), 0.35).normalize();
    cap.lookAt(cap.position.clone().add(faceDir));
    // Spin the hexagon in its own plane. Negative = clockwise (as seen looking at
    // the cap face); bump the magnitude to rotate it further.
    cap.rotateZ(-0.2);
    // Bake that orientation/offset into the geometry and zero the mesh transform so
    // the disk lives in the same group-local frame as the lathe. That lets
    // updateMainBranchThickness shrink it with a plain x/z scale (like the tube),
    // instead of leaving it flared out past a thinned branch.
    cap.updateMatrix();
    cap.geometry.applyMatrix4(cap.matrix);
    cap.position.set(0, 0, 0);
    cap.quaternion.identity();
    cap.scale.set(1, 1, 1);
    cap.castShadow = true;
    cap.receiveShadow = true;
    return cap;
  }

  // Broken end. Lay out concentric rings perpendicular to the branch axis, each
  // pushed back into the branch (deeper toward the center) with per-vertex jitter
  // so the rim is ragged and the interior dishes inward like a snapped stub.
  const segments = 8;
  const ringFracs = [1.0, 0.66, 0.36, 0.0]; // rim → center (0 collapses to a point)
  const depth = Math.max(endRadius, 0.8) * 0.6; // how far the center dishes in

  // Build the break in the tube's end-ring plane (constant local +Y), not around
  // the lean-tilted tangent — orienting it to the tangent tips the rim plane and
  // opens a gap on one side where it lifts off the wall.
  const axis = new THREE.Vector3(0, 1, 0);
  const ref = new THREE.Vector3(1, 0, 0);
  const u = new THREE.Vector3().crossVectors(axis, ref).normalize();
  const v = new THREE.Vector3().crossVectors(axis, u).normalize();

  const rings = [];
  for (let r = 0; r < ringFracs.length; r++) {
    const frac = ringFracs[r];
    const isCenter = frac === 0;
    const isRim = r === 0;
    const count = isCenter ? 1 : segments;
    const baseRadius = (isRim ? 1.0 : 0.9) * endRadius * frac; // rim meets the wall
    const rowDepth = depth * Math.pow(1 - frac, 1.4);
    const ring = [];
    for (let s = 0; s < count; s++) {
      const a = (s / segments) * Math.PI * 2;
      // Rim jitters outward only so it never dips inside the wall (gap); inner
      // rings stay ragged both ways.
      const radJag = isCenter ? 0
        : isRim ? 1 + Math2.random() * 0.06
        : 1 + (Math2.random() - 0.5) * 0.18;
      const rr = baseRadius * radJag;
      const jagDepth = (Math2.random() - 0.5) * depth * 0.35;
      const pos = center.clone()
        .add(u.clone().multiplyScalar(Math.cos(a) * rr))
        .add(v.clone().multiplyScalar(Math.sin(a) * rr))
        .add(axis.clone().multiplyScalar(-(rowDepth + jagDepth)));
      ring.push({ pos, frac });
    }
    rings.push(ring);
  }

  const positions = [];
  const fracs = [];
  const pushVert = (vert) => {
    positions.push(vert.pos.x, vert.pos.y, vert.pos.z);
    fracs.push(vert.frac);
  };

  for (let r = 0; r < rings.length - 1; r++) {
    const outer = rings[r];
    const inner = rings[r + 1];
    if (inner.length === 1) {
      const c = inner[0];
      for (let s = 0; s < segments; s++) {
        pushVert(outer[s]);
        pushVert(outer[(s + 1) % segments]);
        pushVert(c);
      }
    } else {
      for (let s = 0; s < segments; s++) {
        const o0 = outer[s], o1 = outer[(s + 1) % segments];
        const i0 = inner[s], i1 = inner[(s + 1) % segments];
        pushVert(o0); pushVert(o1); pushVert(i1);
        pushVert(o0); pushVert(i1); pushVert(i0);
      }
    }
  }

  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  const capFracs = new Float32Array(fracs);
  const colorArray = new Float32Array(fracs.length * 3);
  this._fillBrokenCapColors(colorArray, capFracs, new THREE.Color(parameters.truncColor));
  geom.setAttribute('color', new THREE.BufferAttribute(colorArray, 3));
  geom.computeVertexNormals();

  const mat = new THREE.MeshStandardMaterial({
    vertexColors: true,
    roughness: 0.85,
    metalness: 0.1,
    flatShading: true,
    side: THREE.DoubleSide
  });
  const cap = new THREE.Mesh(geom, mat);
  cap.userData.isBrokenCap = true;       // skipped by updateMainBranchColors
  cap.userData.capFracs = capFracs;      // so updateTrunkColor can repaint the gradient
  cap.castShadow = true;
  cap.receiveShadow = true;
  return cap;
}

extractMainBranchCenterline(geometryPositions, originalPoints, branchLength) {
  const centerlineData = [];
  
  // Group vertices by Y position (height along the branch)
  const heightGroups = {};
  
  // Group vertices by height
  for (let i = 0; i < geometryPositions.count; i++) {
    const x = geometryPositions.getX(i);
    const y = geometryPositions.getY(i);
    const z = geometryPositions.getZ(i);
    
    const heightKey = Math.round(y * 10) / 10; // Round to nearest 0.1
    
    if (!heightGroups[heightKey]) {
      heightGroups[heightKey] = [];
    }
    heightGroups[heightKey].push({ x, y, z });
  }
  
  // Calculate center point for each height
  Object.keys(heightGroups).sort((a, b) => parseFloat(a) - parseFloat(b)).forEach(heightKey => {
    const vertices = heightGroups[heightKey];
    let centerX = 0, centerY = 0, centerZ = 0;
    
    vertices.forEach(vertex => {
      centerX += vertex.x;
      centerY += vertex.y;
      centerZ += vertex.z;
    });
    
    centerX /= vertices.length;
    centerY /= vertices.length;
    centerZ /= vertices.length;
    
    // Find corresponding radius from original points
    const y = centerY;
    const t = y / branchLength;
    let radius = originalPoints[0].x * Math.pow(1 - t, 1.2); // Use original thickness calculation
    radius = Math.max(radius, 0.2);
    
    centerlineData.push({
      x: centerX,
      y: centerY,
      z: centerZ,
      radius: radius
    });
  });
  
  return centerlineData;
}

// Position + radius along a main branch's centerline at an arbitrary height,
// interpolating between the two bracketing samples (same rationale as
// getTrunkPositionAtHeight: snapping leaves sub-branches off a curved main branch).
getMainBranchPositionAtHeight(centerline, targetHeight) {
  if (!centerline || centerline.length < 2) {
    return { x: 0, y: targetHeight, z: 0, radius: 2 };
  }

  if (targetHeight <= centerline[0].y) {
    const p = centerline[0];
    return { x: p.x, y: targetHeight, z: p.z, radius: p.radius };
  }
  const last = centerline[centerline.length - 1];
  if (targetHeight >= last.y) {
    return { x: last.x, y: targetHeight, z: last.z, radius: last.radius };
  }

  for (let i = 1; i < centerline.length; i++) {
    const b = centerline[i];
    if (targetHeight <= b.y) {
      const a = centerline[i - 1];
      const span = b.y - a.y;
      const f = span > 0 ? (targetHeight - a.y) / span : 0;
      return {
        x: a.x + (b.x - a.x) * f,
        y: targetHeight,
        z: a.z + (b.z - a.z) * f,
        radius: a.radius + (b.radius - a.radius) * f
      };
    }
  }

  return { x: last.x, y: targetHeight, z: last.z, radius: last.radius };
}

updateMainBranchColors(branchGroup, newTrunkColor) {
  
  branchGroup.children.forEach((child, childIndex) => {
    
    // Broken end caps also use vertex colors, but carry a bark→heartwood gradient
    // (not the bark-by-length scheme below); they're repainted in the traverse pass.
    if (child.userData && child.userData.isBrokenCap) return;

    // Handle main branch meshes and interior meshes
    if (child.material && child.material.vertexColors && child.geometry.attributes.color) {
      
      const colors = child.geometry.attributes.color.array;
      const positions = child.geometry.attributes.position;
      
      // Find the maximum Y value for length calculation
      let maxY = -Infinity;
      for (let i = 0; i < positions.count; i++) {
        const y = positions.getY(i);
        if (y > maxY) maxY = y;
      }
      
      for (let i = 0; i < positions.count; i++) {
        const y = positions.getY(i);
        const lengthRatio = maxY > 0 ? y / maxY : 0;
        
        let color;
        if (child.userData.isMainBranchInterior) {
          // Lighter color for interior
          color = newTrunkColor.clone();
          color.offsetHSL(0, -0.1, 0.2);
          color.offsetHSL(0, 0, lengthRatio * 0.1 - 0.05);
        } else {
          // Regular branch color
          color = newTrunkColor.clone();
          color.offsetHSL(0, 0, lengthRatio * 0.2 - 0.1);
        }

        color.offsetHSL((Math2.random() - 0.5) * 0.05, 0, (Math2.random() - 0.5) * 0.1);

        const colorIndex = i * 3;
        colors[colorIndex] = color.r;
        colors[colorIndex + 1] = color.g;
        colors[colorIndex + 2] = color.b;
      }

      child.geometry.attributes.color.needsUpdate = true;
      // The birch texture belongs only on the outer bark, not the interior wood.
      if (!child.userData.isMainBranchInterior) syncBirchTexture(child.material);
    }
    
    // NEW: Handle sub-branch groups (created by addBranchesToMainBranch)
    else if (child.type === 'Group' && child.children.length > 0) {
      this.updateSubBranchColors(child, newTrunkColor);
    }
  });
}

pruneMainBranch(branchGroup, instant = false) {
  if (instant) { this.mesh.remove(branchGroup); return; }
  gsap.to(branchGroup.scale, {
    x: 0.01, y: 0.01, z: 0.01,
    duration: 0.8,
    ease: "power2.in",
    onComplete: () => {
      this.mesh.remove(branchGroup);
    }
  });
}

// --- Partial pruning of MAIN branches --------------------------------------
// A main branch is a lathe of `latheProfileCount` rings stacked along its length.
// Pruning keeps rings [0, k] and drops everything past ring k — the lathe-mesh
// equivalent of Branch.cutAt's tube truncation. Because the rings are ordered up
// the branch, "cut at ring k" is just an index filter: keep faces whose lower
// profile row is < k, drop the rest. The dropped faces become the falling offcut.

// Split a lathe geometry's triangles into the stub (rings [0,k]) and the offcut
// (rings [k, tip]). A face spans profile rows j and j+1; its row is min(rows), so
// faces with row < k stay on the stub and the rest fall away. Vertex index maps to
// its profile row as index % P (LatheGeometry lays out vertices phi-major).
_splitLatheIndex(geometry, P, k) {
  const idx = geometry.index.array;
  const stub = [], offcut = [];
  for (let f = 0; f < idx.length; f += 3) {
    const a = idx[f], b = idx[f + 1], c = idx[f + 2];
    const row = Math.min(a % P, b % P, c % P);
    (row < k ? stub : offcut).push(a, b, c);
  }
  return { stub, offcut };
}

// Map a world-space hit on a main branch to the ring it would be cut at: 0 (cut at
// the base = whole-branch removal) through cutRing-1 (lop off just the tip ring).
// Always returns a removable ring so pointing anywhere on the branch cuts from
// there outward; null only if the branch has no geometry. The bias (+0.2) makes a
// segment start pruning ~20% in, matching Branch.cutRingForPoint.
cutRingForMainBranch(group, worldPoint) {
  const c = group.userData.centerline;
  const tipRing = group.userData.cutRing != null
    ? group.userData.cutRing : ((group.userData.latheProfileCount || 1) - 1);
  if (!c || c.length < 2 || tipRing < 1) return null;

  group.updateWorldMatrix(true, false);
  const localY = group.worldToLocal(worldPoint.clone()).y; // height along the branch axis

  // Continuous ring position from localY (rings sit at centerline[j].y).
  let r;
  if (localY <= c[0].y) {
    r = 0;
  } else {
    r = c.length - 1;
    for (let j = 1; j < c.length; j++) {
      if (localY <= c[j].y) {
        r = (j - 1) + (localY - c[j - 1].y) / ((c[j].y - c[j - 1].y) || 1);
        break;
      }
    }
  }

  // keep rings [0,k], remove (k, tip]; clamp so the tip ring is the deepest cut.
  return THREE.MathUtils.clamp(Math.floor(r + 0.2), 0, tipRing - 1);
}

// Red-tint the offcut (the wood + sub-branches a click at ring k would remove) so
// the hover previews the cut, mirroring Branch.previewCut. Whole-branch removal
// (k === 0) is previewed by interaction.js's setMainBranchHighlight instead.
previewMainBranchCut(group, k) {
  this.clearMainBranchCutPreview(group);
  const P = group.userData.latheProfileCount;
  const c = group.userData.centerline;
  if (P == null || !c) return;
  const outer = group.children.find(m => m.userData && m.userData.isMainBranch);
  if (!outer) return;

  const preview = { meshes: [], doomed: [] };

  const { offcut } = this._splitLatheIndex(outer.geometry, P, k);
  if (offcut.length) {
    const geo = outer.geometry.clone();
    geo.setIndex(offcut);
    const overlay = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color: 0xd21a1a }));
    overlay.raycast = () => {};                 // don't intercept hover picking
    overlay.scale.set(outer.scale.x * 1.06, 1, outer.scale.z * 1.06); // just outside the bark
    group.add(overlay);
    preview.meshes.push(overlay);
  }

  const cutY = c[Math.min(k, c.length - 1)].y;
  group.children.forEach(child => {
    const sub = child.userData && child.userData.branch;
    if (sub && !sub.isPruned && sub.parentHeight != null && sub.parentHeight > cutY) {
      sub.previewDoomed();
      preview.doomed.push(sub);
    }
  });

  group.userData._cutPreview = preview;
}

clearMainBranchCutPreview(group) {
  const preview = group.userData._cutPreview;
  if (!preview) return;
  preview.meshes.forEach(m => { group.remove(m); m.geometry.dispose(); m.material.dispose(); });
  preview.doomed.forEach(sub => sub.restoreDoomed());
  group.userData._cutPreview = null;
}

// Lop the main branch off at ring k: keep rings [0,k], drop the rest (wood, the old
// end cap, and any sub-branch whose base sits past the cut) as a falling offcut, and
// re-cap the fresh stub with a torn break. k <= 0 removes the whole branch; k at/past
// the current tip is a no-op. `instant` (save/replay) applies the same end state
// without the falling animation. Mirrors Branch.cutAt for main branches.
pruneMainBranchAt(group, k, instant = false) {
  this.clearMainBranchCutPreview(group);
  const gud = group.userData;
  const P = gud.latheProfileCount;
  const c = gud.centerline;
  if (P == null || !c) return;
  const tipRing = gud.cutRing != null ? gud.cutRing : (P - 1);
  if (k <= 0) { this.pruneMainBranch(group, instant); return; }
  if (k >= tipRing) return;

  // Debris is assembled in the group's local space (so offcut pieces keep their
  // place), then re-parented to the scene root to fall straight down.
  const debris = instant ? null : new THREE.Group();
  if (debris) group.add(debris);

  // Truncate every lathe mesh (outer bark + interior layers); hand the offcut faces
  // to the debris as standalone meshes that share the same local transform.
  group.children.forEach(mesh => {
    const ud = mesh.userData;
    if (!ud || !(ud.isMainBranch || ud.isMainBranchInterior)) return;
    const { stub, offcut } = this._splitLatheIndex(mesh.geometry, P, k);
    if (debris && offcut.length) {
      const geo = mesh.geometry.clone();
      geo.setIndex(offcut);
      const off = new THREE.Mesh(geo, getDebrisMaterial(ud.isMainBranchInterior ? 'mainInner' : 'mainOuter', mesh.material));
      off.castShadow = true;
      off.position.copy(mesh.position);
      off.rotation.copy(mesh.rotation);
      off.scale.copy(mesh.scale);
      debris.add(off);
    }
    mesh.geometry.setIndex(stub);
  });

  // Sub-branches past the cut fall with the offcut; the rest stay on the stub.
  const cutY = c[Math.min(k, c.length - 1)].y;
  [...group.children].forEach(child => {
    const sub = child.userData && child.userData.branch;
    if (sub && !sub.isPruned && sub.parentHeight != null && sub.parentHeight > cutY) {
      // The sub-branch's own pine cones share tree-wide materials; isolate them so the
      // offcut fade only touches copies (instant has no debris/fade — nothing to do).
      if (debris) { sub._isolateConeMaterials(); debris.add(sub.mesh); } else group.remove(sub.mesh);
      sub.isPruned = true;
    }
  });

  // The old tip cap falls with the offcut; the fresh stub gets a new torn break.
  [...group.children].forEach(child => {
    const ud = child.userData;
    if (ud && (ud.isEndCap || ud.isBrokenCap)) {
      if (debris) debris.add(child); else group.remove(child);
    }
  });
  const tip = c[k];
  const prev = c[Math.max(k - 1, 0)];
  const newCap = this.createMainBranchEndCap([prev, tip], tip.radius, true);
  if (newCap) {
    // The cap is built at the branch's as-built (unscaled) radius, so match the
    // current thickness scale the rest of the branch carries — otherwise a stub
    // cut after the thickness slider was lowered pops back to full girth.
    if (gud.buildBaseRadius) {
      const s = Math.min(1, this.baseRadius / gud.buildBaseRadius);
      newCap.scale.x = s;
      newCap.scale.z = s;
    }
    group.add(newCap);
  }

  // Record the new (shorter) length so curve drags and further prunes clip to it.
  gud.cutRing = k;
  gud.centerline = c.slice(0, k + 1);

  if (!debris) return;

  // Re-parent to the scene root (preserving world transform) so "down" is world-down,
  // then fall + fade out — same as Branch.cutAt's offcut.
  let root = this.mesh; while (root.parent) root = root.parent;
  root.attach(debris);
  const materials = [];
  debris.traverse(o => { if (o.material) { o.material.transparent = true; materials.push(o.material); } });
  gsap.to(debris.position, { y: '-=22', duration: 0.9, ease: 'power1.in' });
  gsap.to(materials, {
    opacity: 0,
    duration: 0.9,
    ease: 'power2.in',
    onComplete: () => {
      root.remove(debris);
      // Pooled offcut materials are reused — reset; dispose the rest (the fallen
      // sub-branches' own materials, which are gone for good).
      materials.forEach(m => {
        if (m.userData?.pooled) m.opacity = m.userData.baseOpacity;
        else m.dispose();
      });
    }
  });
}

// Pre-compile the debris (falling-offcut) shader programs at build time so the
// FIRST prune doesn't pay the one-time ~100ms compile stall. We build throwaway
// meshes that mirror exactly what cutAt / pruneMainBranchAt create — same
// geometry, same instancing, same castShadow — using the pooled debris materials,
// add them (invisible) to the scene, and let renderer.compile() link their
// programs (it traverses all objects, visible or not, and compiles shadow-depth
// programs for castShadow meshes). The pooled materials persist in debrisMatPool;
// the warm meshes are removed immediately and never rendered. Best-effort: a no-op
// if the renderer/scene/camera aren't wired yet.
prewarmDebris() {
  const r = app.renderer, s = app.scene, cam = app.camera;
  if (!r || !s || !cam) return;
  const warm = new THREE.Group();
  warm.visible = false;

  const addFoliage = (kind, live) => {
    if (!live) return;
    const key = `${kind}:${live.material.vertexColors ? 1 : 0}`;
    if (debrisMatPool.has(key)) return; // already compiled (e.g. prior tree)
    const fall = new THREE.InstancedMesh(live.geometry, getDebrisMaterial(kind, live.material), Math.max(live.count, 1));
    fall.castShadow = true;
    warm.add(fall);
  };

  // Every Branch (regular + sub-branches on main branches) carries userData.branch.
  const branches = new Set();
  this.mesh.traverse(o => { if (o.userData?.branch) branches.add(o.userData.branch); });
  let woodDone = false;
  branches.forEach(b => {
    if (!woodDone && b.branchMesh) {
      const m = new THREE.Mesh(b.branchMesh.geometry, getDebrisMaterial('wood', b.branchMesh.material));
      m.castShadow = true; warm.add(m); woodDone = true;
    }
    addFoliage('needle', b.instancedNeedles);
    addFoliage('leaf', b.instancedLeaves);
    addFoliage('snow', b.instancedSnow);
  });

  // Main-branch lathe offcut materials (outer bark + interior layers).
  this.mesh.traverse(o => {
    const ud = o.userData;
    if (!o.material || !ud || !(ud.isMainBranch || ud.isMainBranchInterior)) return;
    const kind = ud.isMainBranchInterior ? 'mainInner' : 'mainOuter';
    if (debrisMatPool.has(`${kind}:${o.material.vertexColors ? 1 : 0}`)) return;
    const m = new THREE.Mesh(o.geometry, getDebrisMaterial(kind, o.material));
    m.castShadow = true; warm.add(m);
  });

  if (!warm.children.length) return;
  s.add(warm);
  try { r.compile(s, cam); } catch { /* compile is a best-effort warm-up */ }
  s.remove(warm);
}

// Sub-branch method for main branches
addBranchesToMainBranch(mainBranchGroup, mainBranchLength, mainBranchThickness) {
  const numSubBranches = Math2.rangeRandomInt(2, 5); // 2-4 sub-branches per main branch
  const centerline = mainBranchGroup.userData.centerline;
  
  if (!centerline || centerline.length === 0) {
    console.warn('No centerline data available for main branch, falling back to simple positioning');
    this.addBranchesToMainBranchFallback(mainBranchGroup, mainBranchLength, mainBranchThickness);
    return;
  }
  
  for (let i = 0; i < numSubBranches; i++) {
    const t = 0.3 + (i / numSubBranches) * 0.6; // Position along main branch (30% to 90%)
    const subBranchHeight = mainBranchLength * t;
    
    // Get the ACTUAL position along the transformed main branch
    const branchPos = this.getMainBranchPositionAtHeight(centerline, subBranchHeight);

    // Calculate position around the main branch at this specific point
    const angle = (Math2.random() * Math.PI * 2); // Random angle around main branch

    // Create sub-branch with thickness proportional to the main branch radius at this height
    const subBranchLength = mainBranchLength * (0.7 - t * 0.6 + Math2.random() * 0.15); // 30-70% of main branch length
    const subBranchThickness = branchPos.radius * (0.4 + Math2.random() * 0.2); // 40-60% of main branch radius at this height

    // Pass the parent CENTERLINE position (not an outward-offset surface point) so the
    // Branch constructor's penetration sinks the base INTO the main branch instead of
    // stacking a second outward offset on top — otherwise thin tip sub-branches float off.
    const subBranch = new Branch(
      subBranchLength,
      subBranchThickness,
      angle,
      { x: branchPos.x, y: branchPos.y, z: branchPos.z, radius: branchPos.radius },
      i
    );

    // Stable id for save/replay: parent main id + a per-main running sub index.
    subBranch.mesh.userData.branch = subBranch;
    subBranch.branchId = (mainBranchGroup.userData.branchId || 'm#?') + '/s#' + (mainBranchGroup.userData.subCount++);
    // Height along the parent main branch, so it can be re-placed onto the bent
    // centerline when the Curve slider reshapes the main branch (see updateMainBranchShape).
    subBranch.parentHeight = subBranchHeight;

    mainBranchGroup.add(subBranch.mesh);
  }
}

// NEW: Fallback method in case centerline extraction fails
addBranchesToMainBranchFallback(mainBranchGroup, mainBranchLength, mainBranchThickness) {
  const numSubBranches = Math2.rangeRandomInt(2, 5);
  
  for (let i = 0; i < numSubBranches; i++) {
    const t = 0.3 + (i / numSubBranches) * 0.6;
    const subBranchHeight = mainBranchLength * t;
    
    // Simple positioning without centerline data: base sits on the branch axis and the
    // constructor's penetration carries it outward, so it stays embedded in the parent.
    const radiusAtHeight = mainBranchThickness * Math.pow(1 - t, 1.2);
    const angle = (Math2.random() * Math.PI * 2);

    const subBranchLength = mainBranchLength * (0.7 - t * 0.6 + Math2.random() * 0.15);
    const subBranchThickness = radiusAtHeight * (0.02 + Math2.random() * 0.05);

    const subBranch = new Branch(
      subBranchLength,
      subBranchThickness,
      angle,
      { x: 0, y: subBranchHeight, z: 0, radius: radiusAtHeight },
      i
    );

    subBranch.mesh.userData.branch = subBranch;
    subBranch.branchId = (mainBranchGroup.userData.branchId || 'm#?') + '/s#' + (mainBranchGroup.userData.subCount++);
    subBranch.parentHeight = subBranchHeight;

    mainBranchGroup.add(subBranch.mesh);
  }
}

// New method to extract the actual trunk centerline from the geometry
extractTrunkCenterline(geometryPositions, originalPoints) {
  this.trunkCurveData = [];
  
  // For each height level, find the center point of all vertices at that height
  const heightGroups = {};
  
  // Group vertices by height
  for (let i = 0; i < geometryPositions.count; i++) {
    const x = geometryPositions.getX(i);
    const y = geometryPositions.getY(i);
    const z = geometryPositions.getZ(i);
    
    const heightKey = Math.round(y * 10) / 10; // Round to nearest 0.1
    
    if (!heightGroups[heightKey]) {
      heightGroups[heightKey] = [];
    }
    heightGroups[heightKey].push({ x, y, z });
  }
  
  // Calculate center point for each height
  Object.keys(heightGroups).sort((a, b) => parseFloat(a) - parseFloat(b)).forEach(heightKey => {
    const vertices = heightGroups[heightKey];
    let centerX = 0, centerY = 0, centerZ = 0;
    
    vertices.forEach(vertex => {
      centerX += vertex.x;
      centerY += vertex.y;
      centerZ += vertex.z;
    });
    
    centerX /= vertices.length;
    centerY /= vertices.length;
    centerZ /= vertices.length;
    
    // Find corresponding radius from original points
    const y = centerY;
    const t = y / this.height;
    let radius = this.baseRadius * Math.pow(1 - t, 1.5);
    radius = Math.max(radius, 0.2);
    
    this.trunkCurveData.push({
      x: centerX,
      y: centerY,
      z: centerZ,
      radius: radius
    });
  });
}

// Return the trunk centerline position + radius at an arbitrary height by
// INTERPOLATING between the two bracketing curve samples (trunkCurveData is sorted
// by y ascending). Snapping to the nearest sample instead used to place a branch at
// the x/z of a slightly different height — invisible inside the fat lower trunk, but
// a visible gap on the thin, leaning upper trunk (the "consistently detached" branch).
getTrunkPositionAtHeight(targetHeight) {
  const curve = this.trunkCurveData;
  if (!curve || curve.length < 2) {
    return { x: 0, y: targetHeight, z: 0, radius: this.baseRadius || 4 };
  }

  // Clamp below/above the sampled range to the end samples.
  if (targetHeight <= curve[0].y) {
    return { x: curve[0].x, y: targetHeight, z: curve[0].z, radius: curve[0].radius };
  }
  const last = curve[curve.length - 1];
  if (targetHeight >= last.y) {
    return { x: last.x, y: targetHeight, z: last.z, radius: last.radius };
  }

  // Find the bracketing pair [a, b] with a.y <= targetHeight <= b.y and lerp.
  for (let i = 1; i < curve.length; i++) {
    const b = curve[i];
    if (targetHeight <= b.y) {
      const a = curve[i - 1];
      const span = b.y - a.y;
      const f = span > 0 ? (targetHeight - a.y) / span : 0;
      return {
        x: a.x + (b.x - a.x) * f,
        y: targetHeight,
        z: a.z + (b.z - a.z) * f,
        radius: a.radius + (b.radius - a.radius) * f
      };
    }
  }

  return { x: last.x, y: targetHeight, z: last.z, radius: last.radius };
}

// Create branch geometry
createBranches() {
  const branchCount = 8; // Fixed number of branches - no longer uses foliageDensity
  const height = parameters.truncHeight;
  const actualTrunkEndHeight = this.trunkEndHeight || height;
  
  // Clear existing branches
  this.branches.forEach(branch => {
    this.mesh.remove(branch.mesh);
  });
  this.branches = [];

  // Create branches at different heights with proper trunk connection
  for (let i = 0; i < branchCount; i++) {
    const heightRatio = 0.5 + (i / branchCount) * 0.55; // Branches from 50% to 95% of trunk height
    const branchHeight = actualTrunkEndHeight * heightRatio;
    
    // Get the actual trunk position and radius at this height
    const trunkPos = this.getTrunkPositionAtHeight(branchHeight);
    
    // Calculate branch angle
    const angle = (i / branchCount) * Math.PI * 2.6 + Math2.random() * 0.33;

    const branchLength = actualTrunkEndHeight * (1 - heightRatio) * 0.8;
    
    const branch = new Branch(
      branchLength,
      trunkPos.radius * 0.3,
      angle,
      trunkPos,
      i
    );
    branch.heightRatio = heightRatio; // for re-attaching when the trunk is reshaped
    branch.branchId = 'b#' + i; // stable id for save/replay (trunk branch slot)

    this.branches.push(branch);
    this.mesh.add(branch.mesh);
  }
}

addMoreBranches() {
  const currentBranchCount = this.branches.length;
  const newBranchCount = Math.min(currentBranchCount + 2, 22);
  const actualTrunkEndHeight = this.trunkEndHeight || parameters.truncHeight;
  
  for (let i = currentBranchCount; i < newBranchCount; i++) {
    const heightRatio = Math2.random() * 0.5 + 0.4; // 40% to 90% of actual trunk height
    const branchHeight = actualTrunkEndHeight * heightRatio;
    
    // Get the actual trunk position at this height
    const trunkPos = this.getTrunkPositionAtHeight(branchHeight);
    
    const angle = Math2.random() * Math.PI * 2;
    
    const branch = new Branch(
      actualTrunkEndHeight * (1 - heightRatio) * 0.8,     // Branch length
      trunkPos.radius * 0.25,                             // Branch thickness
      angle,
      trunkPos,
      i
    );
    branch.heightRatio = heightRatio; // for re-attaching when the trunk is reshaped
    branch.branchId = 'b#' + i; // stable id for save/replay (trunk branch slot)

    this.branches.push(branch);
    this.mesh.add(branch.mesh);

    // Animate new branch growth
    branch.mesh.scale.set(0.01, 0.01, 0.01);
    gsap.to(branch.mesh.scale, {
      x: 1, y: 1, z: 1,
      duration: 1.5,
      ease: "back.out(1.7)",
      delay: (i - currentBranchCount) * 0.2
    });
  }
}

// Method 2: Add sub-branches to existing main branches
addMoreSubBranches() {
  // Find all main branch groups
  const mainBranches = this.mesh.children.filter(child => 
    child.userData && child.userData.isMainBranch
  );
  
  if (mainBranches.length === 0) {
    return;
  }
  
  // Randomly select 1-2 main branches to add sub-branches to
  const branchesToGrow = Math.min(Math2.rangeRandomInt(1, 3), mainBranches.length);
  
  for (let i = 0; i < branchesToGrow; i++) {
    const randomMainBranch = mainBranches[Math.floor(Math2.random() * mainBranches.length)];
    this.addSubBranchToMainBranch(randomMainBranch);
  }
}

// Sprout extra twigs on branches that ALREADY exist, so the grow button thickens
// the canopy of older growth too — not only the freshly added branches (which get
// their twigs from the Branch constructor). Picks a random handful of eligible
// branches and grows a couple of animated twigs on each.
growMoreTwigs() {
  // Collect every live, twig-capable branch anywhere in the tree (trunk branches
  // + main-branch sub-branches). userData.branch can sit on a branch's group or
  // its wood mesh, so dedupe with a Set. Twigs and too-thin branches are skipped.
  const seen = new Set();
  const candidates = [];
  const collect = (obj) => {
    const b = obj.userData && obj.userData.branch;
    if (b && !seen.has(b)) {
      seen.add(b);
      if (!b.isPruned && !b.isTwig && b.thickness >= TWIG_MIN_PARENT_THICKNESS) candidates.push(b);
    }
    if (obj.children) obj.children.forEach(collect);
  };
  collect(this.mesh);

  if (candidates.length === 0) return;

  const count = Math.min(Math2.rangeRandomInt(2, 5), candidates.length); // bush out 2-4 of them
  for (let i = 0; i < count; i++) {
    candidates[Math.floor(Math2.random() * candidates.length)].growTwigs();
  }
}

// Method 3: Add a sub-branch to a specific main branch
addSubBranchToMainBranch(mainBranchGroup) {
  // Get the centerline data from the main branch
  const centerline = mainBranchGroup.userData.centerline;
  
  // Get the main branch dimensions.
  let mainBranchLength = 50; // Default fallback
  let mainBranchThickness = 4;

  if (centerline && centerline.length > 1) {
    // Use the centerline, which is clipped back when the branch is pruned, so new
    // sub-branches land on the SURVIVING stub rather than out where the branch used
    // to reach. (A partial prune only drops faces — the lopped-off lathe vertices
    // remain, so a geometry bounding box would still report the full length.)
    mainBranchLength = centerline[centerline.length - 1].y;
    mainBranchThickness = centerline[0].radius;
  } else {
    // No centerline: fall back to the geometry bounds.
    const mainBranchMesh = mainBranchGroup.children.find(child =>
      child.type === 'Mesh' && !child.userData.isMainBranchInterior
    );
    if (mainBranchMesh && mainBranchMesh.geometry) {
      const box = new THREE.Box3().setFromObject(mainBranchMesh);
      mainBranchLength = Math.max(box.max.y - box.min.y, box.max.z - box.min.z, box.max.x - box.min.x);
      mainBranchThickness = Math.min(box.max.x - box.min.x, box.max.z - box.min.z) * 0.5;
    }
  }
  
  // Add 1-2 new sub-branches to this main branch
  const numNewSubBranches = Math2.rangeRandomInt(1, 3);
  
  for (let i = 0; i < numNewSubBranches; i++) {
    const t = 0.3 + Math2.random() * 0.6; // Position along main branch (30% to 90%)
    const subBranchHeight = mainBranchLength * t;
    
    let branchPos, radiusAtHeight;
    // Single angle drives BOTH the growth direction and the constructor's penetration, so
    // the base sinks into the parent along the same axis the branch grows out of. (Previously
    // the offset used one random angle and the constructor a different one, leaving thin
    // tip sub-branches floating.)
    const angle = Math2.random() * Math.PI * 2;

    if (centerline && centerline.length > 0) {
      // Use centerline for accurate positioning
      branchPos = this.getMainBranchPositionAtHeight(centerline, subBranchHeight);
      radiusAtHeight = branchPos.radius;
    } else {
      // Fallback to simple calculation: base on the branch axis; penetration carries it out.
      console.warn('No centerline available, using fallback positioning');
      radiusAtHeight = mainBranchThickness * Math.pow(1 - t, 1.2);
      branchPos = { x: 0, y: subBranchHeight, z: 0, radius: radiusAtHeight };
    }

    // PROPER SIZING RULES: Higher branches (larger t values) should be shorter and thinner
    const remainingLength = 1 - t;
    const subBranchLength = mainBranchLength * (1 - t) * (0.6 + Math2.random() * 0.3);
    const subBranchThickness = radiusAtHeight * (0.3 - t * 0.2 + Math2.random() * 0.1);

    const subBranch = new Branch(
      subBranchLength,
      subBranchThickness,
      angle,
      branchPos,
      Date.now() + i // Use timestamp for unique ID
    );
    
    // Store reference to branch object for future updates
    subBranch.mesh.userData.branch = subBranch;
    subBranch.branchId = (mainBranchGroup.userData.branchId || 'm#?') + '/s#' + (mainBranchGroup.userData.subCount++);
    // Height along the parent, so the Curve slider can re-place it (updateMainBranchShape).
    subBranch.parentHeight = subBranchHeight;

    // Animate the new sub-branch growth
    subBranch.mesh.scale.set(0.01, 0.01, 0.01);
    gsap.to(subBranch.mesh.scale, {
      x: 1, y: 1, z: 1,
      duration: 1.5,
      ease: "back.out(1.7)",
      delay: i * 0.3
    });
    
    mainBranchGroup.add(subBranch.mesh);
  }
}

// Add `count` main branches off the trunk, sized/placed via the seeded RNG so the
// layout is reproducible. Shared by the "main branches" button (which records the
// action) and by history replay. With animate=false (replay) branches appear at
// full scale immediately. NOTE: gsap timing jitter stays on Math.random (cosmetic)
// so it never perturbs the seeded stream replay depends on.
addMainBranches(count, animate = true) {
  for (let i = 0; i < count; i++) {
    const heightRatio = 0.015 + Math2.random() * 0.28;
    const branchHeight = this.height * heightRatio;
    const trunkPos = this.getTrunkPositionAtHeight(branchHeight);

    const branchLength = this.height * (0.4 + Math2.random() * 0.3);
    const branchThickness = this.baseRadius * (0.6 + Math2.random() * 0.2);
    const angle = Math2.random() * Math.PI * 2;

    const mainBranch = this.createMainBranch(
      branchLength, branchThickness, angle, trunkPos, this._mainSeq++
    );
    this.mesh.add(mainBranch);

    if (animate) {
      // Grow in staggered: limb → sub-branches → twigs (see animateMainBranchGrowth).
      this.animateMainBranchGrowth(mainBranch, i * 0.4);
    }
  }
}

// ---- Save / replay support ----------------------------------------------
//
// Deterministic per-action sub-seed. Each recorded action re-seeds the RNG from
// this before it runs, keyed by its slot in the history log. That isolates an
// action's randomness from anything drawn BETWEEN actions (e.g. live trunk-slider
// rebuilds re-noise the bark and consume the stream), so a save replays faithfully
// no matter what untracked draws happened in the session. The index matches
// between recording (history.length) and replay (loop index), so the streams line up.
_seedForAction(index) {
  let a = (this.seed ^ Math.imul(index + 1, 0x9e3779b1)) >>> 0;
  a = Math.imul(a ^ (a >>> 16), 0x45d9f3b) >>> 0;
  a = Math.imul(a ^ (a >>> 16), 0x45d9f3b) >>> 0;
  Math2.seed((a ^ (a >>> 16)) >>> 0);
}

// Call right before performing a user action that will be record()ed: seeds the
// RNG for this action's history slot so the live result matches replay exactly.
beginRecordedAction() {
  this._seedForAction(this.history.length);
}

// Reshape the trunk to a past dims snapshot mid-replay, so the next regrown batch
// sizes its branches against the trunk as it was then (mirrors a live slider drag
// between grows). Only height/thickness matter to branch size; curve/noise are
// cosmetic and settle on the final reshape. The bark re-noise this consumes from
// the seeded stream is wiped by the _seedForAction() that precedes each replayed
// action, so it never desyncs replay.
_applyTrunc(trunc) {
  parameters.truncHeight = trunc.h;
  parameters.truncThickness = trunc.t;
  this.updateTrunkGeometry();
}

// Per-frame winter snow shed/regrow. Walks the tree for snow InstancedMeshes and
// advances each (recompose only happens when a clump is mid-shed or refilling, so
// a settled snowy tree is free). Driven by snow.js's updateWinter() each frame.
updateSnow(dt) {
  if (!this.mesh || dt <= 0) return;
  this.mesh.traverse(o => {
    if (o.userData && o.userData.snow) updateSnowMesh(o, dt);
  });
}

// How many main branches a single "main branches" action adds. Drawn from the
// seeded stream so replay reproduces the same count (mirrors the old inline
// max(1, rangeRandomInt(1,3)) in main.js).
mainBranchBatchSize() {
  return Math.max(1, Math2.rangeRandomInt(1, 3));
}

// Current number of main-branch limbs on the tree (each is a Group flagged
// isMainBranch). Used to enforce MAX_MAIN_BRANCHES on the user "main branches"
// action — purely a count of the live scene graph, so it never touches the
// seeded stream and replay is unaffected.
mainBranchCount() {
  return this.mesh.children.filter(c => c.userData && c.userData.isMainBranch).length;
}

// Total live (non-pruned) branch objects across the whole tree: trunk branches,
// main-branch sub-branches, and twigs. Deduped via a Set since userData.branch
// can sit on both a branch's group and its wood mesh (mirrors growMoreTwigs's
// walk). Pure read of the scene graph — never the seeded stream — used to cap
// the "grow" button at MAX_GROW_BRANCHES.
branchCount() {
  const seen = new Set();
  const walk = (obj) => {
    const b = obj.userData && obj.userData.branch;
    if (b && !b.isPruned) seen.add(b);
    if (obj.children) obj.children.forEach(walk);
  };
  walk(this.mesh);
  return seen.size;
}

// Build maps branchId -> Branch (regular/sub) and branchId -> main-branch Group
// by walking the current scene graph — used to resolve prune/shape targets on
// replay. Rebuilt per lookup since the graph changes as edits are applied.
collectBranchIndex() {
  const branches = new Map();
  const mains = new Map();
  const walk = (obj) => {
    const b = obj.userData && obj.userData.branch;
    if (b && b.branchId && !branches.has(b.branchId)) branches.set(b.branchId, b);
    if (obj.type === 'Group' && obj.userData && obj.userData.isMainBranch && obj.userData.branchId) {
      mains.set(obj.userData.branchId, obj);
    }
    if (obj.children) obj.children.forEach(walk);
  };
  walk(this.mesh);
  return { branches, mains };
}

// Replay a saved edit log onto this (freshly seeded) tree to reproduce it
// exactly. Grow ops re-run the same seeded methods — so they consume the RNG
// stream identically and rebuild the same meshes — then prune/shape ops apply by
// id without animation. Order must match how the edits were originally made.
replayHistory(history) {
  if (!Array.isArray(history)) return;
  // The base tree was just built at genTrunc, so the trunk reflects those dims now.
  // Track that so we only rebuild it when a branch-creating action was recorded at
  // different dims (and restore it to genTrunc if an earlier action moved it).
  let curTrunc = this.genTrunc;
  for (let i = 0; i < history.length; i++) {
    const entry = history[i];
    // Trunk-branch-creating ops must run against the trunk dims they were grown at.
    const trunkBranchOp =
      entry.op === 'mainBranches' ||
      (entry.op === 'grow' && entry.kind !== 'addSubBranches');
    if (trunkBranchOp) {
      const want = entry.trunc || this.genTrunc;
      if (want && curTrunc && (want.h !== curTrunc.h || want.t !== curTrunc.t)) {
        this._applyTrunc(want);
        curTrunc = want;
      }
    }
    this._seedForAction(i); // match the seeding the live action used
    switch (entry.op) {
      case 'grow':
        if (entry.kind === 'addSubBranches') this.addMoreSubBranches();
        else this.addMoreBranches();
        this.growMoreTwigs();
        break;
      case 'mainBranches':
        this.addMainBranches(this.mainBranchBatchSize(), false);
        break;
      case 'cut': {
        const b = this.collectBranchIndex().branches.get(entry.id);
        if (b) b.cutAt(entry.ring, true);
        break;
      }
      case 'pruneMain': {
        const g = this.collectBranchIndex().mains.get(entry.id);
        // ring present = partial prune (lop off past ring k); absent = whole branch.
        if (g && entry.ring != null) this.pruneMainBranchAt(g, entry.ring, true);
        else if (g) this.pruneMainBranch(g, true);
        break;
      }
      case 'shape': {
        const idx = this.collectBranchIndex();
        if (entry.isMain) {
          const g = idx.mains.get(entry.id);
          if (g) { g.rotation.y = entry.ry; g.rotation.z = entry.rz; }
        } else {
          const b = idx.branches.get(entry.id);
          if (b && b.mesh) { b.mesh.rotation.y = entry.ry; b.mesh.rotation.z = entry.rz; }
        }
        break;
      }
    }
  }
}

    }

    class Branch {
      constructor(length, thickness, angle, trunkPos, branchIndex, isTwig = false) {
  this.mesh = new THREE.Group();
  this.length = length;
  this.thickness = thickness;
  this.angle = angle; // kept so the branch can re-attach if the trunk is reshaped
  this.isPruned = false;
  this.isTwig = isTwig; // a third-level branch; never sprouts further twigs

  // FIX: Adjust connection depth based on branch length
  // Longer branches need to go deeper into the trunk to look properly connected
  const basePenetration = trunkPos.radius * 0.3; // Base penetration
  const lengthFactor = Math.min(length / 50, 1.5); // Scale based on branch length
  const connectionDepth = basePenetration + (trunkPos.radius * 0.3 * lengthFactor);
  
  // Calculate position inside the trunk
  const x = trunkPos.x + Math.cos(angle) * connectionDepth;
  const z = trunkPos.z + Math.sin(angle) * connectionDepth;
  const y = trunkPos.y;
  
  this.mesh.position.set(x, y, z);
  
  // Calculate direction - point away from trunk center
  const directionX = Math.cos(angle);
  const directionZ = Math.sin(angle);
  
  // Set rotation to point outward and slightly up
  this.mesh.rotation.y = angle;
  this.mesh.rotation.z = -Math.PI * 0.1; // Slight upward angle

  this.createBranchGeometry();
  this.addFoliage();
  this.createEndCaps();
  this.addTwigs();
}

// Build and attach one twig at parameter `t` along this branch, returning it —
// or null if this branch can't carry twigs (it's a twig itself, too thin, or
// has no curve). Twigs are full Branch instances (own bark, end caps, foliage)
// parented to this branch's mesh, so they ride along when the branch is shaped,
// pruned, or recolored (foliage updates traverse recursively). Shared by the
// initial build (addTwigs) and later animated growth (growTwigs).
_spawnTwig(t) {
  if (this.isTwig || this.thickness < TWIG_MIN_PARENT_THICKNESS || !this.curve) return null;

  // A partial prune shrinks tipRing but leaves this.curve at full length, so a
  // twig sampled past the cut would float in thin air where the branch used to
  // reach. Clamp onto the surviving stub (a touch inside the tip so it sits on
  // wood, not past the cap). Done after the caller's RNG draw, so seeded replay
  // is unaffected — t still consumed the same random, we only relocate it.
  const tipFraction = this.tipRing / BRANCH_TUBULAR_SEGMENTS;
  t = Math.min(t, tipFraction * 0.95);

  const point = this.curve.getPoint(t);
  const tangent = this.curve.getTangent(t);

  // Twigs taper with the branch: ones near the thin outer end come in thinner
  // (and a touch shorter) than ones closer to the base. t spans 0.45–0.95.
  const taper = 1 - t * 0.6; // ~0.73 at the inner end, ~0.43 at the tip
  const twigLength = this.length * (0.3 + Math2.random() * 0.25) * (0.7 + taper * 0.3);
  const twigThickness = this.thickness * (0.4 + Math2.random() * 0.2) * taper;
  // Splay around the parent branch. In branch-local space +X points outward
  // (away from the trunk), so this Y angle near 0 sends the twig outward and
  // near ±π curls it back toward the trunk. A flat 0..2π draw curls ~half the
  // twigs inward; bias it toward the outward hemisphere by squeezing the rear
  // arc (raise the normalized magnitude to a power >1) so most twigs point away
  // while a few still reach back for variety. Still one RNG draw — seeded
  // replay is unaffected.
  let splayAngle = Math2.random() * Math.PI * 2;
  if (splayAngle > Math.PI) splayAngle -= Math.PI * 2;        // -π..π, signed off outward
  splayAngle = Math.sign(splayAngle) * Math.pow(Math.abs(splayAngle) / Math.PI, 1.7) * Math.PI;

  // trunkPos is unused here (twigs aren't trunk-attached); the Branch
  // constructor seeds position/rotation, then we override both to sit on the
  // parent's curve and angle the twig off it.
  const twig = new Branch(
    twigLength,
    twigThickness,
    splayAngle,
    { x: 0, y: 0, z: 0, radius: 0 },
    0,
    true // isTwig — stops the recursion at one extra level
  );

  twig.mesh.position.copy(point);
  twig.mesh.rotation.set(
    (Math2.random() - 0.5) * 0.9,                                  // tilt off the branch plane
    splayAngle,                                                    // splay around the branch
    Math.atan2(tangent.y, tangent.x) + (Math2.random() - 0.5) * 0.6 // roughly follow the branch, then diverge
  );

  this.mesh.add(twig.mesh);
  (this.twigs ||= []).push(twig);
  return twig;
}

// Sprout the initial third level of twigs along the outer half of this branch,
// where real trees fork. No-op on branches too thin to carry them.
addTwigs() {
  const numTwigs = Math2.rangeRandomInt(2, 5); // 2-4 twigs
  for (let i = 0; i < numTwigs; i++) this._spawnTwig(0.45 + Math2.random() * 0.5);
}

// Sprout a few MORE twigs on an already-built branch, animating each in — used
// by the grow button so existing branches keep getting bushier, not just new
// ones. Returns the number actually added (0 if the branch is too thin).
growTwigs() {
  const numNew = Math2.rangeRandomInt(1, 3); // 1-2 more
  let added = 0;
  for (let i = 0; i < numNew; i++) {
    const twig = this._spawnTwig(0.45 + Math2.random() * 0.5);
    if (!twig) break; // branch can't carry twigs; don't bother retrying
    twig.mesh.scale.set(0.01, 0.01, 0.01);
    gsap.to(twig.mesh.scale, {
      x: 1, y: 1, z: 1,
      duration: 1.2,
      ease: "back.out(1.7)",
      delay: i * 0.15
    });
    added++;
  }
  return added;
}

// Re-place this branch's base onto a (possibly reshaped) trunk position. Mirrors
// the connection math in the constructor; leaves rotation/geometry/foliage alone
// so user shaping is preserved.
reattach(trunkPos) {
  const basePenetration = trunkPos.radius * 0.3;
  const lengthFactor = Math.min(this.length / 50, 1.5);
  const connectionDepth = basePenetration + (trunkPos.radius * 0.3 * lengthFactor);
  this.mesh.position.set(
    trunkPos.x + Math.cos(this.angle) * connectionDepth,
    trunkPos.y,
    trunkPos.z + Math.sin(this.angle) * connectionDepth
  );
}

updateFoliageDensity() {
  if (!this.foliageGroup || this.isPruned) return;

  // Clear existing foliage
  while (this.foliageGroup.children.length > 0) {
    this.foliageGroup.remove(this.foliageGroup.children[0]);
  }
  
  // Recreate foliage with new density
  if (isNeedleType()) {
    this.createNeedles();
  } else {
    this.createDeciduousLeaves();
  }

  // Twigs carry their own foliage but aren't in tree.branches; recurse so their
  // density updates too.
  if (this.twigs) this.twigs.forEach(twig => twig.updateFoliageDensity());
}

storeBranchShape() {
  this.storedRotation = this.mesh.rotation.clone();
  this.storedPosition = this.mesh.position.clone();
}

restoreBranchShape() {
  if (this.storedRotation) {
    this.mesh.rotation.copy(this.storedRotation);
  }
  if (this.storedPosition) {
    this.mesh.position.copy(this.storedPosition);
  }
}

// Method to update foliage size in real time
updateFoliageSize() {
  if (!this.foliageGroup || this.isPruned) return;

  // Clear existing foliage
  while (this.foliageGroup.children.length > 0) {
    this.foliageGroup.remove(this.foliageGroup.children[0]);
  }
  
  // Recreate foliage with new size
  if (isNeedleType()) {
    this.createNeedles();
  } else {
    this.createDeciduousLeaves();
  }

  // Twigs carry their own foliage but aren't in tree.branches; recurse so their
  // size updates too.
  if (this.twigs) this.twigs.forEach(twig => twig.updateFoliageSize());
}

updateLeafColors() {
  if (this.instancedLeaves) {
    const geom = this.instancedLeaves.geometry;
    const palette = leafPalette();
    const gradient = paletteIsGradient(palette);
    // The ramp may be missing if this leaf was first built with a flat palette and
    // the user has just switched to a gradient one; add it on demand.
    if (gradient && !geom.attributes.aGrad) addLeafGradientRamp(geom);
    const arr = geom.attributes.instanceColor.array;
    const arr2 = geom.attributes.instanceColor2.array;
    const tips = this.leafTipT;
    for (let i = 0; i < this.instancedLeaves.count; i++) {
      const [lo, hi] = pickLeafColors(palette, gradient);
      // Re-apply the spring tip flush from the stored per-leaf position so a
      // switch to the spring palette lightens tips / deepens the base.
      const t = tips ? tips[i] : 0.5;
      applySpringTipFlush(lo, t);
      if (hi !== lo) applySpringTipFlush(hi, t);
      const o = i * 3;
      arr[o] = lo.r; arr[o + 1] = lo.g; arr[o + 2] = lo.b;
      arr2[o] = hi.r; arr2[o + 1] = hi.g; arr2[o + 2] = hi.b;
    }
    geom.attributes.instanceColor.needsUpdate = true;
    geom.attributes.instanceColor2.needsUpdate = true;
  }

  // Twigs carry their own foliage but aren't in tree.branches; recurse so their
  // leaves recolor too.
  if (this.twigs) this.twigs.forEach(twig => twig.updateLeafColors());
}

      createBranchGeometry() {
  // Create curved branch with low poly aesthetic
  const curve = new THREE.QuadraticBezierCurve3(
    new THREE.Vector3(0, 0, 0),
    new THREE.Vector3(this.length * 0.5, this.length * 0.3, 0),
    new THREE.Vector3(this.length, this.length * 0.2, 0)
  );

  this.curve = curve;

  // LOW POLY
  const branchGeometry = new THREE.TubeGeometry(curve, BRANCH_TUBULAR_SEGMENTS, this.thickness, BRANCH_RADIAL_SEGMENTS);
  this._applyBarkColors(branchGeometry);

  const branchMaterial = new THREE.MeshStandardMaterial({
    vertexColors: true,
    roughness: 0.8,
    metalness: 0.1,
    flatShading: true, // LOW POLY: Enable flat shading
    //two sided: true
    side: THREE.DoubleSide
  });

  const branchMesh = new THREE.Mesh(branchGeometry, branchMaterial);
  branchMesh.castShadow = true;
  branchMesh.receiveShadow = true;
  branchMesh.userData.branch = this;

  this.mesh.add(branchMesh);
  this.branchMesh = branchMesh;
  this.tipRing = BRANCH_TUBULAR_SEGMENTS; // current tip; shrinks as the branch is partially pruned
  this.tubeParams = {
    radialSegments: BRANCH_RADIAL_SEGMENTS,
    tubularSegments: BRANCH_TUBULAR_SEGMENTS
  };
}

// Write the bark gradient into a tube geometry's vertex `color` attribute.
// Shared by the full branch and the truncated stub rebuilt on a partial prune.
_applyBarkColors(geometry) {
  const colors = [];
  const positions = geometry.attributes.position;
  const baseColor = new THREE.Color(parameters.truncColor);

  for (let i = 0; i < positions.count; i++) {
    const lengthRatio = positions.getZ(i) / this.length;
    const color = baseColor.clone();
    color.offsetHSL(0, 0, lengthRatio * 0.15);
    applyBirchBand(color, positions, i, lengthRatio, 'z');

    colors.push(color.r, color.g, color.b);
  }

  geometry.setAttribute('color', new THREE.BufferAttribute(new Float32Array(colors), 3));
}

// --- Partial pruning -------------------------------------------------------
// A branch is a tube of BRANCH_TUBULAR_SEGMENTS segments along this.curve.
// Hovering highlights everything past the nearest ring in red (previewCut);
// clicking lops that part off, keeping the stub up to the ring (cutAt). The
// very base always keeps at least one segment; hovering the tip cuts nothing.

// World-space hit point -> the ring index to cut at: 0 removes the whole
// branch, tipRing-1 removes just the last segment. Always returns a removable
// ring so pointing anywhere on the branch cuts from there outward.
cutRingForPoint(worldPoint) {
  this.mesh.updateWorldMatrix(true, false);
  const local = this.mesh.worldToLocal(worldPoint.clone());

  // Nearest point on the (baked, un-bent) curve, sampled finely.
  let bestT = 0;
  let bestDist = Infinity;
  const SAMPLES = 40;
  for (let i = 0; i <= SAMPLES; i++) {
    const t = i / SAMPLES;
    const d = this.curve.getPoint(t).distanceToSquared(local);
    if (d < bestDist) { bestDist = d; bestT = t; }
  }

  // Snap to a ring, biased so a segment starts pruning once the cursor is ~20%
  // in from its tip edge (a plain round waits for the 50% midpoint), so cuts
  // trigger sooner as you move down a segment.
  const ring = Math.floor(bestT * BRANCH_TUBULAR_SEGMENTS + 0.2);
  return THREE.MathUtils.clamp(ring, 0, this.tipRing - 1);
}

// Highlight the wood (and foliage) past `ring` red to preview what a click
// removes. The wood highlight is a separate red overlay sleeve over exactly the
// doomed segments, so the boundary is a hard edge at the cut ring — tinting the
// tube's own vertex colors would gradient across the shared seam ring.
previewCut(ring) {
  if (this._previewRing === ring) return;
  this.clearCutPreview();
  this._previewRing = ring;

  const cutT = ring / BRANCH_TUBULAR_SEGMENTS;
  const tipT = this.tipRing / BRANCH_TUBULAR_SEGMENTS;
  this._cutPreviewMesh = new THREE.Mesh(
    new THREE.TubeGeometry(
      new SubCurve(this.curve, cutT, tipT),
      this.tipRing - ring,
      this.thickness * 1.06, // just outside the wood, to avoid z-fighting
      BRANCH_RADIAL_SEGMENTS
    ),
    new THREE.MeshBasicMaterial({ color: 0xd21a1a })
  );
  this._cutPreviewMesh.raycast = () => {}; // don't intercept hover picking
  this.mesh.add(this._cutPreviewMesh);

  // Remember and recolor the foliage beyond the cut so it can be restored.
  this._foliagePreview = [];
  const cutX = this.curve.getPoint(cutT).x;
  this._eachFoliageBeyond(cutX, (item) => {
    if (item.kind === 'leaf') {
      this._foliagePreview.push({ kind: 'leaf', ref: item.ref, color: item.ref.material.color.getHex() });
      item.ref.material.color.setHex(0xd21a1a);
    } else { // needle instance
      const arr = item.ref.geometry.attributes.instanceColor.array;
      const o = item.index * 3;
      this._foliagePreview.push({ kind: 'needle', ref: item.ref, index: item.index, rgb: [arr[o], arr[o + 1], arr[o + 2]] });
      arr[o] = 0.82; arr[o + 1] = 0.1; arr[o + 2] = 0.1;
      item.ref.geometry.attributes.instanceColor.needsUpdate = true;
    }
  });

  // Twigs whose base sits past the cut would fall with it — light them up too,
  // matching which twigs cutAt() actually reparents into the debris.
  this._previewTwigs = [];
  if (this.twigs) {
    for (const twig of this.twigs) {
      if (!twig.isPruned && twig.mesh.position.x >= cutX) {
        twig.previewDoomed();
        this._previewTwigs.push(twig);
      }
    }
  }
}

// Undo a previewCut, removing the red overlay and restoring foliage colors.
clearCutPreview() {
  if (this._previewRing == null) return;

  if (this._cutPreviewMesh) {
    this.mesh.remove(this._cutPreviewMesh);
    this._cutPreviewMesh.geometry.dispose();
    this._cutPreviewMesh.material.dispose();
    this._cutPreviewMesh = null;
  }

  if (this._foliagePreview) {
    // Flag every instanced mesh we recolored (a branch can carry more than one, e.g.
    // the evergreen type's needles + snow); tracking only the last leaves the rest red.
    const dirty = new Set();
    for (const item of this._foliagePreview) {
      if (item.kind === 'leaf') {
        item.ref.material.color.setHex(item.color);
      } else {
        const arr = item.ref.geometry.attributes.instanceColor.array;
        const o = item.index * 3;
        arr[o] = item.rgb[0]; arr[o + 1] = item.rgb[1]; arr[o + 2] = item.rgb[2];
        dirty.add(item.ref);
      }
    }
    for (const ref of dirty) ref.geometry.attributes.instanceColor.needsUpdate = true;
    this._foliagePreview = null;
  }

  if (this._previewTwigs) {
    this._previewTwigs.forEach(twig => twig.restoreDoomed());
    this._previewTwigs = null;
  }

  this._previewRing = null;
}

// Light up this entire branch red — used on twigs that a parent branch's cut
// would take with it, so the hover preview makes clear they're going too. The
// twig is always pruned whole, so we tint all of it: wood glows via emissive
// (each branch owns its wood material), foliage reuses the same red as the
// parent's previewCut. Idempotent; paired with restoreDoomed().
previewDoomed() {
  if (this._doomedPreview) return;

  const wood = [];
  [this.branchMesh, this.endCap].forEach(m => {
    if (m && m.material && m.material.emissive) {
      wood.push({ ref: m, emissive: m.material.emissive.getHex() });
      m.material.emissive.setHex(0xd21a1a);
    }
  });

  const foliage = [];
  this._eachFoliageBeyond(-Infinity, (item) => { // -Infinity = the whole twig
    if (item.kind === 'leaf') {
      foliage.push({ kind: 'leaf', ref: item.ref, color: item.ref.material.color.getHex() });
      item.ref.material.color.setHex(0xd21a1a);
    } else { // needle instance
      const arr = item.ref.geometry.attributes.instanceColor.array;
      const o = item.index * 3;
      foliage.push({ kind: 'needle', ref: item.ref, index: item.index, rgb: [arr[o], arr[o + 1], arr[o + 2]] });
      arr[o] = 0.82; arr[o + 1] = 0.1; arr[o + 2] = 0.1;
      item.ref.geometry.attributes.instanceColor.needsUpdate = true;
    }
  });

  this._doomedPreview = { wood, foliage };

  // Twigs growing off this branch fall with it (see cutAt/prune), so tint them
  // too — otherwise the red preview stops at the sub-branch and the player can't
  // tell the attached twigs are going as well.
  if (this.twigs) this.twigs.forEach(twig => twig.previewDoomed());
}

// Undo previewDoomed, restoring the twig's wood and foliage colors.
restoreDoomed() {
  if (this.twigs) this.twigs.forEach(twig => twig.restoreDoomed());
  if (!this._doomedPreview) return;

  this._doomedPreview.wood.forEach(w => w.ref.material.emissive.setHex(w.emissive));

  // A branch can carry more than one instanced foliage mesh (e.g. the evergreen
  // type has both needles and snow), so flag needsUpdate on EVERY mesh we touched —
  // tracking just the last one leaves the others stuck on the red preview color.
  const dirty = new Set();
  for (const item of this._doomedPreview.foliage) {
    if (item.kind === 'leaf') {
      item.ref.material.color.setHex(item.color);
    } else {
      const arr = item.ref.geometry.attributes.instanceColor.array;
      const o = item.index * 3;
      arr[o] = item.rgb[0]; arr[o + 1] = item.rgb[1]; arr[o + 2] = item.rgb[2];
      dirty.add(item.ref);
    }
  }
  for (const ref of dirty) ref.geometry.attributes.instanceColor.needsUpdate = true;
  this._doomedPreview = null;
}

// Visit each foliage element whose base sits at or beyond local-x `cutX`.
// Needles and leaves are both InstancedMeshes (visited per-instance, kind
// 'needle'); the 'leaf' kind is kept for any future per-mesh foliage.
_eachFoliageBeyond(cutX, cb) {
  if (!this.foliageGroup) return;
  const p = new THREE.Vector3();
  const m = new THREE.Matrix4();
  this.foliageGroup.children.forEach(child => {
    if (child.isInstancedMesh) {
      for (let i = 0; i < child.count; i++) {
        child.getMatrixAt(i, m);
        p.setFromMatrixPosition(m);
        if (p.x >= cutX) cb({ kind: 'needle', ref: child, index: i });
      }
    } else if (child.isMesh && child.position.x >= cutX) {
      cb({ kind: 'leaf', ref: child });
    }
  });
}

// Lop off everything past `ring` and drop it as falling debris. ring 0 removes
// the whole branch; otherwise the stub tube is rebuilt and re-capped.
// `instant` (used by save/replay) applies the same end state but discards the
// offcut immediately instead of animating it falling away.
cutAt(ring, instant = false) {
  this.clearCutPreview();
  if (ring == null || ring >= this.tipRing) return;

  // Debris shares this.mesh's local space (identity transform) so reparented
  // pieces keep their positions, then we animate the whole group away.
  const debris = new THREE.Group();
  this.mesh.add(debris);

  if (ring === 0) {
    // Whole-branch removal: hand every part to the debris and mark it pruned.
    debris.add(this.branchMesh);
    if (this.endCap) { debris.add(this.endCap); this.endCap = null; }
    // Cones share tree-wide geometry/material — drop them via the helper (which
    // isolates their materials) rather than the generic reparent below.
    this._dropPineCones(debris, instant);
    [...this.foliageGroup.children].forEach(child => debris.add(child));
    // Any twigs growing off this branch fall with it.
    if (this.twigs) this.twigs.forEach(twig => { debris.add(twig.mesh); twig.isPruned = true; });
    this.isPruned = true;
    this.tipRing = 0;
  } else {
    const cutT = ring / BRANCH_TUBULAR_SEGMENTS;
    const tipT = this.tipRing / BRANCH_TUBULAR_SEGMENTS; // current tip, before this cut
    const cutX = this.curve.getPoint(cutT).x;

    // Wood: keep [0, cutT], hand the offcut [cutT, tipT] to the debris group.
    // Use the pooled debris material so fading the offcut doesn't touch the live
    // stub and we don't recompile a shader program on every cut.
    const offcut = new THREE.Mesh(
      this._makeTubeGeometry(cutT, tipT, this.tipRing - ring),
      getDebrisMaterial('wood', this.branchMesh.material)
    );
    offcut.castShadow = true;
    debris.add(offcut);
    this.branchMesh.geometry.dispose();
    this.branchMesh.geometry = this._makeTubeGeometry(0, cutT, ring);
    this.tipRing = ring;

    // Cap: the old tip cap falls with the offcut; the fresh stub gets a new cap.
    if (this.endCap) {
      debris.add(this.endCap); // reparent keeps local coords
      this.endCap = null;
    }
    this.endCap = this._makeCap(cutT);
    if (this.endCap) this.mesh.add(this.endCap);

    // Twigs whose base sits past the cut fall with the offcut; ones on the
    // surviving stub stay put. (Twig base x is in this same curve-local space.)
    if (this.twigs) {
      this.twigs.forEach(twig => {
        if (!twig.isPruned && twig.mesh.position.x >= cutX) {
          debris.add(twig.mesh);
          twig.isPruned = true;
        }
      });
    }
    // Instanced foliage (needles and leaves) past the cut: instances can't be
    // reparented one-by-one, so each instanced mesh hands its offcut instances to
    // a falling copy and collapses them on the live stub. See _dropInstancedFoliageBeyond.
    this._dropInstancedFoliageBeyond(this.instancedNeedles, cutX, debris, 'needle');
    this._dropInstancedFoliageBeyond(this.instancedLeaves, cutX, debris, 'leaf');
    this._dropInstancedFoliageBeyond(this.instancedSnow, cutX, debris, 'snow');
    // Pine cones seated on the offcut section fall with it (section-aware by coneT).
    this._dropPineCones(debris, instant, cutT);
    // Retire the winter-snow clumps seated past the cut so the shed/regrow never
    // rebuilds them on the (now removed) tip — otherwise they regrow in thin air.
    this._retireSnowBeyond(cutT);
  }

  // Replay path: skip the falling animation, just drop the offcut now.
  if (instant) {
    this.mesh.remove(debris);
    debris.traverse(o => {
      // Pooled debris materials are shared/reused — never dispose them.
      if (o.material && !o.material.userData?.pooled) o.material.dispose?.();
      // Instanced foliage shares the live stub's geometry — disposing it would
      // wipe the surviving foliage; only dispose the offcut's own geometry.
      if (o.geometry && !o.isInstancedMesh) o.geometry.dispose?.();
    });
    return;
  }

  // The debris was assembled in the branch's (rotated) local space. Re-parent
  // it to the scene root with attach() — this preserves its world transform but
  // makes "down" truly world-down, so the offcut falls straight rather than
  // drifting back toward the trunk.
  let root = this.mesh;
  while (root.parent) root = root.parent;
  root.attach(debris);

  // Fall straight down and fade out. (A scale-to-zero would shrink toward the
  // debris pivot, which sits at the branch base — i.e. back toward the trunk —
  // so we fade instead of shrink.)
  const materials = [];
  debris.traverse(o => {
    if (o.material) { o.material.transparent = true; materials.push(o.material); }
  });
  gsap.to(debris.position, { y: '-=22', duration: 0.9, ease: 'power1.in' });
  gsap.to(materials, {
    opacity: 0,
    duration: 0.9,
    ease: 'power2.in',
    onComplete: () => {
      root.remove(debris);
      // Pooled debris materials are reused across cuts — reset, don't dispose
      // (disposing would free their shader program and recompile next prune).
      materials.forEach(m => {
        if (m.userData?.pooled) m.opacity = m.userData.baseOpacity;
        else m.dispose();
      });
    }
  });
}

// Hand an instanced foliage mesh's offcut (instances whose base sits past local-x
// `cutX`) to the falling `debris`. Instances can't be reparented one at a time, so
// we make a copy that keeps only the doomed instances (the rest collapsed to zero
// scale) and collapse those same instances on the live stub. The copy shares the
// geometry (so per-instance colors come along) and uses the pooled debris
// material (transparent, never disposed) so fading the offcut doesn't touch the
// survivors and we don't recompile a shader program. No-op when nothing to drop.
_dropInstancedFoliageBeyond(live, cutX, debris, kind) {
  if (!live) return;
  const fall = new THREE.InstancedMesh(live.geometry, getDebrisMaterial(kind, live.material), live.count);
  fall.castShadow = true;
  const m = new THREE.Matrix4();
  const p = new THREE.Vector3();
  const collapsed = new THREE.Matrix4().makeScale(0, 0, 0);
  let dirty = false;
  for (let i = 0; i < live.count; i++) {
    live.getMatrixAt(i, m);
    p.setFromMatrixPosition(m);
    if (p.x >= cutX) {
      fall.setMatrixAt(i, m);         // keep in the falling copy
      live.setMatrixAt(i, collapsed); // remove from the surviving stub
      dirty = true;
    } else {
      fall.setMatrixAt(i, collapsed); // not part of the offcut
    }
  }
  fall.instanceMatrix.needsUpdate = true;
  if (dirty) {
    live.instanceMatrix.needsUpdate = true;
    debris.add(fall);
  }
}

// After a partial cut, permanently retire the snow-cover clumps seated past curve
// param `cutT`: zero them in the regrow state (so updateSnowMesh leaves them at
// scale 0 and never reveals them again) and collapse their live instance now, in
// case it sat on the dirty side of the x-based offcut drop. The visible falling
// offcut is built by _dropInstancedFoliageBeyond; this only fixes the regrow state.
_retireSnowBeyond(cutT) {
  const snow = this.instancedSnow;
  const s = snow && snow.userData.snow;
  if (!s) return;
  const collapsed = new THREE.Matrix4().makeScale(0, 0, 0);
  let dirty = false;
  for (let i = 0; i < s.count; i++) {
    if (s.tParam[i] < cutT) continue;
    s.f[i] = 0; s.falling[i] = 0; s.drop[i] = 0; s.vel[i] = 0;
    s.thresh[i] = 2; // > 1 → smoothstep(fill∈[0,1], …) never reveals it again
    snow.setMatrixAt(i, collapsed);
    dirty = true;
  }
  if (dirty) snow.instanceMatrix.needsUpdate = true;
}

// Build a bark-colored tube over the curve slice [t0, t1] with `tubSegs` segments.
_makeTubeGeometry(t0, t1, tubSegs) {
  const geom = new THREE.TubeGeometry(
    new SubCurve(this.curve, t0, t1),
    Math.max(tubSegs, 1),
    this.thickness,
    BRANCH_RADIAL_SEGMENTS
  );
  this._applyBarkColors(geom);
  return geom;
}

// Add foliage to the branch
addFoliage() {
  this.foliageGroup = new THREE.Group();
  
  if (isNeedleType()) {
    this.createNeedles();
  } else {
    this.createDeciduousLeaves();
  }
  
  this.mesh.add(this.foliageGroup);
}

// Dispatch a needled (conifer) foliage build to the right style. Every style sets
// this.instancedNeedles / this.needlesPerCluster, so recolor + prune work the same.
createNeedles() {
  if (parameters.leafType === 'juniper') this.createJuniperFoliage();
  else if (parameters.leafType === 'larch') this.createLarchFoliage();
  else if (parameters.leafType === 'candle') this.createCandleNeedles();
  else this.createPineNeedles();
  // Evergreen / Winter: the same pine needles, but with snow draped over the top.
  // overFoliage keeps the needles we just built (createSnow normally clears them).
  if (parameters.leafType === 'evergreen') this.createSnow({ overFoliage: true });
  // Optional pine cones (pine + candle only); rides the foliage rebuild so it's
  // cleared/recreated with the needles automatically.
  if (pineConesWanted()) this.createPineCones();
}

// Hang a few GLB pine cones near this branch's tip, parented into foliageGroup so
// they track shaping/prune and clear with the foliage. Sparse (only some branches
// bear cones) and cosmetic — Math.random, never the seeded stream, so placement is
// not reproduced across saves (intentional; only the on/off state persists). No-op
// until the GLB has loaded (makeCone() returns null), then refreshPineCones catches
// up. Skips twigs and the thinnest/shortest branches.
createPineCones() {
  if (!this.foliageGroup || this.isPruned || !this.curve) return;
  if (this.thickness < 0.5 || this.length < 12) return; // skip only the very finest sprigs
  if (Math.random() > 0.35) return;                // most branches stay bare

  // Keep cones on the part of the branch that still exists — a partially pruned
  // branch leaves this.curve full-length but caps the live tube at tipRing.
  const tipFraction = this.tipRing / BRANCH_TUBULAR_SEGMENTS;

  const count = 1 + (Math.random() < 0.35 ? 1 : 0); // usually 1, sometimes 2
  for (let i = 0; i < count; i++) {
    const cone = makeCone();
    if (!cone) return;                              // GLB not loaded yet

    // Size relative to the branch it grows on (the GLB is Y-up: base near origin,
    // tip at +Y). Modest — a couple of branch-radii tall — so it reads as a cone,
    // not an ornament; floored so cones on the thinnest sprigs aren't microscopic.
    const height = THREE.MathUtils.clamp(this.thickness * 1.6, 1.8, 5.5)
      * (0.85 + Math.random() * 0.3);
    cone.scale.multiplyScalar(height / CONE_LENGTH);

    // The GLB cone is a SPINDLE — fattest in the middle, tapering to a point at both
    // ends — so anchoring it by the bottom tip makes it balance on a point with its
    // body in the air (reads as "floating"). Instead sink the lower ~quarter into the
    // branch so the fat middle sits at the bark and it looks like it's growing out.
    // (Clone is centre-origin; shift it down inside the pivot, along the cone axis.)
    cone.position.y = height * 0.25;
    const pivot = new THREE.Group();
    pivot.userData.isPineCone = true;
    pivot.add(cone);

    const t = (0.55 + Math.random() * 0.4) * tipFraction; // bunched toward the live tip
    pivot.userData.coneT = t;                        // curve param, for section-aware prune
    // Seat the cone anywhere AROUND the branch (top, sides, underside) and point it
    // radially outward, so cones jut sideways and hang down — any direction, not just
    // off the top. Build the cross-section basis perpendicular to the branch axis, pick
    // a random angle around it, anchor just inside the bark on that side, then aim the
    // cone's axis (local +Y) down that radial with a small random wobble.
    const tangent = this.curve.getTangent(t).normalize();
    let e1 = new THREE.Vector3().crossVectors(tangent, new THREE.Vector3(0, 1, 0));
    if (e1.lengthSq() < 1e-4) e1.set(1, 0, 0); else e1.normalize();
    const e2 = new THREE.Vector3().crossVectors(tangent, e1).normalize();
    const phi = Math.random() * Math.PI * 2;
    const radial = e1.multiplyScalar(Math.cos(phi)).addScaledVector(e2, Math.sin(phi)).normalize();
    pivot.position.copy(this.curve.getPoint(t)).addScaledVector(radial, this.thickness * 0.55);
    pivot.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), radial);
    pivot.rotateX((Math.random() - 0.5) * 0.8); // small wobble off the pure radial
    pivot.rotateZ((Math.random() - 0.5) * 0.8);
    this.foliageGroup.add(pivot);
  }
}

// Add/remove just the pine cones (cone toggle) without rebuilding the needles, so
// flipping the checkbox doesn't re-sprout all the foliage. Recurses into twigs.
refreshPineCones() {
  if (this.foliageGroup) {
    for (const c of [...this.foliageGroup.children]) {
      if (c.userData && c.userData.isPineCone) this.foliageGroup.remove(c);
    }
    if (!this.isPruned && pineConesWanted()) this.createPineCones();
  }
  if (this.twigs) this.twigs.forEach(twig => twig.refreshPineCones());
}

// Pine cones share their geometry + material with every other cone on the tree
// (cloned cheaply by reference in makeCone), so a debris fade/dispose would otherwise
// fade and then free every cone at once. Clone the materials on any cone still in this
// branch's foliage onto throwaway copies, so a subsequent debris fall only touches the
// copies (the fade path never disposes geometry). Used when this whole branch's foliage
// is handed to a falling main-branch offcut.
_isolateConeMaterials() {
  if (!this.foliageGroup) return;
  for (const c of this.foliageGroup.children) {
    if (c.userData && c.userData.isPineCone) {
      c.traverse(o => { if (o.isMesh && o.material) o.material = o.material.clone(); });
    }
  }
}

// Drop this branch's pine cones — all of them, or only those on the section at/beyond
// curve param `cutT` (a partial cut) — so a cut never leaves a cone floating where its
// branch section used to be. Animated cuts isolate each falling cone's materials and
// hand it to the falling `debris`; the instant (replay) path just removes them, so a
// shared cone geometry never reaches the debris geometry-dispose.
_dropPineCones(debris, instant, cutT = -Infinity) {
  if (!this.foliageGroup) return;
  for (const c of [...this.foliageGroup.children]) {
    if (!c.userData || !c.userData.isPineCone || c.userData.coneT < cutT) continue;
    this.foliageGroup.remove(c);
    if (instant) continue;
    c.traverse(o => { if (o.isMesh && o.material) o.material = o.material.clone(); });
    debris.add(c);
  }
}

// Larch — a deciduous conifer. Its short, soft needles ring the branch in whorls:
// each whorl seats 15–30 needles evenly around the FULL circumference and fans them
// radially outward (leaning toward the tip), and the whorls are spaced ALONG the
// whole branch — the distinctive larch look of bottlebrush rings rather than tufts
// bunched on top. Built on the needle machinery (instancedNeedles + needlesPerCluster
// + needleTipT) so recolor/prune/wind all apply, but unlike the evergreen conifers
// its mesh is marked isLeaf so the autumn leaf-fall gilds and drops it needle-by-
// needle (see leafFall.js fallMode).
createLarchFoliage() {
  this.instancedLeaves = null;
  this.instancedSnow = null;

  const sizeScale = parameters.foliageSize / 15;

  // Whorls spaced along the branch; whorl size (needles per ring) scales with
  // density toward the 15–30 of a real larch rosette.
  const numSpurs = Math.max(3, Math.min(
    Math.floor(3 + this.length * 0.12 + parameters.foliageDensity * 0.14), 22));
  const needlesPerRosette = Math.max(12, Math.min(
    Math.floor(14 + parameters.foliageDensity * 0.3), 30));
  // A few extra rosettes seated right at the growing tip: a fresh larch/pine tip is a
  // little forward-pointing clump, so these sit at the very end and sweep their
  // needles out ALONG the branch (a candle-like tuft) rather than ringing it flat.
  const tipRosettes = this.isTwig ? 2 : 3;
  const numWhorls = numSpurs + tipRosettes;
  const totalNeedles = numWhorls * needlesPerRosette;
  // Stored under the needle field name so updateNeedleColors recolors per whorl.
  this.needlesPerCluster = needlesPerRosette;

  // Short, slim, soft needle.
  const needleLength = 2.6 * sizeScale;
  const needleWidth = 0.10;
  const needleGeometry = new THREE.CylinderGeometry(needleWidth, needleWidth * 0.05, needleLength, 3, 1);
  needleGeometry.translate(0, needleLength / 2, 0); // base at origin, tip toward +Y

  const colorPalette = needlePalette();
  const paletteColors = Colors[colorPalette] || Colors.pine;

  const needleMaterial = new THREE.MeshStandardMaterial({
    color: 0xffffff, roughness: 0.8, metalness: 0.1, flatShading: true, side: THREE.DoubleSide
  });

  const instancedNeedles = new THREE.InstancedMesh(needleGeometry, needleMaterial, totalNeedles);
  const instanceColors = new Float32Array(totalNeedles * 3);
  // Per-spur position along the branch (0 base → 1 tip), kept so updateNeedleColors
  // can re-apply the spring tip flush per rosette.
  const clusterTipT = new Float32Array(numWhorls);

  const dummy = new THREE.Object3D();
  const quat = new THREE.Quaternion();
  const Y_AXIS = new THREE.Vector3(0, 1, 0);
  let instanceIndex = 0;

  const tipFraction = this.tipRing / BRANCH_TUBULAR_SEGMENTS;
  // Whorls run most of the branch, but the sub-branches (the non-twig branches) leave
  // their base barer so the needles cluster up toward the growing end — off the base
  // ~25%. The fine twigs keep needles almost to their base.
  const WHORL_START = this.isTwig ? 0.1 : 0.25;
  const TILT = 0.32;          // gentle lean toward the tip → an upswept shuttlecock whorl

  for (let whorlIdx = 0; whorlIdx < numWhorls; whorlIdx++) {
    // The last `tipRosettes` whorls are the fresh-tip clump: a CASCADE seated along the
    // very end rather than one tuft. tipF runs 0 (innermost — only a touch tighter than
    // the last real whorl) → 1 (outermost, right at the tip: tightest, almost straight
    // out). It drives the lean, how tight the bases gather, how far past the cap they
    // sit, and the size, so the clump tapers to a fine point.
    const isTipRosette = whorlIdx >= numSpurs;
    const tipF = isTipRosette
      ? (tipRosettes > 1 ? (whorlIdx - numSpurs) / (tipRosettes - 1) : 1)
      : 0;
    let t;
    if (isTipRosette) {
      t = (0.86 + 0.14 * tipF) * tipFraction;
    } else {
      // Spread the whorls evenly from WHORL_START all the way to the tip — the last
      // whorl lands at tipFraction (needles reach the very end), with a clamped jitter
      // so the spacing still looks natural rather than ruled.
      const span = Math.max(numSpurs - 1, 1);
      const frac = THREE.MathUtils.clamp((whorlIdx + (Math2.random() - 0.5) * 0.6) / span, 0, 1);
      t = (WHORL_START + (1 - WHORL_START) * frac) * tipFraction;
    }
    const curvePoint = this.curve.getPoint(t);
    const tangent = this.curve.getTangent(t).normalize();

    // Cross-section basis (e1, e2) spanning the plane perpendicular to the branch,
    // so the whorl can radiate the needles evenly all the way AROUND the branch.
    const e1 = new THREE.Vector3().crossVectors(tangent, Y_AXIS);
    if (e1.lengthSq() < 1e-4) e1.set(1, 0, 0);
    e1.normalize();
    const e2 = new THREE.Vector3().crossVectors(tangent, e1).normalize();
    // Random phase so successive whorls don't line their needles up into rows.
    const phase = Math2.random() * Math.PI * 2;

    const outward = THREE.MathUtils.clamp((t / tipFraction - WHORL_START) / (1 - WHORL_START), 0, 1);
    clusterTipT[whorlIdx] = outward;
    // Rosettes nearer the tip lean further forward (point further out); the clump
    // cascade sweeps from slightly tighter than the last whorl up to almost straight out.
    const tilt = isTipRosette
      ? THREE.MathUtils.lerp(1.35, 2.4, tipF)
      : TILT + outward * 0.9;
    const baseColor = applySpringTipFlush(
      new THREE.Color(paletteColors[Math.floor(Math2.random() * paletteColors.length)]), outward);

    for (let n = 0; n < needlesPerRosette; n++) {
      // Evenly step the needle around the full circle (a small jitter keeps it from
      // looking machined) — this is what makes the whorl wrap clear around the branch.
      const theta = phase + (n + (Math2.random() - 0.5) * 0.4) / needlesPerRosette * Math.PI * 2;
      const radial = e1.clone().multiplyScalar(Math.cos(theta)).addScaledVector(e2, Math.sin(theta));
      // Point radially outward, leaning toward the tip; a touch of per-needle tilt
      // variation gives the whorl some depth instead of a perfectly flat disc.
      const dir = radial.clone().addScaledVector(tangent, tilt + (Math2.random() - 0.5) * 0.2).normalize();

      // Seat each needle on the branch surface at its own angle around the trunk, so
      // the bases ring the branch and the needles fan out from the wood all around.
      // The tip-clump rosettes instead gather their bases tighter to the axis and sit
      // progressively further past the end, so the cascade bunches into a fine point
      // capping the branch end (the outermost tightest of all).
      const seatR = isTipRosette ? THREE.MathUtils.lerp(0.30, 0.07, tipF) : 0.5;
      dummy.position.copy(curvePoint).addScaledVector(radial, this.thickness * seatR);
      if (isTipRosette) dummy.position.addScaledVector(tangent, this.thickness * THREE.MathUtils.lerp(0.2, 0.7, tipF));
      quat.setFromUnitVectors(Y_AXIS, dir);
      dummy.quaternion.copy(quat);
      // Whorls shrink toward the branch tip (smaller, younger growth out there), on
      // top of the per-needle length jitter; the clump cascade tapers further out to
      // a fine point.
      const taper = (1 - 0.42 * outward) * (isTipRosette ? THREE.MathUtils.lerp(0.95, 0.72, tipF) : 1);
      dummy.scale.set(taper, (0.8 + Math2.random() * 0.45) * taper, taper);
      dummy.updateMatrix();
      instancedNeedles.setMatrixAt(instanceIndex, dummy.matrix);

      const needleColor = baseColor.clone();
      needleColor.offsetHSL((Math2.random() - 0.5) * 0.1, (Math2.random() - 0.5) * 0.22, (Math2.random() - 0.5) * 0.3);
      const ci = instanceIndex * 3;
      instanceColors[ci] = needleColor.r; instanceColors[ci + 1] = needleColor.g; instanceColors[ci + 2] = needleColor.b;
      instanceIndex++;
    }
  }

  this.needleTipT = clusterTipT;
  needleGeometry.setAttribute('instanceColor', new THREE.InstancedBufferAttribute(instanceColors, 3));
  injectFoliageShader(needleMaterial); // per-instance color + shared wind sway
  instancedNeedles.instanceMatrix.needsUpdate = true;
  instancedNeedles.castShadow = true;
  instancedNeedles.receiveShadow = false;
  instancedNeedles.raycast = () => {}; // let prune/shape clicks pass through to wood
  // Deciduous conifer: mark sheddable so the autumn leaf-fall gilds and drops it.
  instancedNeedles.userData.isLeaf = true;
  this.foliageGroup.add(instancedNeedles);
  this.instancedNeedles = instancedNeedles;
}

// Shimpaku juniper scale foliage — the iconic non-pine bonsai. Instead of needle
// cylinders, each instance is a scaly "cord" (makeJuniperCordGeometry): a twig
// sheathed in tiny appressed scale leaves. The cords are fanned into dense, domed
// tufts on the outer branch — more hemispherical than a pine tuft — so they mass
// into juniper's billowing cloud pads. Reuses the needle bookkeeping
// (instancedNeedles + needlesPerCluster + needleTipT) so updateNeedleColors, the
// prune-drop path, and the wind shader all apply unchanged.
createJuniperFoliage() {
  this.instancedLeaves = null;
  this.instancedSnow = null;

  const sizeScale = parameters.foliageSize / 15;

  // A few dense, rounded tufts; cord count per tuft scales with the density slider.
  const numClusters = Math.floor(4 + parameters.foliageDensity * 0.45 + this.length * 0.1);
  const finalClusterCount = Math.max(Math.min(numClusters, 30), 2);
  const cordsPerCluster = Math.max(4, Math.floor(5 + parameters.foliageDensity * 0.22));
  const totalCords = finalClusterCount * cordsPerCluster;
  // Stored under the needle field name so updateNeedleColors recolors per tuft.
  this.needlesPerCluster = cordsPerCluster;

  const cordGeometry = makeJuniperCordGeometry(sizeScale);

  const colorPalette = needlePalette();
  const paletteColors = Colors[colorPalette] || Colors.juniper;

  const cordMaterial = new THREE.MeshStandardMaterial({
    color: 0xffffff, roughness: 0.85, metalness: 0.0, flatShading: true, side: THREE.DoubleSide
  });

  const instanced = new THREE.InstancedMesh(cordGeometry, cordMaterial, totalCords);
  const instanceColors = new Float32Array(totalCords * 3);
  // Per-cluster position along the branch (0 base → 1 tip), kept so
  // updateNeedleColors can re-apply the spring tip flush per tuft.
  const clusterTipT = new Float32Array(finalClusterCount);

  const dummy = new THREE.Object3D();
  const quat = new THREE.Quaternion();
  const Y_AXIS = new THREE.Vector3(0, 1, 0);
  let instanceIndex = 0;

  const tipFraction = this.tipRing / BRANCH_TUBULAR_SEGMENTS;
  const PAD_START = 0.25;
  const CONE = Math.PI * 0.52; // near-hemispherical splay → rounded cloud puff

  for (let clusterIdx = 0; clusterIdx < finalClusterCount; clusterIdx++) {
    const t = (PAD_START + (1 - PAD_START) * (clusterIdx + Math2.random() * 0.5) / finalClusterCount) * tipFraction;
    const curvePoint = this.curve.getPoint(t);
    const tangent = this.curve.getTangent(t).normalize();

    // Seat the puff on the upper side of the branch and aim it up-and-out.
    const topNormal = Y_AXIS.clone().addScaledVector(tangent, -Y_AXIS.dot(tangent));
    if (topNormal.lengthSq() < 1e-4) topNormal.set(1, 0, 0);
    topNormal.normalize();
    const base = curvePoint.clone().addScaledVector(topNormal, this.thickness * 0.5);
    const axis = topNormal.clone().addScaledVector(tangent, 0.25).normalize();

    // Two perpendiculars to the tuft axis, to splay cords around it.
    const u = new THREE.Vector3().crossVectors(axis, Y_AXIS);
    if (u.lengthSq() < 1e-4) u.set(1, 0, 0);
    u.normalize();
    const v = new THREE.Vector3().crossVectors(axis, u).normalize();

    const outward = THREE.MathUtils.clamp((t / tipFraction - PAD_START) / (1 - PAD_START), 0, 1);
    clusterTipT[clusterIdx] = outward;
    const baseColor = applySpringTipFlush(
      new THREE.Color(paletteColors[Math.floor(Math2.random() * paletteColors.length)]), outward);

    for (let c = 0; c < cordsPerCluster; c++) {
      // Splay the cords through a near-hemisphere; sqrt() biases them toward the
      // rim so the puff fills into a dome rather than a sparse spike.
      const phi = CONE * Math.sqrt(Math2.random());
      const theta = Math2.random() * Math.PI * 2;
      const dir = axis.clone().multiplyScalar(Math.cos(phi))
        .addScaledVector(u, Math.sin(phi) * Math.cos(theta))
        .addScaledVector(v, Math.sin(phi) * Math.sin(theta))
        .normalize();

      // Spread the bases through a small puff body so cords emerge from a volume.
      dummy.position.copy(base)
        .addScaledVector(axis, Math2.random() * this.thickness * 0.4)
        .addScaledVector(u, (Math2.random() - 0.5) * this.thickness * 0.5)
        .addScaledVector(v, (Math2.random() - 0.5) * this.thickness * 0.5);
      quat.setFromUnitVectors(Y_AXIS, dir);
      dummy.quaternion.copy(quat);
      dummy.rotateY(Math2.random() * Math.PI * 2); // random roll so scale rows vary
      dummy.scale.set(0.85 + Math2.random() * 0.4, 0.8 + Math2.random() * 0.5, 0.85 + Math2.random() * 0.4);
      dummy.updateMatrix();
      instanced.setMatrixAt(instanceIndex, dummy.matrix);

      const cordColor = baseColor.clone();
      cordColor.offsetHSL((Math2.random() - 0.5) * 0.06, (Math2.random() - 0.5) * 0.18, (Math2.random() - 0.5) * 0.22);
      const ci = instanceIndex * 3;
      instanceColors[ci] = cordColor.r; instanceColors[ci + 1] = cordColor.g; instanceColors[ci + 2] = cordColor.b;
      instanceIndex++;
    }
  }

  this.needleTipT = clusterTipT;
  cordGeometry.setAttribute('instanceColor', new THREE.InstancedBufferAttribute(instanceColors, 3));
  injectFoliageShader(cordMaterial); // per-instance color + shared wind sway
  instanced.instanceMatrix.needsUpdate = true;
  instanced.castShadow = true;
  instanced.receiveShadow = false;
  instanced.raycast = () => {}; // let prune/shape clicks pass through to wood
  this.foliageGroup.add(instanced);
  this.instancedNeedles = instanced;
}

// Upswept long-needle "candle": each cluster is a brush of slim, pointed needles
// fanning out of a common base in a cone that sweeps forward toward the branch
// tip and tilts up — the soft, directional look of new pine candle growth, vs the
// short flat tufts of createPineNeedles. Shares the needle color/wind/prune
// machinery (instancedNeedles + needlesPerCluster) with the classic style.
createCandleNeedles() {
  this.instancedLeaves = null;
  this.instancedSnow = null;

  // Cluster count mirrors classic pine; the brush itself is denser per cluster.
  const numClusters = Math.floor(4 + parameters.foliageDensity * 0.5 + this.length * 0.1);
  const finalClusterCount = Math.max(Math.min(numClusters, 30), 2);
  const needlesPerCluster = Math.floor(8 + parameters.foliageDensity * 0.35);
  const totalNeedles = finalClusterCount * needlesPerCluster;
  this.needlesPerCluster = needlesPerCluster;

  // Slim, long, finely pointed needle (vs the stubby taper of classic pine).
  const needleLength = 5.5 * (parameters.foliageSize / 15);
  const needleWidth = 0.16;
  const needleGeometry = new THREE.CylinderGeometry(needleWidth, needleWidth * 0.04, needleLength, 3, 1);
  needleGeometry.translate(0, needleLength / 2, 0); // base at origin, tip toward +Y

  const colorPalette = needlePalette();
  const paletteColors = Colors[colorPalette] || Colors.pine;

  const needleMaterial = new THREE.MeshStandardMaterial({
    color: 0xffffff, roughness: 0.8, metalness: 0.1, flatShading: true, side: THREE.DoubleSide
  });

  const instancedNeedles = new THREE.InstancedMesh(needleGeometry, needleMaterial, totalNeedles);
  const instanceColors = new Float32Array(totalNeedles * 3);
  // Per-cluster position along the branch (0 base → 1 tip), kept so
  // updateNeedleColors can re-apply the spring tip flush per tuft.
  const clusterTipT = new Float32Array(finalClusterCount);

  const dummy = new THREE.Object3D();
  const quat = new THREE.Quaternion();
  const Y_AXIS = new THREE.Vector3(0, 1, 0);
  let instanceIndex = 0;

  // Keep candles on the part of the branch that still exists, bunched toward the
  // outer end like the pine pad.
  const tipFraction = this.tipRing / BRANCH_TUBULAR_SEGMENTS;
  const PAD_START = 0.25;
  const CONE = Math.PI * 0.30; // brush splay half-angle (~54°)

  for (let clusterIdx = 0; clusterIdx < finalClusterCount; clusterIdx++) {
    const t = (PAD_START + (1 - PAD_START) * (clusterIdx + Math2.random() * 0.5) / finalClusterCount) * tipFraction;
    const curvePoint = this.curve.getPoint(t);
    const forward = this.curve.getTangent(t).normalize(); // toward the branch tip

    // Branch "up": world-up projected off the branch axis. The candle base lifts
    // off the surface along it and the brush axis tilts up toward it (upswept).
    const up = Y_AXIS.clone().addScaledVector(forward, -Y_AXIS.dot(forward));
    if (up.lengthSq() < 1e-4) up.set(1, 0, 0);
    up.normalize();
    const base = curvePoint.clone().addScaledVector(up, this.thickness * 0.5);
    const axis = forward.clone().addScaledVector(up, 0.35).normalize();

    // Two perpendiculars to the brush axis, to splay needles around it.
    const u = new THREE.Vector3().crossVectors(axis, Y_AXIS);
    if (u.lengthSq() < 1e-4) u.set(1, 0, 0);
    u.normalize();
    const v = new THREE.Vector3().crossVectors(axis, u).normalize();

    // Spring flush: the whole tuft shifts lighter/yellower toward the branch tip,
    // deeper green near the base (a no-op for every non-spring palette).
    const outward = THREE.MathUtils.clamp((t / tipFraction - PAD_START) / (1 - PAD_START), 0, 1);
    clusterTipT[clusterIdx] = outward;
    const baseColor = applySpringTipFlush(
      new THREE.Color(paletteColors[Math.floor(Math2.random() * paletteColors.length)]), outward);

    for (let needleIdx = 0; needleIdx < needlesPerCluster; needleIdx++) {
      // Direction within the cone: sqrt() pushes more needles toward the rim so
      // the candle flares like a brush rather than a tight spike.
      const phi = CONE * Math.sqrt(Math2.random());
      const theta = Math2.random() * Math.PI * 2;
      const dir = axis.clone().multiplyScalar(Math.cos(phi))
        .addScaledVector(u, Math.sin(phi) * Math.cos(theta))
        .addScaledVector(v, Math.sin(phi) * Math.sin(theta))
        .normalize();

      // Spread the bases a little along the axis (a short stem) and off it, so the
      // needles emerge from a candle body instead of one point.
      dummy.position.copy(base)
        .addScaledVector(axis, Math2.random() * needleLength * 0.18)
        .addScaledVector(u, (Math2.random() - 0.5) * this.thickness * 0.3)
        .addScaledVector(v, (Math2.random() - 0.5) * this.thickness * 0.3);
      quat.setFromUnitVectors(Y_AXIS, dir);
      dummy.quaternion.copy(quat);
      dummy.scale.set(1, 0.85 + Math2.random() * 0.4, 1); // per-needle length variation
      dummy.updateMatrix();
      instancedNeedles.setMatrixAt(instanceIndex, dummy.matrix);

      const needleColor = baseColor.clone();
      needleColor.offsetHSL((Math2.random() - 0.5) * 0.12, (Math2.random() - 0.5) * 0.25, (Math2.random() - 0.5) * 0.35);
      const ci = instanceIndex * 3;
      instanceColors[ci] = needleColor.r; instanceColors[ci + 1] = needleColor.g; instanceColors[ci + 2] = needleColor.b;
      instanceIndex++;
    }
  }

  this.needleTipT = clusterTipT;
  needleGeometry.setAttribute('instanceColor', new THREE.InstancedBufferAttribute(instanceColors, 3));
  injectFoliageShader(needleMaterial); // per-instance color + shared wind sway
  instancedNeedles.instanceMatrix.needsUpdate = true;
  instancedNeedles.castShadow = true;
  instancedNeedles.receiveShadow = false;
  instancedNeedles.raycast = () => {}; // let prune/shape clicks pass through to wood
  this.foliageGroup.add(instancedNeedles);
  this.instancedNeedles = instancedNeedles;
}

// Create pine needle clusters
createPineNeedles() {
  // This branch is now needled, not leaved — drop any stale leaf reference so
  // the prune path doesn't try to shed leaves that aren't here.
  this.instancedLeaves = null;

  // 'none' (Bare / Winter): snow, not needles. (Reached only if something routes
  // a bare tree here; the normal path sends 'none' to createDeciduousLeaves.)
  if (parameters.leafType === 'none') { this.createSnow(); return; }
  this.instancedSnow = null; // needled branch: clear any winter-snow reference

  // Calculate clusters based on foliageDensity parameter
  const baseClusterCount = 4; // Reduced base count
  const clustersPerDensityUnit = parameters.foliageDensity * 0.5; // Use density parameter
  const lengthFactor = this.length * 0.1; // Branch length influence
  const numClusters = Math.floor(baseClusterCount + clustersPerDensityUnit + lengthFactor);
  const maxClusters = Math.min(numClusters, 30); // Reasonable maximum
  const finalClusterCount = Math.max(maxClusters, 2); // Minimum of 2 clusters
  
  // ~5 needles per tuft (a realistic pine fascicle), scaling only gently with
  // density — at the default density of 18 this lands on 5.
  const needlesPerCluster = Math.floor(3 + (parameters.foliageDensity * 0.12));
  const totalNeedles = finalClusterCount * needlesPerCluster;

  // Remember the real layout so updateNeedleColors recolors every cluster
  // exactly (a stale hardcoded count leaves trailing needles the old color).
  this.needlesPerCluster = needlesPerCluster;

  // Short like classic pine, but as slim and finely pointed as the candle needle —
  // the thin profile is what gives the better, less-plastic look.
  const needleLength = 3.5 * (parameters.foliageSize / 15);
  const needleWidth = 0.16;

  const needleGeometry = new THREE.CylinderGeometry(
    needleWidth,
    needleWidth * 0.04,
    needleLength,
    3, // Low poly
    1
  );
  
  // Move base to origin
  needleGeometry.translate(0, needleLength / 2, 0);

  // Get colors from palette
  const colorPalette = needlePalette();
  const paletteColors = Colors[colorPalette] || Colors.pine;

  // Create material
  const needleMaterial = new THREE.MeshStandardMaterial({
    color: 0xffffff, // White base
    roughness: 0.8,
    metalness: 0.1,
    flatShading: true,
    side: THREE.DoubleSide
  });

  // Create instanced mesh
  const instancedNeedles = new THREE.InstancedMesh(
    needleGeometry,
    needleMaterial,
    totalNeedles
  );

  // Create array for per-needle colors
  const instanceColors = new Float32Array(totalNeedles * 3);
  // Per-cluster position along the branch (0 base → 1 tip), kept so
  // updateNeedleColors can re-apply the spring tip flush per tuft.
  const clusterTipT = new Float32Array(finalClusterCount);

  // Position instances
  const dummy = new THREE.Object3D();
  let instanceIndex = 0;

  // Keep foliage on the part of the branch that still exists: a partially
  // pruned branch only spans [0, tipFraction] of the original curve.
  const tipFraction = this.tipRing / BRANCH_TUBULAR_SEGMENTS;

  // Concentrate the needle clusters toward the outer end so the canopy reads as
  // a rounded bonsai foliage pad rather than fuzz running the whole branch.
  const PAD_START = 0.3;
  const Y_AXIS = new THREE.Vector3(0, 1, 0);
  const quat = new THREE.Quaternion();
  const CONE = Math.PI * 0.34; // tuft splay half-angle (~61°): an upward fan, kept tight

  for (let clusterIdx = 0; clusterIdx < finalClusterCount; clusterIdx++) {
    const t = (PAD_START + (1 - PAD_START) * (clusterIdx + 0.2) / finalClusterCount) * tipFraction;
    const curvePoint = this.curve.getPoint(t);
    const tangent = this.curve.getTangent(t).normalize();

    // Needles point mostly UP — the upright pads of a classic bonsai. The tuft
    // splays around world-up (with a slight lean toward the branch tip and a small
    // random tilt for variety), NOT around the branch, so needles fan upward
    // instead of wrapping onto the sides and underside.
    const side = new THREE.Vector3().crossVectors(tangent, Y_AXIS);
    if (side.lengthSq() < 1e-4) side.set(1, 0, 0);
    side.normalize();
    const axis = Y_AXIS.clone()
      .addScaledVector(tangent, 0.2)                 // gentle lean toward the tip
      .addScaledVector(side, (Math2.random() - 0.5) * 0.5) // small per-tuft tilt off vertical
      .normalize();

    // Seat the tuft on the upper side of the branch.
    const topNormal = Y_AXIS.clone().addScaledVector(tangent, -Y_AXIS.dot(tangent));
    if (topNormal.lengthSq() < 1e-4) topNormal.set(1, 0, 0);
    topNormal.normalize();
    const base = curvePoint.clone().addScaledVector(topNormal, this.thickness * 0.5);

    // Two perpendiculars to the tuft axis, to splay needles around it.
    const u = new THREE.Vector3().crossVectors(axis, Y_AXIS);
    if (u.lengthSq() < 1e-4) u.set(1, 0, 0);
    u.normalize();
    const v = new THREE.Vector3().crossVectors(axis, u).normalize();

    // Spring flush: the whole tuft shifts lighter/yellower toward the branch tip,
    // deeper green near the base (a no-op for every non-spring palette).
    const outward = THREE.MathUtils.clamp((t / tipFraction - PAD_START) / (1 - PAD_START), 0, 1);
    clusterTipT[clusterIdx] = outward;
    const baseColor = applySpringTipFlush(
      new THREE.Color(paletteColors[Math.floor(Math2.random() * paletteColors.length)]), outward);

    for (let needleIdx = 0; needleIdx < needlesPerCluster; needleIdx++) {
      // Splay within a cone; sqrt() biases needles toward the rim for a full,
      // rounded tuft rather than a sparse centre.
      const phi = CONE * Math.sqrt(Math2.random());
      const theta = Math2.random() * Math.PI * 2;
      const dir = axis.clone().multiplyScalar(Math.cos(phi))
        .addScaledVector(u, Math.sin(phi) * Math.cos(theta))
        .addScaledVector(v, Math.sin(phi) * Math.sin(theta))
        .normalize();

      // Emerge from a small tuft body, not a single point.
      dummy.position.copy(base)
        .addScaledVector(axis, Math2.random() * needleLength * 0.15)
        .addScaledVector(u, (Math2.random() - 0.5) * this.thickness * 0.25)
        .addScaledVector(v, (Math2.random() - 0.5) * this.thickness * 0.25);
      quat.setFromUnitVectors(Y_AXIS, dir);
      dummy.quaternion.copy(quat);
      dummy.scale.set(1, 0.8 + Math2.random() * 0.4, 1); // per-needle length variation
      dummy.updateMatrix();
      instancedNeedles.setMatrixAt(instanceIndex, dummy.matrix);

      const needleColor = baseColor.clone();
      needleColor.offsetHSL(
        (Math2.random() - 0.5) * 0.12, // Slight hue shift
        (Math2.random() - 0.5) * 0.25, // Saturation variation
        (Math2.random() - 0.5) * 0.35  // Brightness variation
      );
      const colorIndex = instanceIndex * 3;
      instanceColors[colorIndex] = needleColor.r;
      instanceColors[colorIndex + 1] = needleColor.g;
      instanceColors[colorIndex + 2] = needleColor.b;

      instanceIndex++;
    }
  }

  this.needleTipT = clusterTipT;

  // Add instance colors to geometry
  needleGeometry.setAttribute('instanceColor',
    new THREE.InstancedBufferAttribute(instanceColors, 3)
  );

  // Per-instance color + wind sway (shared with deciduous leaves).
  injectFoliageShader(needleMaterial);

  // Update all matrices
  instancedNeedles.instanceMatrix.needsUpdate = true;
  
  // Enable shadows
  instancedNeedles.castShadow = true;
  instancedNeedles.receiveShadow = false;

  // Ignore needles when picking so pruning clicks pass through to the wood.
  instancedNeedles.raycast = () => {};

  // Add to branch
  this.foliageGroup.add(instancedNeedles);
  
  // Store reference for updates
  this.instancedNeedles = instancedNeedles;
}

updateNeedleColors() {
  if (!this.instancedNeedles) {
    // No needles on this branch, but its twigs may carry their own.
    if (this.twigs) this.twigs.forEach(twig => twig.updateNeedleColors());
    return;
  }

  const colorPalette = needlePalette();
  const paletteColors = Colors[colorPalette] || Colors.pine;
  
  const instanceColors = this.instancedNeedles.geometry.attributes.instanceColor.array;
  // Use the same per-cluster count the needles were built with so every
  // instance (including the last cluster) gets recolored.
  const needlesPerCluster = this.needlesPerCluster || 10;

  let instanceIndex = 0;
  const numClusters = Math.floor(this.instancedNeedles.count / needlesPerCluster);
  const tips = this.needleTipT;

  for (let clusterIdx = 0; clusterIdx < numClusters; clusterIdx++) {
    // Pick new base color for this cluster, with the spring tip flush re-applied
    // from the stored per-cluster position (a no-op for non-spring palettes).
    const baseColorHex = paletteColors[Math.floor(Math2.random() * paletteColors.length)];
    const baseColor = applySpringTipFlush(
      new THREE.Color(baseColorHex), tips ? tips[clusterIdx] : 0.5);

    // Update all needles in this cluster
    for (let needleIdx = 0; needleIdx < needlesPerCluster; needleIdx++) {
      const needleColor = baseColor.clone();
      needleColor.offsetHSL(
        (Math2.random() - 0.5) * 0.12,
        (Math2.random() - 0.5) * 0.25,
        (Math2.random() - 0.5) * 0.35
      );
      
      const colorIndex = instanceIndex * 3;
      instanceColors[colorIndex] = needleColor.r;
      instanceColors[colorIndex + 1] = needleColor.g;
      instanceColors[colorIndex + 2] = needleColor.b;
      
      instanceIndex++;
    }
  }
  
  // Mark for update
  this.instancedNeedles.geometry.attributes.instanceColor.needsUpdate = true;

  // Twigs carry their own needles but aren't in tree.branches; recurse so they
  // recolor too.
  if (this.twigs) this.twigs.forEach(twig => twig.updateNeedleColors());
}

// Create deciduous leaves
createDeciduousLeaves() {
  // This branch is now leaved, not needled — drop any stale needle reference so
  // the prune path doesn't try to shed needles that aren't here.
  this.instancedNeedles = null;

  // 'none' (Bare / Winter): no leaves — drape snow along the bare branch instead
  // (createSnow clears the leaf/needle refs). The exposed structure stays easy to
  // prune since the snow ignores picking.
  if (parameters.leafType === 'none') { this.createSnow(); return; }
  this.instancedSnow = null; // leafed branch: clear any winter-snow reference

  // Calculate leaf count based on foliageDensity parameter
  const baseLeafCount = 3;
  const densityFactor = parameters.foliageDensity * 0.8; // Use density parameter
  const lengthFactor = this.length * 0.05; // Branch length influence
  const leafCount = Math.floor(baseLeafCount + densityFactor + lengthFactor);
  // Per-branch ceiling is leaf-type-aware: small/narrow blades need far more
  // instances to read as a full canopy than large ones, so willow gets the
  // highest cap and the big maple/blossom leaves the lowest. Without this the
  // old flat cap of 25 saturated around density ~28, leaving willow in
  // particular sparse while the slider still had most of its range left.
  const LEAF_CAP = { willow: 70, deciduous: 50, cherry: 40, maple: 38 };
  const maxLeaves = Math.min(leafCount, LEAF_CAP[parameters.leafType] || 50);
  const finalLeafCount = Math.max(maxLeaves, 2); // Minimum of 2 leaves

  const sizeScale = parameters.foliageSize / 15;

  // One leaf geometry shared by every instance on this branch; per-leaf variety
  // comes from the instance matrix (position/rotation/scale) and instanceColor.
  const leafGeometry = makeLeafGeometry();

  const leafMaterial = new THREE.MeshStandardMaterial({
    color: 0xffffff, // base white; tinted per-instance via instanceColor
    roughness: 0.7,
    metalness: 0.1,
    flatShading: true,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 0.9,
    // Cherry blossoms carry a per-vertex centre gradient; other leaf geometries
    // have no color attribute, so vertex colors stay off for them.
    vertexColors: parameters.leafType === 'cherry'
  });
  // Per-instance color + wind sway (shared with pine needles).
  injectFoliageShader(leafMaterial);

  const leaves = new THREE.InstancedMesh(leafGeometry, leafMaterial, finalLeafCount);
  // Two per-instance colors: base (at the petiole) and tip. For flat palettes both
  // hold the same color so the leaf reads as a single tint; gradient palettes
  // (autumn) blend base→tip along the per-vertex ramp added below.
  const palette = leafPalette();
  const gradient = paletteIsGradient(palette);
  const instanceColors = new Float32Array(finalLeafCount * 3);
  const instanceColors2 = new Float32Array(finalLeafCount * 3);
  // Per-leaf position along the branch (0 base → 1 tip), kept so updateLeafColors
  // can re-apply the spring tip flush without recomputing leaf placement.
  const leafTipT = new Float32Array(finalLeafCount);

  // Keep leaves on the part of the branch that still exists: a partially
  // pruned branch only spans [0, tipFraction] of the original curve.
  const tipFraction = this.tipRing / BRANCH_TUBULAR_SEGMENTS;
  const dummy = new THREE.Object3D();

  // Foliage pad: real bonsai bunch their leaves into a rounded mass on the outer
  // end of each branch. Each leaf attaches to the branch surface by its stem and
  // the blade hangs out at the end of that stem, so successive leaves splay all
  // the way around the branch instead of clumping into one blob.
  const PAD_START = 0.4;
  const Y_AXIS = new THREE.Vector3(0, 1, 0);
  const quat = new THREE.Quaternion();

  for (let i = 0; i < finalLeafCount; i++) {
    // Step along the outer pad, with a little jitter so leaves don't ring up.
    const t = (PAD_START + (1 - PAD_START) * ((i + Math2.random() * 0.6) / finalLeafCount)) * tipFraction;
    const curvePoint = this.curve.getPoint(t);
    const tangent = this.curve.getTangent(t).normalize();

    // "Up" around the branch: world-up projected into the plane perpendicular to
    // the branch (the top of the branch). Leaves fan around the top and sides
    // from here but stay off the underside, the way needles do — so the clock
    // angle is restricted to leave a clear arc on the bottom.
    const normal = Y_AXIS.clone().addScaledVector(tangent, -Y_AXIS.dot(tangent));
    if (normal.lengthSq() < 1e-4) normal.set(1, 0, 0); // ~vertical branch: any perp
    normal.normalize();
    const UNDERSIDE_GAP = Math.PI * 0.7;               // bare arc on the underside
    const clock = (Math2.random() - 0.5) * (Math.PI * 2 - UNDERSIDE_GAP);
    normal.applyAxisAngle(tangent, clock);

    // Aim: leaves near the branch base point straight out (radial), but the
    // further out along the pad they sit, the more they lean toward the tip
    // direction (the tangent) — so the canopy points away from the trunk at the
    // ends instead of bristling sideways.
    const outward = (t / tipFraction - PAD_START) / (1 - PAD_START); // 0 at pad start → 1 at tip
    const aim = normal.clone().addScaledVector(tangent, outward * 1.3).normalize();

    // Stem base sits just off the branch surface (offset along the radial normal);
    // the stem (+Y in the geometry) follows `aim`, then we spin the blade around
    // the stem and add a gentle droop.
    dummy.position.copy(curvePoint).addScaledVector(normal, this.thickness * 0.85);
    quat.setFromUnitVectors(Y_AXIS, aim);
    dummy.quaternion.copy(quat);
    dummy.rotateY(Math2.random() * Math.PI * 2);   // spin the blade around its stem
    dummy.rotateX((Math2.random() - 0.5) * 0.8);   // gentle droop
    dummy.scale.setScalar((0.8 + Math2.random() * 0.4) * sizeScale);
    dummy.updateMatrix();
    leaves.setMatrixAt(i, dummy.matrix);

    const [lo, hi] = pickLeafColors(palette, gradient);
    // Spring leaves flush lighter toward the branch tips, deeper green at the
    // base (lo === hi for the flat spring palette, so shade the one color once).
    applySpringTipFlush(lo, outward);
    if (hi !== lo) applySpringTipFlush(hi, outward);
    leafTipT[i] = outward;
    const o = i * 3;
    instanceColors[o] = lo.r; instanceColors[o + 1] = lo.g; instanceColors[o + 2] = lo.b;
    instanceColors2[o] = hi.r; instanceColors2[o + 1] = hi.g; instanceColors2[o + 2] = hi.b;
  }
  this.leafTipT = leafTipT;

  if (gradient) addLeafGradientRamp(leafGeometry);
  leafGeometry.setAttribute('instanceColor', new THREE.InstancedBufferAttribute(instanceColors, 3));
  leafGeometry.setAttribute('instanceColor2', new THREE.InstancedBufferAttribute(instanceColors2, 3));
  leaves.instanceMatrix.needsUpdate = true;
  leaves.castShadow = true;
  // Ignore leaves when picking so pruning clicks pass through to the wood.
  leaves.raycast = () => {};
  // Marks this as sheddable foliage for the autumn leaf-fall (see updateLeafFall).
  leaves.userData.isLeaf = true;

  this.foliageGroup.add(leaves);
  this.instancedLeaves = leaves;
}

// Snow / frost for 'none' (Bare / Winter) mode: small white clumps draped along
// the TOP of the bare branch. Built like the leaf canopy — one InstancedMesh per
// branch, placed from the seeded Math2 stream so saves replay identically — but
// with NO wind sway (snow sits frozen, so it skips injectFoliageShader) and a
// faint blue emissive so shadowed sides read as frost. It lives in foliageGroup,
// so the existing prune machinery carries it for free: whole-branch cuts drop it
// with the branch, partial cuts shed the clumps past the cut, and the hover
// preview recolors it — that last path reads an instanceColor attribute off every
// instanced foliage child, so we add one (white) even though the snow material
// ignores the value.
createSnow({ overFoliage = false } = {}) {
  // Bare-winter snow replaces the foliage, so it clears any stale needle/leaf refs.
  // The Evergreen / Winter type instead layers snow OVER freshly-built needles
  // (overFoliage), so it keeps them.
  if (!overFoliage) {
    this.instancedNeedles = null;
    this.instancedLeaves = null;
  }

  const tipFraction = this.tipRing / BRANCH_TUBULAR_SEGMENTS;
  // More clumps on longer branches and at higher foliage density; clamped. Run it
  // dense so neighbouring clumps overlap into a continuous ridge of snow rather
  // than separate plops.
  const count = Math.max(3, Math.min(
    Math.floor(this.length * 0.22 + parameters.foliageDensity * 0.5), 38));
  // Clumps scale with branch thickness, nudged by the foliage-size slider so that
  // control stays meaningful in winter mode.
  const sizeScale = 0.7 + (parameters.foliageSize / 15) * 0.5;

  const snowGeometry = new THREE.IcosahedronGeometry(1, 2); // smooth rounded clump
  const snowMaterial = new THREE.MeshStandardMaterial({
    color: 0xeef4ff,        // cool white
    roughness: 0.95,
    metalness: 0.0,
    flatShading: false,     // smooth normals so clumps read as soft mounds, not facets
    emissive: 0x2a3f5f,     // faint blue frost in shadow
    emissiveIntensity: 0.15
  });
  // Share the Surface Textures toggle (see setTreeSnowTexture); the pot caps do the
  // same via environment's rock registry.
  if (_snowTextureEnabled) snowMaterial.normalMap = getSnowNormal();

  const snow = new THREE.InstancedMesh(snowGeometry, snowMaterial, count);
  const dummy = new THREE.Object3D();
  const SNOW_START = 0.05; // skip the very base where the branch meets its parent
  // New growth (the grow buttons) arrives bare and lets snow settle over time;
  // generated/loaded trees start fully covered. See setSnowStartsBare.
  const startBare = snowStartsBare;
  const bareMat = new THREE.Matrix4().makeScale(0, 0, 0);

  // Per-clump rest transform + seating params, kept so the snow can be knocked off
  // when the branch is sculpted (shedSnowIn), re-seated on the branch's NEW top side
  // (reseatSnow), and slowly re-accumulate (updateSnowMesh).
  const basePos = new Float32Array(count * 3);
  const baseQuat = new Float32Array(count * 4);
  const baseScale = new Float32Array(count * 3);
  const thresh = new Float32Array(count);
  const tParam = new Float32Array(count);
  const clock = new Float32Array(count);
  const spin = new Float32Array(count);

  for (let i = 0; i < count; i++) {
    // Walk from just past the base to the surviving tip. Draw the seating randoms
    // from the seeded stream in a fixed order (t, clock, spin, size) so saves replay
    // identically.
    const t = SNOW_START + (tipFraction - SNOW_START) * ((i + Math2.random() * 0.7) / count);
    const clk = (Math2.random() - 0.5) * 0.7;          // clock jitter: pile near the top
    const spn = Math2.random() * Math.PI * 2;           // random spin so clumps differ
    const r = this.thickness * (1.35 + Math2.random() * 0.5) * sizeScale;

    // Seat on the current top. At build time local +Y == world-up; after a shape the
    // branch sheds and reseatSnow() re-derives this against the new pose.
    seatSnowClump(this.curve, this.thickness, t, clk, spn, _GEOM_Y, dummy.position, dummy.quaternion);
    dummy.scale.set(r, r * 0.7, r); // wide + flattened so clumps merge into a ridge
    dummy.updateMatrix();
    snow.setMatrixAt(i, startBare ? bareMat : dummy.matrix); // bare → hidden until it accumulates

    basePos[i * 3] = dummy.position.x;
    basePos[i * 3 + 1] = dummy.position.y;
    basePos[i * 3 + 2] = dummy.position.z;
    baseQuat[i * 4] = dummy.quaternion.x;
    baseQuat[i * 4 + 1] = dummy.quaternion.y;
    baseQuat[i * 4 + 2] = dummy.quaternion.z;
    baseQuat[i * 4 + 3] = dummy.quaternion.w;
    baseScale[i * 3] = r;
    baseScale[i * 3 + 1] = r * 0.7;
    baseScale[i * 3 + 2] = r;
    tParam[i] = t; clock[i] = clk; spin[i] = spn;
    // Reveal order for the regrow (cosmetic → Math.random, not the seeded stream).
    thresh[i] = Math.random();
  }

  // White instanceColor so the cut hover-preview (which writes .array on every
  // instanced foliage child) has an attribute to touch; the material ignores it.
  const white = new Float32Array(count * 3).fill(1);
  snowGeometry.setAttribute('instanceColor', new THREE.InstancedBufferAttribute(white, 3));

  snow.instanceMatrix.needsUpdate = true;
  snow.castShadow = true;
  snow.raycast = () => {}; // let prune clicks pass through to the wood
  // Snow-cover animation state. `fill` ramps 0→1; clump i shows once fill passes
  // thresh[i]. Sculpting a branch sheds its snow (fill→0, clumps slide off), re-seats
  // the clumps onto the new top, then re-accumulates over SNOW_REGROW_S.
  // updateSnowMesh() drives it each winter frame; reseatSnow needs `branch` + params.
  snow.userData.snow = {
    branch: this, count, basePos, baseQuat, baseScale, thresh, tParam, clock, spin,
    f: new Float32Array(count).fill(startBare ? 0 : 1), // current scale factor per clump (0..1)
    drop: new Float32Array(count),       // local-down slide distance while shedding
    vel: new Float32Array(count),        // shed fall speed (clump-radii / s)
    falling: new Uint8Array(count),      // clump currently sliding off?
    fallPos: new Float32Array(count * 3), // frozen seat a falling clump slides from
    fallQuat: new Float32Array(count * 4),// its orientation while it slides
    localDown: new THREE.Vector3(0, -1, 0), // world-down in mesh-local space (set on shed)
    fill: startBare ? 0 : 1,             // 0 = bare new growth, 1 = fully covered
  };
  this.foliageGroup.add(snow);
  this.instancedSnow = snow;
}

// Update leaf type when changed in controls
updateLeafType() {
  if (this.isPruned) return;

  if (this.foliageGroup) {
    const oldGroup = this.foliageGroup;

    // Find any snow already on the old group so the transition can animate it:
    // leaving a snowy type melts it off; arriving at one (with none there before)
    // settles fresh snow in. Staying snowy (none↔evergreen) just swaps coats.
    const snowyNow = parameters.leafType === 'none' || parameters.leafType === 'evergreen';
    let oldSnow = null;
    for (const c of oldGroup.children) {
      if (c.userData && c.userData.snow) { oldSnow = c; break; }
    }
    const meltSnow = snowyNow ? null : oldSnow; // rescue old snow to thaw it
    const enteringWinter = snowyNow && !oldSnow; // snow now, none before → settle it in

    // Clear existing foliage
    this.mesh.remove(oldGroup);
    this.foliageGroup = new THREE.Group();

    // Create new foliage based on type
    if (isNeedleType()) {
      this.createNeedles();
    } else {
      this.createDeciduousLeaves();
    }

    this.mesh.add(this.foliageGroup);

    // Sprout the fresh foliage: scale each leaf/needle up from nothing, staggered
    // and jittered, so it looks like it grows in (createNeedles/createDeciduousLeaves
    // leave exactly one of these set; 'none' has neither — just snow).
    growInFoliage(this.instancedLeaves);
    growInFoliage(this.instancedNeedles);

    // Entering winter: accumulate the new snow in rather than have it appear at full
    // depth (the bare 'none' branch and the snow draped over evergreen needles).
    if (enteringWinter) settleSnowIn(this.instancedSnow);

    // Re-home the rescued snow into the live group (so it tracks branch shaping)
    // and thaw it. The new leaves/needles own instancedSnow tracking now.
    if (meltSnow) {
      oldGroup.remove(meltSnow);
      this.foliageGroup.add(meltSnow);
      this.instancedSnow = null;
      meltSnowAway(meltSnow);
    }
  }

  // Twigs carry their own foliage but aren't in tree.branches; recurse so they
  // switch leaf type too.
  if (this.twigs) this.twigs.forEach(twig => twig.updateLeafType());
}

createEndCaps() {
  this.endCap = this._makeCap(1);
  this.mesh.add(this.endCap);
}

// Build a circular bark cap oriented to the branch curve at parameter `t`.
// Used for the original tip and for re-capping the stub after a partial prune.
_makeCap(t) {
  const capRadius = this.thickness * 0.9;
  const capGeometry = new THREE.CircleGeometry(capRadius, 8);

  const point = this.curve.getPoint(t);
  const tangent = this.curve.getTangent(t);

  const capMaterial = new THREE.MeshStandardMaterial({
    color: new THREE.Color(parameters.truncColor).offsetHSL(0, -0.1, 0.3),
    roughness: 0.7,
    metalness: 0.1,
    flatShading: true
  });

  const cap = new THREE.Mesh(capGeometry, capMaterial);
  cap.userData.isEndCap = true; // so updateTrunkColor paths can recolor it by reference
  cap.position.copy(point);
  cap.lookAt(point.clone().add(tangent));
  cap.castShadow = true;
  cap.receiveShadow = true;
  return cap;
}

//PRUNE
      prune() {
        if (!this.isPruned) {
          this.isPruned = true;
          
          gsap.to(this.mesh.scale, {
            x: 0.01, y: 0.01, z: 0.01,
            duration: 0.8,
            ease: "power2.in",
            onComplete: () => {
              this.mesh.visible = false;
            }
          });
        }
      }
    }

export { Tree };
